import { ArrowUpRightIcon } from '@phosphor-icons/react/ArrowUpRight'
import { ArrowClockwiseIcon } from '@phosphor-icons/react/ArrowClockwise'
import { BrainIcon } from '@phosphor-icons/react/Brain'
import { CaretDownIcon } from '@phosphor-icons/react/CaretDown'
import { ChatCircleDotsIcon } from '@phosphor-icons/react/ChatCircleDots'
import { CheckCircleIcon } from '@phosphor-icons/react/CheckCircle'
import { CheckIcon } from '@phosphor-icons/react/Check'
import { CircleNotchIcon } from '@phosphor-icons/react/CircleNotch'
import { ClockCounterClockwiseIcon } from '@phosphor-icons/react/ClockCounterClockwise'
import { CopyIcon } from '@phosphor-icons/react/Copy'
import { FileCodeIcon } from '@phosphor-icons/react/FileCode'
import { FolderOpenIcon } from '@phosphor-icons/react/FolderOpen'
import { GameControllerIcon } from '@phosphor-icons/react/GameController'
import { GlobeHemisphereWestIcon } from '@phosphor-icons/react/GlobeHemisphereWest'
import { GearSixIcon } from '@phosphor-icons/react/GearSix'
import { GitBranchIcon } from '@phosphor-icons/react/GitBranch'
import { GitCommitIcon } from '@phosphor-icons/react/GitCommit'
import { MagnifyingGlassIcon } from '@phosphor-icons/react/MagnifyingGlass'
import { EyeIcon } from '@phosphor-icons/react/Eye'
import { HandPalmIcon } from '@phosphor-icons/react/HandPalm'
import { PaperclipIcon } from '@phosphor-icons/react/Paperclip'
import { PaperPlaneRightIcon } from '@phosphor-icons/react/PaperPlaneRight'
import { PlusIcon } from '@phosphor-icons/react/Plus'
import { PuzzlePieceIcon } from '@phosphor-icons/react/PuzzlePiece'
import { SparkleIcon } from '@phosphor-icons/react/Sparkle'
import { StopIcon } from '@phosphor-icons/react/Stop'
import { ShieldCheckIcon } from '@phosphor-icons/react/ShieldCheck'
import { ShieldWarningIcon } from '@phosphor-icons/react/ShieldWarning'
import { TerminalWindowIcon } from '@phosphor-icons/react/TerminalWindow'
import { UsersThreeIcon } from '@phosphor-icons/react/UsersThree'
import { WarningIcon } from '@phosphor-icons/react/Warning'
import { XIcon } from '@phosphor-icons/react/X'
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createUniverseIndex, searchArtifacts, type MemoryArtifact, type MemoryUniverse } from '../data'
import {
  codexApi,
  loadBootstrap,
  type BootstrapState,
  type CodexModel,
  type CodexThread,
  type ProjectChange,
  type ProjectFile,
  type ProjectFilePreview,
  type ProjectDiff,
  type ProviderDescriptor,
  type ProviderModel,
  type ProviderSelection,
  type RuntimePreflight,
  type RemotePairing,
  type RemoteOperation,
  type RemoteChat,
  type BrowserCapabilities,
  type BrowserSession,
  type RouteDecision,
  type MemoryContext,
  type MemoryHealth,
  type WorkflowPlan,
  type AgentGraph,
  type TerminalOutput,
} from './api'
import {
  DEFAULT_SETTINGS,
  loadActiveProjectId,
  loadProjects,
  loadSettings,
  saveActiveProjectId,
  saveProjects,
  saveSettings,
  loadTaskDraft,
  loadTaskQueue,
  saveTaskQueue,
  saveTaskDraft,
  loadTaskMetadata,
  saveTaskMetadata,
  isAbsoluteWorkspacePath,
  type DraftAttachment,
  type PermissionProfileId,
  type StudioSettings,
  type StudioProject,
  type TaskMetadata,
  type WorkflowReasoningEffort,
} from './storage'
import './styles/index.css'
import { TaskRuntimeRegistry, taskIdForThread, taskIdForWorkflow } from './task-runtime/client'
import { isTerminalExecutionState, shouldRunCompletionSideEffects } from './task-runtime/lifecycle'
import { boundMemoryContext, classifyTaskIntent, classifyTeamRequest, shouldRetrieveMemory, MEMORY_CONTEXT_TOKEN_BUDGET } from './task-runtime/intent'
import type { PlanState, TaskState } from './task-runtime/types'
import { ActivityTimeline } from './components/activity/ActivityTimeline'
import { TerminalView } from './components/terminal/TerminalView'
import { CommandPalette, type WorkspaceCommand } from './components/CommandPalette'
import { TetrisView } from './components/tetris/TetrisView'

type View = 'chat' | 'files' | 'changes' | 'terminal' | 'evidence' | 'browser' | 'memory' | 'extensions' | 'settings' | 'tetris'
type ChatMessage = {
  id: string
  role: 'assistant' | 'user' | 'notice'
  content: string
  streaming?: boolean
  createdAt?: number
}

const MAX_FALLBACK_MESSAGES = 240

type TeamComposerProfile = {
  id: string
  name: string
  role: string
  detail: string
  asset: string
  accent: string
  state?: string
}

const TEAM_COMPOSER_PROFILES: TeamComposerProfile[] = [
  { id: 'adrian', name: 'Adrian', role: 'Team Lead', detail: 'Ekibi yönlendirir, doğru uzmanları çağırır ve nihai sonucu sahiplenir.', asset: '/assets/team/adrian.png', accent: '#FF8A16' },
  { id: 'sofia', name: 'Sofia', role: 'Product Strategist', detail: 'İhtiyaçları, kullanıcı yolculuğunu ve kabul ölçütlerini netleştirir.', asset: '/assets/team/sofia.png', accent: '#E8B342' },
  { id: 'vera', name: 'Vera', role: 'Software Engineer', detail: 'Uygulama özelliklerini güvenli, test edilebilir kodla hayata geçirir.', asset: '/assets/team/vera.png', accent: '#F04444' },
  { id: 'cassia', name: 'Cassia', role: 'Systems Architect', detail: 'API, veri modeli, depolama ve sistem sınırlarını tasarlar.', asset: '/assets/team/cassia.png', accent: '#9A50FF' },
  { id: 'mira', name: 'Mira', role: 'Product Designer', detail: 'Arayüzü, akışları ve kullanıcı deneyimini tasarlar.', asset: '/assets/team/mira.png', accent: '#FF3E9D' },
  { id: 'elena', name: 'Elena', role: 'Research & Integrations Analyst', detail: 'Araştırma yapar ve güvenilir dış sistem bağlantılarını inceler.', asset: '/assets/team/elena.png', accent: '#20D6D2' },
  { id: 'tessa', name: 'Tessa', role: 'Debugging Specialist', detail: 'Hataların kök nedenini bulur ve çözümü doğrular.', asset: '/assets/team/tessa.png', accent: '#FFC533' },
  { id: 'clara', name: 'Clara', role: 'Quality & Security Reviewer', detail: 'Test, güvenlik, izin ve regresyon risklerini bağımsız inceler.', asset: '/assets/team/clara.png', accent: '#58E93B' },
  { id: 'seren', name: 'Seren', role: 'Project Intelligence Specialist', detail: 'Proje bağlamını birleştirir, riskleri ve bağımlılıkları izler.', asset: '/assets/team/seren.png', accent: '#45E4E8' },
  { id: 'maren', name: 'Maren', role: 'DevOps Engineer', detail: 'Çalışma ortamı, dağıtım ve operasyon akışlarını yönetir.', asset: '/assets/team/maren.png', accent: '#3D9CFF' },
  { id: 'rina', name: 'Rina', role: 'Browser & Desktop Automation Specialist', detail: 'Tarayıcı ve masaüstü akışlarını güvenli biçimde otomatikleştirir.', asset: '/assets/team/rina.png', accent: '#8C3DFF' },
]

type AttachedFile = DraftAttachment

type MemoryUsage = Pick<MemoryContext, 'sources' | 'approximateTokens' | 'indexedAt'> & { truncated?: boolean; budget: number }

type QueuedTask = {
  id: string
  text: string
  attachments: AttachedFile[]
  createdAt: number
}

type ApprovalRequest = {
  requestId: string | number
  method: string
  params: Record<string, unknown>
  receivedAt: number
}

const EMPTY_BOOTSTRAP: BootstrapState = {
  health: { ok: false, ready: false },
  eventsUrl: '',
  account: null,
  rateLimits: null,
  models: [],
  threads: [],
  skills: [],
  apps: [],
}

const FALLBACK_MODELS: CodexModel[] = [
  { id: 'gpt-5.6-luna', displayName: 'GPT-5.6 Luna', defaultReasoningEffort: 'low', supportedReasoningEfforts: ['low', 'medium', 'high', 'xhigh', 'max'].map((reasoningEffort) => ({ reasoningEffort })) },
  { id: 'gpt-5.6-terra', displayName: 'GPT-5.6 Terra', defaultReasoningEffort: 'medium', supportedReasoningEfforts: ['low', 'medium', 'high', 'xhigh', 'max', 'ultra'].map((reasoningEffort) => ({ reasoningEffort })) },
  { id: 'gpt-5.6-sol', displayName: 'GPT-5.6 Sol', defaultReasoningEffort: 'high', supportedReasoningEfforts: ['low', 'medium', 'high', 'xhigh', 'max', 'ultra'].map((reasoningEffort) => ({ reasoningEffort })) },
]

const PERMISSION_PROFILE_OPTIONS: Array<{ value: PermissionProfileId; label: string; description: string }> = [
  { value: 'read-only', label: 'Yalnızca incele', description: 'Dosyaları ve proje bağlamını inceleyebilir; değişiklik yapamaz.' },
  { value: 'granular', label: 'Onay iste', description: 'Komut ve dosya değişikliklerinden önce her zaman sor.' },
  { value: 'workspace-auto', label: 'Benim için onayla', description: 'Yalnızca potansiyel olarak güvenli olmayan işlemler için sor.' },
  { value: 'full-access', label: 'Tam erişim', description: 'İnternete ve çalışma alanındaki dosyalara sınırsız erişim.' },
]

const WELCOME_MESSAGE: ChatMessage = {
  id: 'welcome',
  role: 'assistant',
  content: 'Ready to inspect the project, build a feature, or resolve a problem safely.',
}

const EMPTY_MEMORY: MemoryUniverse = {
  schemaVersion: '1.0', seed: 'empty', projects: [], clusters: [], artifacts: [], relations: [], events: [],
}

const PROVIDER_LABELS: Record<string, string> = {
  codex: 'Codex',
  'antigravity-local': 'Antigravity',
  bedrock: 'Amazon Bedrock',
}

const PROVIDER_STATE_LABELS: Record<string, string> = {
  unavailable: 'Unavailable',
  needs_login: 'Sign-in required',
  ready: 'Ready',
  busy: 'Busy',
  rate_limited: 'Rate limited',
  error: 'Needs attention',
}

function modelName(model: Pick<CodexModel, 'id' | 'model' | 'displayName'>) {
  return model.displayName || model.model || model.id
}

const STANDARD_WORKFLOW_EFFORTS = ['low', 'medium', 'high'] as const
const LUNA_WORKFLOW_EFFORTS = ['low', 'medium', 'high', 'xhigh', 'max'] as const
const SOL_TERRA_WORKFLOW_EFFORTS = ['low', 'medium', 'high', 'xhigh', 'max', 'ultra'] as const
const WORKFLOW_EFFORT_ORDER = ['minimal', ...STANDARD_WORKFLOW_EFFORTS, 'xhigh', 'max', 'ultra'] as const

type WorkflowModelOption = {
  value: string
  label: string
  providerId: string
  model: string
  supportedReasoningEfforts: WorkflowReasoningEffort[]
  defaultReasoningEffort: WorkflowReasoningEffort
}

function normalizeWorkflowReasoningEfforts(value: unknown): WorkflowReasoningEffort[] {
  if (!Array.isArray(value)) return []
  const efforts = value
    .map((entry) => typeof entry === 'string' ? entry : (entry && typeof entry === 'object' && 'reasoningEffort' in entry ? entry.reasoningEffort : ''))
    .filter((entry): entry is WorkflowReasoningEffort => typeof entry === 'string' && (WORKFLOW_EFFORT_ORDER as readonly string[]).includes(entry))
  return WORKFLOW_EFFORT_ORDER.filter((effort) => efforts.includes(effort))
}

function fallbackWorkflowReasoningEfforts(providerId: string, modelId: string): WorkflowReasoningEffort[] {
  const normalized = modelId.toLowerCase()
  if (providerId === 'antigravity-local') return [...STANDARD_WORKFLOW_EFFORTS]
  if (/(?:^|[-_:])luna(?:$|[-_:])/.test(normalized)) return [...LUNA_WORKFLOW_EFFORTS]
  if (/(?:^|[-_:])(?:sol|terra)(?:$|[-_:])/.test(normalized)) return [...SOL_TERRA_WORKFLOW_EFFORTS]
  return [...STANDARD_WORKFLOW_EFFORTS]
}

function workflowEffortsForModel(model: CodexModel | ProviderModel, providerId: string, modelId: string) {
  const advertised = normalizeWorkflowReasoningEfforts(model.supportedReasoningEfforts)
  return advertised.length ? advertised : fallbackWorkflowReasoningEfforts(providerId, modelId)
}

function workflowDefaultEffort(model: CodexModel | ProviderModel, supported: WorkflowReasoningEffort[]): WorkflowReasoningEffort {
  const advertised = typeof model.defaultReasoningEffort === 'string' && (WORKFLOW_EFFORT_ORDER as readonly string[]).includes(model.defaultReasoningEffort)
    ? model.defaultReasoningEffort as WorkflowReasoningEffort
    : undefined
  return advertised && supported.includes(advertised) ? advertised : supported.includes('medium') ? 'medium' : supported[0] || 'medium'
}

const WORKFLOW_EFFORT_LABELS: Record<WorkflowReasoningEffort, string> = {
  minimal: 'Minimal',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  xhigh: 'Extra High',
  max: 'Max',
  ultra: 'Ultra',
}

const CHAT_REASONING_LABELS: Record<WorkflowReasoningEffort, string> = {
  minimal: 'Minimal',
  low: 'Düşük',
  medium: 'Orta',
  high: 'Yüksek',
  xhigh: 'Ekstra yüksek',
  max: 'Max',
  ultra: 'Ultra',
}

const FEATURED_CHATGPT_MODELS = new Set(['gpt-5.6-sol', 'gpt-5.6-terra', 'gpt-5.6-luna'])

function workflowModelOptions(models: CodexModel[], providerModels: ProviderModel[]): WorkflowModelOption[] {
  const seen = new Set<string>()
  const options: WorkflowModelOption[] = []
  for (const model of models) {
    const value = `codex:${model.id}`
    if (seen.has(value)) continue
    seen.add(value)
    const supportedReasoningEfforts = workflowEffortsForModel(model, 'codex', model.id)
    options.push({ value, label: modelName(model), providerId: 'codex', model: model.id, supportedReasoningEfforts, defaultReasoningEffort: workflowDefaultEffort(model, supportedReasoningEfforts) })
  }
  for (const model of providerModels) {
    if (!model.available) continue
    const value = model.id.includes(':') ? model.id : `${model.providerId}:${model.id}`
    if (seen.has(value)) continue
    seen.add(value)
    const modelId = model.providerModelId || model.id.split(':').slice(1).join(':') || model.id
    const supportedReasoningEfforts = workflowEffortsForModel(model, model.providerId, modelId)
    options.push({ value, label: modelName(model), providerId: model.providerId, model: modelId, supportedReasoningEfforts, defaultReasoningEffort: workflowDefaultEffort(model, supportedReasoningEfforts) })
  }
  return options
}

function workflowSelection(value: string, fallbackModel: string, effort: string): ProviderSelection {
  const separator = value.indexOf(':')
  return {
    providerId: separator > 0 ? value.slice(0, separator) : 'codex',
    model: separator > 0 ? value.slice(separator + 1) : (value || fallbackModel),
    effort,
    timeoutMs: 10 * 60_000,
  }
}

function shortWorkflowModelLabel(value: string, options: WorkflowModelOption[], fallback: string) {
  const label = options.find((option) => option.value === value)?.label || fallback
  const normalized = label.replace(/^GPT[-\s]?5\.6[-\s]?/i, '').trim()
  return normalized || label
}

const WORKFLOW_ACTIVE_STATES = new Set(['draft', 'replanning', 'ready', 'running', 'running_step', 'testing', 'reviewing', 'retrying'])

function workflowIsActive(state: string | undefined) {
  return Boolean(state && WORKFLOW_ACTIVE_STATES.has(state))
}

function formatThreadTime(thread: CodexThread) {
  const raw = thread.updatedAt ?? thread.createdAt
  if (!raw) return 'önceki çalışma'
  const timestamp = raw > 1e12 ? raw : raw * 1000
  const distance = Date.now() - timestamp
  if (distance < 60_000) return 'şimdi'
  if (distance < 3_600_000) return `${Math.max(1, Math.floor(distance / 60_000))} dk`
  if (distance < 86_400_000) return `${Math.floor(distance / 3_600_000)} sa`
  return new Intl.DateTimeFormat('tr-TR', { day: 'numeric', month: 'short' }).format(timestamp)
}

function sanitizeUserText(value: string, limit = 240) {
  return value
    .replace(/<side_chat_context>[\s\S]*?<\/side_chat_context>/gi, '')
    .replace(/<memory_palace>[\s\S]*?<\/memory_palace>/gi, '')
    .replace(/<file_context\b[^>]*>[\s\S]*?<\/file_context>/gi, '')
    .replace(/<attached_files>[\s\S]*?<\/attached_files>/gi, '')
    .replace(/<[^>]{1,80}>/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, limit)
}

function userTaskTitle(value: string) {
  const firstLine = sanitizeUserText(value).split(/\r?\n/u)[0]?.trim() || 'New task'
  return firstLine.slice(0, 72)
}

function safeThreadTitle(thread: CodexThread, metadata?: TaskMetadata) {
  return userTaskTitle(metadata?.title || metadata?.rawUserText || thread.name || thread.preview || 'Adsız görev')
}

function planFromWorkflow(workflow: WorkflowPlan, graph?: AgentGraph | null): PlanState {
  return {
    id: workflow.id,
    title: workflow.title,
    status: workflow.state,
    source: 'workflow',
    owner: {
      agent: workflow.metadata?.teamMode ? 'Adrian · Team Lead' : 'Sol supervisor',
      provider: workflow.metadata?.supervisorProviderId,
      model: workflow.metadata?.supervisorModel,
    },
    explanation: workflow.metadata?.planSummary,
    activeStepId: workflow.currentStepId,
    agents: graph?.agents.map((agent) => ({
      id: agent.id,
      role: agent.role,
      title: agent.title,
      model: agent.model,
      state: agent.state,
      statusSummary: agent.statusSummary,
      assignedStepId: agent.assignedStepId,
    })),
    agentMessages: graph?.messages.map((message) => ({
      id: message.id,
      at: message.at ?? message.createdAt ?? undefined,
      from: message.fromLabel ?? message.from.id,
      agentId: message.agentId,
      preview: message.text ?? message.preview,
    })),
    steps: workflow.steps.map((step) => ({
      id: step.id,
      title: step.title,
      description: step.description,
      status: step.status,
      attempts: step.attempts,
      retries: step.retries,
      review: step.review?.summary,
      checks: step.checks?.join(' · '),
      files: step.files,
      commands: step.commands,
      owner: (step.currentExecution ?? step.execution ?? workflow.metadata?.workerSelection) ? {
        provider: (step.currentExecution ?? step.execution ?? workflow.metadata?.workerSelection)?.providerId,
        model: (step.currentExecution ?? step.execution ?? workflow.metadata?.workerSelection)?.model,
        agent: 'Worker',
      } : undefined,
    })),
    review: workflow.steps.find((step) => step.review)?.review ? {
      decision: workflow.steps.find((step) => step.review)?.review?.decision,
      summary: workflow.steps.find((step) => step.review)?.review?.summary,
    } : undefined,
    updatedAt: workflow.updatedAt,
  }
}

function inputForAttachments(attachments: AttachedFile[]): Array<Record<string, unknown>> {
  return attachments.flatMap((file): Array<Record<string, unknown>> => file.kind === 'image' && file.dataUrl
    ? [{ type: 'image', url: file.dataUrl }]
    : file.kind === 'text' && file.content
      ? [{ type: 'text', text: `Attached file: ${file.name}\n\n${file.content}` }]
      : [])
}

function initials(value?: string) {
  if (!value) return 'C'
  return value.split(/\s|@/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || 'C'
}

function formatMessageTime(timestamp: number) {
  return new Intl.DateTimeFormat('tr-TR', { hour: '2-digit', minute: '2-digit' }).format(timestamp)
}

function messagesFromThread(payload: unknown): ChatMessage[] {
  if (!payload || typeof payload !== 'object') return []
  const root = payload as Record<string, unknown>
  const thread = (root.thread && typeof root.thread === 'object' ? root.thread : root) as Record<string, unknown>
  const turns = Array.isArray(thread.turns) ? thread.turns : []
  const messages: ChatMessage[] = []
  for (const turn of turns) {
    if (!turn || typeof turn !== 'object') continue
    const items = Array.isArray((turn as Record<string, unknown>).items) ? (turn as Record<string, unknown>).items as unknown[] : []
    for (const item of items) {
      if (!item || typeof item !== 'object') continue
      const record = item as Record<string, unknown>
      if (record.type === 'userMessage' && Array.isArray(record.content)) {
        const content = record.content
          .filter((part): part is Record<string, unknown> => Boolean(part && typeof part === 'object'))
          .filter((part) => part.type === 'text' && typeof part.text === 'string')
          .map((part) => String(part.text))
          .join('\n')
        if (content) messages.push({ id: String(record.id ?? `user-${messages.length}`), role: 'user', content })
      }
      if (record.type === 'agentMessage' && typeof record.text === 'string' && record.text.trim()) {
        messages.push({ id: String(record.id ?? `assistant-${messages.length}`), role: 'assistant', content: record.text })
      }
    }
  }
  return messages
}

export default function Workbench() {
  const [projects, setProjects] = useState<StudioProject[]>(loadProjects)
  const [activeProjectId, setActiveProjectId] = useState(() => loadActiveProjectId(loadProjects()))
  const [view, setView] = useState<View>(() => {
    const requested = new URLSearchParams(window.location.search).get('view')
    if (requested === 'tetris' || requested === 'game') return 'tetris'
    return requested === 'memory' ? 'memory' : 'chat'
  })
  const [bootstrap, setBootstrap] = useState<BootstrapState>(EMPTY_BOOTSTRAP)
  const [bootstrapLoading, setBootstrapLoading] = useState(true)
  const [bootstrapError, setBootstrapError] = useState('')
  const [connection, setConnection] = useState<'checking' | 'ready' | 'offline'>('checking')
  const [eventTransport, setEventTransport] = useState<'connecting' | 'connected' | 'reconnecting'>('connecting')
  const [refreshNonce, setRefreshNonce] = useState(0)
  const [settings, setSettings] = useState<StudioSettings>(loadSettings)
  const [selectedModel, setSelectedModel] = useState(() => loadSettings().defaultModel)
  const [selectedReasoningEffort, setSelectedReasoningEffort] = useState<WorkflowReasoningEffort>('medium')
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null)
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null)
  const [taskRuntimeRevision, setTaskRuntimeRevision] = useState(0)
  const [activeExecutionId, setActiveExecutionId] = useState<string | null>(null)
  const [activeTurnId, setActiveTurnId] = useState<string | null>(null)
  const [loadingThreadId, setLoadingThreadId] = useState<string | null>(null)
  const [threadLoadError, setThreadLoadError] = useState<{ threadId: string; message: string } | null>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([WELCOME_MESSAGE])
  const [composer, setComposer] = useState('')
  const [attachments, setAttachments] = useState<AttachedFile[]>([])
  const [queue, setQueue] = useState<QueuedTask[]>([])
  const [sideContext, setSideContext] = useState<string | null>(null)
  const [sideParentThreadId, setSideParentThreadId] = useState<string | null>(null)
  const [running, setRunning] = useState(false)
  const [stopping, setStopping] = useState(false)
  const [, setRouteDecision] = useState<RouteDecision | null>(null)
  const [memoryUsage, setMemoryUsage] = useState<MemoryUsage | null>(null)
  const [sidebarSearch, setSidebarSearch] = useState('')
  const [taskMetadata, setTaskMetadata] = useState<Record<string, TaskMetadata>>(loadTaskMetadata)
  const [showArchivedTasks, setShowArchivedTasks] = useState(false)
  const [showInternalTasks, setShowInternalTasks] = useState(false)
  const [extensionSearch, setExtensionSearch] = useState('')
  const [projectModalOpen, setProjectModalOpen] = useState(false)
  const [projectDraft, setProjectDraft] = useState({ name: '', cwd: '' })
  const [projectError, setProjectError] = useState('')
  const [loginPending, setLoginPending] = useState(false)
  const [approvalRequest, setApprovalRequest] = useState<ApprovalRequest | null>(null)
  const [approvalPending, setApprovalPending] = useState(false)
  const [approvalError, setApprovalError] = useState('')
  const [workflowMode, setWorkflowMode] = useState(false)
  const [teamMode, setTeamMode] = useState(false)
  const [workflowPlanning, setWorkflowPlanning] = useState(false)
  const [workflow, setWorkflow] = useState<WorkflowPlan | null>(null)
  const [workflowGraph, setWorkflowGraph] = useState<AgentGraph | null>(null)
  const [teamConversationId, setTeamConversationId] = useState<string | null>(null)
  const [workflowPlanModel, setWorkflowPlanModel] = useState(() => loadSettings().workflowPlanModel || 'codex:gpt-5.6-sol')
  const [workflowBuildModel, setWorkflowBuildModel] = useState(() => loadSettings().workflowBuildModel || 'codex:gpt-5.6-luna')
  const [workflowPlanEffort, setWorkflowPlanEffort] = useState<WorkflowReasoningEffort>(() => loadSettings().workflowPlanEffort || 'high')
  const [workflowBuildEffort, setWorkflowBuildEffort] = useState<WorkflowReasoningEffort>(() => loadSettings().workflowBuildEffort || 'low')
  const [remoteOperations, setRemoteOperations] = useState<RemoteOperation[]>([])
  const [remoteChats, setRemoteChats] = useState<RemoteChat[]>([])
  const [remoteRequestNonce, setRemoteRequestNonce] = useState(0)
  const [browserCapabilities, setBrowserCapabilities] = useState<BrowserCapabilities | null>(null)
  const [browserSessions, setBrowserSessions] = useState<BrowserSession[]>([])
  const [browserUrl, setBrowserUrl] = useState('http://127.0.0.1:4173')
  const [browserError, setBrowserError] = useState('')
  const [providers, setProviders] = useState<ProviderDescriptor[]>([])
  const [providerModels, setProviderModels] = useState<ProviderModel[]>([])
  const [providerLoading, setProviderLoading] = useState(false)
  const [providerError, setProviderError] = useState('')
  const [preflight, setPreflight] = useState<RuntimePreflight | null>(null)
  const [terminalEvents, setTerminalEvents] = useState<TerminalOutput[]>([])
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false)
  const [selectedProvider, setSelectedProvider] = useState('codex')
  const composerRef = useRef<HTMLTextAreaElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const taskRuntimeRegistry = useRef(new TaskRuntimeRegistry()).current
  const queueDispatching = useRef(new Set<string>()).current
  const queuedCompletions = useRef(new Set<string>()).current
  const completionLedger = useRef(new Set<string>()).current
  const threadSelectionRef = useRef(0)
  const taskMetadataRef = useRef(taskMetadata)
  const queueRef = useRef<QueuedTask[]>([])
  const activeIdentityRef = useRef({ threadId: null as string | null, taskId: null as string | null, executionId: null as string | null, turnId: null as string | null })
  const workflowRef = useRef<WorkflowPlan | null>(null)
  const activeProjectRef = useRef({ id: '', cwd: '' })
  const dispatchQueuedRef = useRef<(threadId: string, completedTurnId?: string) => Promise<void>>(() => Promise.resolve())
  const refreshRef = useRef<() => void>(() => undefined)
  const updateTaskMetaRef = useRef<(threadId: string, patch: Partial<Omit<TaskMetadata, 'threadId'>>) => void>(() => undefined)
  const activeTaskState: TaskState | undefined = useMemo(() => {
    void taskRuntimeRevision
    return activeTaskId ? taskRuntimeRegistry.get(activeTaskId).state : undefined
  }, [activeTaskId, taskRuntimeRevision])

  const activeProject = projects.find((project) => project.id === activeProjectId) ?? projects[0]!
  const draftKey = activeTaskId ?? activeThreadId ?? `new:${activeProject.id}`
  const models = bootstrap.models.length ? bootstrap.models : FALLBACK_MODELS
  const workflowModelCatalog = useMemo(() => workflowModelOptions(models, providerModels), [models, providerModels])
  const accountEmail = bootstrap.account?.account?.email
  const planType = bootstrap.account?.planType || bootstrap.account?.account?.planType
  const quotaUsed = bootstrap.rateLimits?.rateLimits?.primary?.usedPercent
  const quotaReached = typeof quotaUsed === 'number' && quotaUsed >= 100
  const authMode = bootstrap.account?.authMode || bootstrap.account?.account?.type
  const signedIn = authMode === 'chatgpt' || Boolean(accountEmail) || planType != null

  useEffect(() => {
    activeProjectRef.current = { id: activeProject.id, cwd: activeProject.cwd }
  }, [activeProject.cwd, activeProject.id])

  useEffect(() => {
    taskMetadataRef.current = taskMetadata
  }, [taskMetadata])

  const setActiveIdentity = useCallback((identity: Partial<typeof activeIdentityRef.current>) => {
    const next = { ...activeIdentityRef.current, ...identity }
    activeIdentityRef.current = next
    setActiveThreadId(next.threadId)
    setActiveTaskId(next.taskId)
    setActiveExecutionId(next.executionId)
    setActiveTurnId(next.turnId)
  }, [])

  const resetActiveTaskView = useCallback(() => {
    setActiveIdentity({ threadId: null, taskId: null, executionId: null, turnId: null })
    setRunning(false)
    setStopping(false)
    setRouteDecision(null)
    setMemoryUsage(null)
    setWorkflow(null)
    setWorkflowGraph(null)
    setTeamConversationId(null)
    setWorkflowMode(false)
    setTeamMode(false)
    setWorkflowPlanning(false)
    setSideParentThreadId(null)
  }, [setActiveIdentity])

  useEffect(() => {
    activeIdentityRef.current = { threadId: activeThreadId, taskId: activeTaskId, executionId: activeExecutionId, turnId: activeTurnId }
  }, [activeExecutionId, activeTaskId, activeThreadId, activeTurnId])

  useEffect(() => { workflowRef.current = workflow }, [workflow])

  useEffect(() => {
    const isAsyncPlanningState = workflow?.state === 'draft' || workflow?.state === 'replanning'
    if (!workflow?.id || !workflowPlanning || !isAsyncPlanningState) return undefined
    let cancelled = false
    const refreshPlanning = async () => {
      try {
        const next = (await codexApi.readWorkflow(workflow.id)).workflow
        if (cancelled) return
        setWorkflow(next)
        const planning = next.state === 'draft' || next.state === 'replanning'
        setWorkflowPlanning(planning)
        setRunning(workflowIsActive(next.state))
        if (!planning) setStopping(false)
      } catch {
        // The durable event stream remains authoritative; polling is only a recovery path.
      }
    }
    const timer = window.setInterval(() => void refreshPlanning(), 1_000)
    void refreshPlanning()
    return () => { cancelled = true; window.clearInterval(timer) }
  }, [workflow?.id, workflow?.state, workflowPlanning])

  useEffect(() => {
    if (!workflow?.id) return
    let cancelled = false
    void codexApi.workflowAgents(workflow.id).then(({ graph }) => {
      if (!cancelled) setWorkflowGraph(graph)
    }).catch(() => undefined)
    return () => { cancelled = true }
  }, [workflow?.id])

  const updateTaskMeta = useCallback((threadId: string, patch: Partial<Omit<TaskMetadata, 'threadId'>>) => {
    setTaskMetadata((current) => {
      const next: TaskMetadata = {
        threadId,
        projectId: current[threadId]?.projectId || activeProject.id,
        cwd: current[threadId]?.cwd || activeProject.cwd,
        environment: current[threadId]?.environment || 'local',
        ...current[threadId],
        ...patch,
      }
      const all = { ...current, [threadId]: next }
      saveTaskMetadata(all)
      return all
    })
  }, [activeProject.cwd, activeProject.id])

  useEffect(() => {
    const draft = loadTaskDraft(draftKey)
    setComposer(draft.text)
    setAttachments(draft.attachments)
    setQueue(loadTaskQueue(draftKey).map((item) => ({ id: item.id, text: item.text, attachments: item.attachments, createdAt: item.createdAt })))
  }, [draftKey])

  useEffect(() => { saveTaskDraft(draftKey, { text: composer, attachments }) }, [attachments, composer, draftKey])
  useEffect(() => { saveTaskQueue(draftKey, queue) }, [draftKey, queue])
  useEffect(() => { queueRef.current = queue }, [queue])

  const refresh = useCallback(() => setRefreshNonce((value) => value + 1), [])
  const updateSettings = useCallback((patch: Partial<StudioSettings>) => {
    setSettings((current) => {
      const next = { ...current, ...patch }
      saveSettings(next)
      return next
    })
    if (patch.defaultModel) { setSelectedProvider('codex'); setSelectedModel(patch.defaultModel) }
  }, [])

  const resolveWorkflowEffort = useCallback((modelValue: string, requested: string, fallback: WorkflowReasoningEffort) => {
    const option = workflowModelCatalog.find((item) => item.value === modelValue)
    if (!option) return (requested || fallback) as WorkflowReasoningEffort
    return option.supportedReasoningEfforts.includes(requested as WorkflowReasoningEffort)
      ? requested as WorkflowReasoningEffort
      : option.defaultReasoningEffort
  }, [workflowModelCatalog])

  useEffect(() => {
    const nextPlanEffort = resolveWorkflowEffort(workflowPlanModel, workflowPlanEffort, 'high')
    const nextBuildEffort = resolveWorkflowEffort(workflowBuildModel, workflowBuildEffort, 'low')
    const patch: Partial<StudioSettings> = {}
    if (nextPlanEffort !== workflowPlanEffort) {
      setWorkflowPlanEffort(nextPlanEffort)
      patch.workflowPlanEffort = nextPlanEffort
    }
    if (nextBuildEffort !== workflowBuildEffort) {
      setWorkflowBuildEffort(nextBuildEffort)
      patch.workflowBuildEffort = nextBuildEffort
    }
    if (Object.keys(patch).length) updateSettings(patch)
  }, [resolveWorkflowEffort, updateSettings, workflowBuildEffort, workflowBuildModel, workflowPlanEffort, workflowPlanModel])

  const dispatchQueued = useCallback(async (threadId: string, completedTurnId?: string) => {
    // A completion from a background task must never consume the queue belonging
    // to the currently visible task.
    if (activeIdentityRef.current.threadId !== threadId) return
    const key = `${threadId}:${activeIdentityRef.current.taskId ?? 'thread'}`
    if (queueDispatching.has(key)) return
    const completionKey = `${key}:${completedTurnId ?? 'unknown'}`
    if (queuedCompletions.has(completionKey)) return
    const next = queueRef.current[0]
    if (!next) return
    queuedCompletions.add(completionKey)
    queueDispatching.add(key)
    setQueue((current) => current.slice(1))
    setMessages((current) => [...current, { id: `queued-user-${next.id}`, role: 'user', content: next.text + (next.attachments.length ? `\n\nAttached: ${next.attachments.map((file) => file.name).join(', ')}` : '') }])
    setRunning(true)
    try {
      const enriched = next.text + (next.attachments.some((file) => file.kind === 'text') ? `\n\nAttached files:\n${next.attachments.filter((file) => file.kind === 'text').map((file) => `--- ${file.name} ---\n${file.content ?? ''}`).join('\n\n')}` : '')
      let model = selectedModel
      let effort: string | undefined = selectedReasoningEffort
      if (selectedModel === 'auto') {
        const decision = await codexApi.route(next.text)
        model = decision.selectedModel
        effort = decision.effort
        setRouteDecision(decision)
      }
      const turn = await codexApi.startTurn(threadId, {
        text: enriched,
        input: [{ type: 'text', text: enriched }, ...inputForAttachments(next.attachments.filter((file) => file.kind === 'image'))],
        model,
        effort,
        summary: 'detailed',
        cwd: activeProject.cwd,
      })
      if (turn.turn?.id) setActiveIdentity({ threadId, turnId: turn.turn.id })
    } catch (error) {
      queuedCompletions.delete(completionKey)
      setQueue((current) => [next, ...current])
      setRunning(false)
      setMessages((current) => [...current, { id: `queue-error-${Date.now()}`, role: 'notice', content: error instanceof Error ? error.message : 'Queued task could not be started.' }])
    } finally {
      queueDispatching.delete(key)
    }
  }, [activeProject.cwd, queueDispatching, queuedCompletions, selectedModel, selectedReasoningEffort, setActiveIdentity])

  useEffect(() => {
    dispatchQueuedRef.current = dispatchQueued
    refreshRef.current = refresh
    updateTaskMetaRef.current = updateTaskMeta
  }, [dispatchQueued, refresh, updateTaskMeta])

  useEffect(() => {
    let cancelled = false
    let attempt = 0
    let retryTimer: number | undefined
    const scheduleRetry = () => {
      if (cancelled) return
      attempt += 1
      const delay = Math.min(8_000, 400 * 2 ** Math.min(attempt - 1, 5))
      retryTimer = window.setTimeout(load, delay)
      if (attempt >= 5) setConnection('offline')
      else setConnection((current) => current === 'ready' ? current : 'checking')
    }
    const load = () => {
      if (cancelled) return
      setBootstrapLoading(true)
      setBootstrapError('')
      setConnection((current) => current === 'ready' ? current : 'checking')
      loadBootstrap(activeProject.cwd)
        .then((next) => {
          if (cancelled) return
          if (!next.health.ready) {
            setBootstrap(next)
            setBootstrapError('Codex catalog is unavailable while the local engine is offline.')
            scheduleRetry()
            return
          }
          attempt = 0
          setBootstrap(next)
          setConnection('ready')
        })
        .catch(() => {
          if (cancelled) return
          setBootstrap((current) => current === EMPTY_BOOTSTRAP ? EMPTY_BOOTSTRAP : current)
          setBootstrapError('Codex catalog could not be loaded.')
          scheduleRetry()
        })
        .finally(() => { if (!cancelled) setBootstrapLoading(false) })
    }
    load()
    return () => {
      cancelled = true
      if (retryTimer !== undefined) window.clearTimeout(retryTimer)
    }
  }, [activeProject.cwd, refreshNonce])

  useEffect(() => {
    if (connection !== 'ready') return
    void codexApi.syncProjects(projects, activeProject.id, settings).catch(() => undefined)
  }, [activeProject.id, connection, projects, settings])

  useEffect(() => {
    if (connection !== 'ready') return
    let cancelled = false
    setProviderLoading(true)
    setProviderError('')
    Promise.all([codexApi.providers(), codexApi.providerModels(), codexApi.preflight()])
      .then(([status, catalog, nextPreflight]) => {
        if (cancelled) return
        setProviders(Array.isArray(status.providers) ? status.providers : [])
        setProviderModels(Array.isArray(catalog.data) ? catalog.data : [])
        setPreflight(nextPreflight)
      })
      .catch((error) => {
        if (cancelled) return
        setProviderError(error instanceof Error ? error.message : 'Provider status could not be loaded.')
      })
      .finally(() => { if (!cancelled) setProviderLoading(false) })
    return () => { cancelled = true }
  }, [connection, refreshNonce])

  const refreshBrowserStatus = useCallback(async () => {
    setBrowserError('')
    try {
      const [capabilities, sessions] = await Promise.all([codexApi.browserCapabilities(), codexApi.browserSessions()])
      setBrowserCapabilities(capabilities)
      setBrowserSessions(Array.isArray(sessions.sessions) ? sessions.sessions : [])
    } catch (error) {
      setBrowserError(error instanceof Error ? error.message : 'Browser status could not be loaded.')
      throw error
    }
  }, [])

  useEffect(() => {
    if (connection !== 'ready') return
    let cancelled = false
    const load = async () => {
      try {
        const [capabilities, sessions] = await Promise.all([codexApi.browserCapabilities(), codexApi.browserSessions()])
        if (cancelled) return
        setBrowserCapabilities(capabilities)
        setBrowserSessions(Array.isArray(sessions.sessions) ? sessions.sessions : [])
        setBrowserError('')
      } catch (error) {
        if (!cancelled) setBrowserError(error instanceof Error ? error.message : 'Browser status could not be loaded.')
      }
    }
    void load()
    const timer = window.setInterval(() => void load(), 2_500)
    return () => { cancelled = true; window.clearInterval(timer) }
  }, [connection, refreshNonce])

  useEffect(() => {
    if (connection !== 'ready') return
    const load = () => void codexApi.remoteOperations().then(({ operations }) => setRemoteOperations(Array.isArray(operations) ? operations : [])).catch(() => undefined)
    load(); const timer = window.setInterval(load, 1500); return () => window.clearInterval(timer)
  }, [connection, refreshNonce])

  useEffect(() => {
    if (connection !== 'ready') return
    const load = () => void codexApi.remoteChats().then(({ chats }) => setRemoteChats(Array.isArray(chats) ? chats : [])).catch(() => undefined)
    load(); const timer = window.setInterval(load, 2500); return () => window.clearInterval(timer)
  }, [connection, refreshNonce])

  const selectRemoteChat = async (chat: RemoteChat) => {
    resetActiveTaskView()
    setView('chat')
    try {
      const { chat: full } = await codexApi.remoteChat(chat.id)
      setMessages((full.messages || []).map((message) => ({ id: message.id, role: message.role, content: message.content })))
    } catch (error) {
      setMessages([{ id: 'remote-chat-error', role: 'notice', content: error instanceof Error ? error.message : 'Shared Code conversation could not be loaded.' }])
    }
  }

  const decideRemoteOperation = async (id: string, decision: 'approve' | 'decline') => {
    await codexApi.decideRemoteOperation(id, decision)
    setRemoteOperations((await codexApi.remoteOperations()).operations)
  }

  useEffect(() => {
    if (connection !== 'ready' || !bootstrap.eventsUrl) return
    setTerminalEvents([])
    setEventTransport('connecting')
    const stream = new EventSource(bootstrap.eventsUrl)

    const parse = (event: Event) => {
      if (!(event instanceof MessageEvent)) return null
      try { return JSON.parse(event.data) as Record<string, unknown> } catch { return null }
    }
    const params = (event: Event) => {
      const packet = parse(event)
      return packet?.params && typeof packet.params === 'object' ? packet.params as Record<string, unknown> : packet
    }
    const projectRuntime = (runtime: ReturnType<typeof taskRuntimeRegistry.get>) => {
      const active = activeIdentityRef.current
      if (active.taskId !== runtime.taskId) return
      setTaskRuntimeRevision((revision) => revision + 1)
      setRunning(runtime.state.running)
      const nextTurnId = runtime.state.activeTurnId ?? (runtime.state.running ? active.turnId : null)
      setActiveTurnId(nextTurnId)
      activeIdentityRef.current.turnId = nextTurnId
      if (!runtime.state.running) setStopping(false)
      if (runtime.state.messages.length) {
        setMessages((current) => [
          ...current.filter((message) => message.role !== 'assistant' || !message.streaming),
          ...runtime.state.messages.map((message) => ({ id: message.id, role: message.role, content: message.content, streaming: message.streaming })),
        ])
      }
    }
    const replayRuntimes = async () => {
      await Promise.all(taskRuntimeRegistry.entries().map(async ([taskId, runtime]) => {
        const after = runtime.state.events.at(-1)?.sequence ?? -1
        await runtime.replay(async (url) => {
          const query = new URL(url, window.location.origin).searchParams
          return codexApi.taskEvents(taskId, Number(query.get('after') ?? after), Number(query.get('limit') ?? 500))
        }, '/api/codex/task-events', after).catch(() => runtime.state)
        projectRuntime(runtime)
        for (const event of runtime.state.events) {
          if (event.sequence <= after || event.method !== 'turn/completed') continue
          const active = activeIdentityRef.current
          if (active.taskId !== taskId) continue
          const payload = event.payload && typeof event.payload === 'object' ? event.payload as Record<string, unknown> : null
          const completedStatus = typeof payload?.status === 'string' ? payload.status : typeof (payload?.turn as Record<string, unknown> | undefined)?.status === 'string' ? String((payload?.turn as Record<string, unknown>).status) : 'completed'
          const turnId = typeof event.turnId === 'string' ? event.turnId : active.turnId
          if (!shouldRunCompletionSideEffects(completionLedger, taskId, turnId)) continue
          if (active.threadId) {
            const cwd = taskMetadataRef.current[active.threadId]?.cwd || activeProjectRef.current.cwd
            void codexApi.captureMemory(cwd, active.threadId).catch(() => undefined)
          }
          if (active.threadId && !['interrupted', 'cancelled', 'failed', 'error'].includes(completedStatus)) void dispatchQueuedRef.current(active.threadId, turnId || undefined)
          refreshRef.current()
        }
      }))
    }
    const onAppError = (event: Event) => {
      if (!(event instanceof MessageEvent)) return
      const data = params(event)
      const active = activeIdentityRef.current
      const eventThreadId = typeof data?.threadId === 'string' ? data.threadId : null
      const eventTaskId = typeof data?.taskId === 'string' ? data.taskId : null
      if (!eventThreadId && !eventTaskId) return
      if ((eventThreadId && eventThreadId !== active.threadId) || (eventTaskId && eventTaskId !== active.taskId)) return
      const nested = data?.error as Record<string, unknown> | undefined
      const message = typeof nested?.message === 'string' ? nested.message : null
      if (message) setMessages((current) => [...current, { id: `notice-${Date.now()}`, role: 'notice', content: message }])
    }
    const onRpc = (event: Event) => {
      const packet = parse(event)
      if (!packet) return
      if (packet.kind === 'request' && packet.requestId != null && [
        'item/commandExecution/requestApproval',
        'item/fileChange/requestApproval',
        'item/permissions/requestApproval',
        'item/tool/requestUserInput',
        'mcpServer/elicitation/request',
        'applyPatchApproval',
        'execCommandApproval',
      ].includes(String(packet.method))) {
        setApprovalError('')
        setApprovalRequest({
          requestId: packet.requestId as string | number,
          method: String(packet.method),
          params: (packet.params as Record<string, unknown> | undefined) ?? {},
          receivedAt: Date.now(),
        })
      }
      if (packet.method === 'serverRequest/resolved') {
        const resolved = packet.params as Record<string, unknown> | undefined
        setApprovalRequest((current) => current && String(current.requestId) === String(resolved?.requestId) ? null : current)
      }
    }
    const onTerminal = (event: Event) => {
      const packet = parse(event)
      const data = params(event)
      const active = activeIdentityRef.current
      if (!data || typeof data.sessionId !== 'string' || (active.taskId && data.taskId !== active.taskId)) return
      const terminalEvent: TerminalOutput = {
        sessionId: String(data.sessionId),
        taskId: String(data.taskId ?? ''),
        stream: data.stream === 'stderr' ? 'stderr' : 'stdout',
        text: typeof data.text === 'string' ? data.text : undefined,
        sequence: typeof data.sequence === 'number' ? data.sequence : undefined,
        truncated: data.truncated === true,
        exitCode: typeof data.exitCode === 'number' ? data.exitCode : data.exitCode === null ? null : undefined,
        status: typeof data.status === 'string' ? data.status : packet?.method === 'terminal/exited' ? 'exited' : undefined,
        reason: typeof data.reason === 'string' ? data.reason : null,
      }
      setTerminalEvents((current) => [...current.slice(-200), terminalEvent])
    }
    const onTaskEvent = (event: Event) => {
      const packet = parse(event)
      const taskEvent = packet?.event
      if (!taskEvent || typeof taskEvent !== 'object') return
      const record = taskEvent as Record<string, unknown>
      if (typeof record.taskId !== 'string') return
      const active = activeIdentityRef.current
      const liveWorkflow = workflowRef.current
      const matchesTask = record.taskId === active.taskId
      const matchesThread = Boolean(active.threadId && record.threadId === active.threadId)
      const matchesWorkflow = Boolean(liveWorkflow?.id && record.workflowId === liveWorkflow.id)
      if (typeof record.threadId === 'string' && record.threadId !== active.threadId && (record.method === 'turn/completed' || record.method === 'turn/started')) {
        updateTaskMetaRef.current(record.threadId, { projectId: activeProjectRef.current.id, cwd: activeProjectRef.current.cwd, unread: true, lastOpenedAt: Date.now() })
      }
      const runtime = taskRuntimeRegistry.ingest(record as never)
      if (!matchesTask && !matchesThread && !matchesWorkflow) return
      projectRuntime(runtime)
      const workflowPayload = record.payload && typeof record.payload === 'object' ? record.payload as Record<string, unknown> : null
      const workflowSnapshot = workflowPayload?.workflow
      if (workflowSnapshot && typeof workflowSnapshot === 'object' && matchesWorkflow) {
        const nextWorkflow = workflowSnapshot as WorkflowPlan
        setWorkflow(nextWorkflow)
        setRunning(workflowIsActive(nextWorkflow.state))
        setWorkflowPlanning(nextWorkflow.state === 'draft' || nextWorkflow.state === 'replanning')
        if (!workflowIsActive(nextWorkflow.state)) setStopping(false)
      }
      const graphSnapshot = workflowPayload?.graph
      if (graphSnapshot && typeof graphSnapshot === 'object' && matchesWorkflow) setWorkflowGraph(graphSnapshot as AgentGraph)
      if (typeof record.turnId === 'string') {
        activeIdentityRef.current.turnId = record.turnId
        setActiveTurnId(record.turnId)
      }
      if (record.method === 'turn/started') setStopping(false)
      if (runtime.state.messages.length) {
        setMessages((current) => [
          ...current.filter((message) => message.role !== 'assistant' || !message.streaming),
          ...runtime.state.messages.map((message) => ({ id: message.id, role: message.role, content: message.content, streaming: message.streaming })),
        ])
      }
      if (record.method === 'turn/completed') {
        const payload = record.payload && typeof record.payload === 'object' ? record.payload as Record<string, unknown> : null
        const completedStatus = typeof payload?.status === 'string' ? payload.status : typeof (payload?.turn as Record<string, unknown> | undefined)?.status === 'string' ? String((payload?.turn as Record<string, unknown>).status) : 'completed'
        const turnId = typeof record.turnId === 'string' ? record.turnId : active.turnId
        if (!shouldRunCompletionSideEffects(completionLedger, record.taskId, turnId)) return
        setStopping(false)
        if (active.threadId) {
          const cwd = taskMetadataRef.current[active.threadId]?.cwd || activeProjectRef.current.cwd
          void codexApi.captureMemory(cwd, active.threadId).catch(() => undefined)
        }
        if (active.threadId && !['interrupted', 'cancelled', 'failed', 'error'].includes(completedStatus)) void dispatchQueuedRef.current(active.threadId, turnId || undefined)
        refreshRef.current()
      }
    }
    stream.onopen = () => {
      setEventTransport('connected')
      void replayRuntimes()
    }
    stream.onerror = () => setEventTransport('reconnecting')
    stream.addEventListener('task:event', onTaskEvent)
    stream.addEventListener('app:error', onAppError)
    stream.addEventListener('rpc', onRpc)
    stream.addEventListener('terminal:output', onTerminal)
    stream.addEventListener('terminal:exited', onTerminal)
    stream.addEventListener('terminal:started', onTerminal)
    return () => {
      stream.close()
      setEventTransport('connecting')
    }
  }, [bootstrap.eventsUrl, connection, taskRuntimeRegistry])

  const filteredThreads = useMemo(() => {
    const query = sidebarSearch.trim().toLocaleLowerCase('tr-TR')
    return bootstrap.threads
      .filter((thread) => showInternalTasks || !(taskMetadata[thread.id]?.internal || thread.loci?.internal))
      .filter((thread) => showArchivedTasks || !taskMetadata[thread.id]?.archived)
      .filter((thread) => {
        if (!query) return true
        const metadata = taskMetadata[thread.id]
        return `${metadata?.title ?? ''} ${thread.name ?? ''} ${thread.preview ?? ''} ${thread.cwd ?? ''} ${metadata?.branch ?? ''}`.toLocaleLowerCase('tr-TR').includes(query)
      })
      .sort((left, right) => {
        const leftMeta = taskMetadata[left.id]
        const rightMeta = taskMetadata[right.id]
        if (Boolean(leftMeta?.pinned) !== Boolean(rightMeta?.pinned)) return leftMeta?.pinned ? -1 : 1
        if (Boolean(leftMeta?.unread) !== Boolean(rightMeta?.unread)) return leftMeta?.unread ? -1 : 1
        return Number(right.updatedAt ?? right.createdAt ?? 0) - Number(left.updatedAt ?? left.createdAt ?? 0)
      })
  }, [bootstrap.threads, showArchivedTasks, showInternalTasks, sidebarSearch, taskMetadata])

  const renameTask = (thread: CodexThread) => {
    const current = safeThreadTitle(thread, taskMetadata[thread.id])
    const title = window.prompt('Rename task', current)?.trim()
    if (!title || title === current) return
    updateTaskMeta(thread.id, { title, unread: false, lastOpenedAt: Date.now() })
  }

  const toggleTaskPinned = (thread: CodexThread) => {
    updateTaskMeta(thread.id, { pinned: !taskMetadata[thread.id]?.pinned })
  }

  const toggleTaskArchived = (thread: CodexThread) => {
    updateTaskMeta(thread.id, { archived: !taskMetadata[thread.id]?.archived, unread: false })
  }

  const selectThread = async (thread: CodexThread) => {
    const selectionId = ++threadSelectionRef.current
    const threadCwd = thread.cwd || taskMetadata[thread.id]?.cwd || activeProject.cwd
    const isCurrentSelection = () => threadSelectionRef.current === selectionId
    setLoadingThreadId(thread.id)
    setThreadLoadError(null)
    updateTaskMeta(thread.id, { projectId: activeProject.id, cwd: threadCwd, unread: false, lastOpenedAt: Date.now() })
    resetActiveTaskView()
    setActiveIdentity({ threadId: thread.id, taskId: null, executionId: null, turnId: null })
    setView('chat')
    setMessages([{ id: 'loading-thread', role: 'notice', content: 'Görev geçmişi yükleniyor…' }])
    try {
      await codexApi.resumeThread(thread.id, { cwd: threadCwd })
      if (!isCurrentSelection()) return
      const taskId = await taskIdForThread(thread.id)
      setActiveIdentity({ threadId: thread.id, taskId, executionId: taskId, turnId: null })
      const runtime = taskRuntimeRegistry.get(taskId)
      await runtime.replay(async (url) => {
        const query = new URL(url, window.location.origin).searchParams
        return codexApi.taskEvents(taskId, Number(query.get('after') ?? -1), Number(query.get('limit') ?? 500))
      }).catch(() => runtime.state)
      if (!isCurrentSelection()) return
      if (activeIdentityRef.current.taskId === taskId) {
        setTaskRuntimeRevision((revision) => revision + 1)
        setRunning(runtime.state.running)
        setActiveTurnId(runtime.state.activeTurnId ?? null)
        activeIdentityRef.current.turnId = runtime.state.activeTurnId ?? null
      }
      if (runtime.state.messages.length) {
        setMessages(runtime.state.messages.map((message) => ({ id: message.id, role: message.role, content: message.content, streaming: message.streaming })))
      } else {
        const historyResponse = await codexApi.readThread(thread.id)
        if (!isCurrentSelection()) return
        const history = messagesFromThread(historyResponse)
        setMessages(history.length ? history : [{ ...WELCOME_MESSAGE, content: thread.preview || 'Bu Codex görevi seçildi. Devam etmek için yeni bir mesaj yazabilirsin.' }])
      }
    } catch (error) {
      if (!isCurrentSelection()) return
      const message = error instanceof Error ? error.message : 'Görev geçmişi yüklenemedi.'
      setMessages([{
        id: 'thread-error',
        role: 'notice',
        content: message,
      }])
      setThreadLoadError({ threadId: thread.id, message })
    } finally {
      if (isCurrentSelection()) setLoadingThreadId(null)
    }
  }

  const createProject = (event: FormEvent) => {
    event.preventDefault()
    const name = projectDraft.name.trim()
    const cwd = projectDraft.cwd.trim().replace(/\/+$/, '')
    if (!name || !isAbsoluteWorkspacePath(cwd)) {
      setProjectError('Enter an absolute local folder path.')
      return
    }
    const roots = bootstrap.health.workspaceRoots ?? []
    const allowed = !roots.length || roots.some((root) => {
      const normalizedRoot = root.replace(/\/+$/, '')
      return cwd === normalizedRoot || cwd.startsWith(`${normalizedRoot}/`)
    })
    if (!allowed) {
      setProjectError(`Bu klasör güvenli çalışma köklerinin dışında. İzinli kök: ${roots.join(', ')}`)
      return
    }
    const project: StudioProject = {
      id: `project-${Date.now()}`,
      name,
      cwd,
      accent: ['#8fd6ff', '#a6e3c4', '#d8b4fe', '#f6c177'][projects.length % 4]!,
      environment: 'local',
      lastOpenedAt: Date.now(),
    }
    const next = [...projects, project]
    setProjects(next)
    saveProjects(next)
    setActiveProjectId(project.id)
    saveActiveProjectId(project.id)
    setProjectDraft({ name: '', cwd: '' })
    setProjectError('')
    setProjectModalOpen(false)
    resetActiveTaskView()
    setMessages([WELCOME_MESSAGE])
    setMemoryUsage(null)
  }

  const chooseProject = (projectId: string) => {
    threadSelectionRef.current += 1
    setLoadingThreadId(null)
    setThreadLoadError(null)
    const next = projects.map((project) => project.id === projectId ? { ...project, lastOpenedAt: Date.now() } : project)
    setProjects(next)
    saveProjects(next)
    setActiveProjectId(projectId)
    saveActiveProjectId(projectId)
    resetActiveTaskView()
    setMessages([WELCOME_MESSAGE])
  }

  const renameProject = (project: StudioProject) => {
    const name = window.prompt('Rename project', project.name)?.trim()
    if (!name || name === project.name) return
    const next = projects.map((item) => item.id === project.id ? { ...item, name } : item)
    setProjects(next)
    saveProjects(next)
  }

  const toggleProjectPinned = (project: StudioProject) => {
    const next = projects.map((item) => item.id === project.id ? { ...item, pinned: !item.pinned } : item)
    setProjects(next)
    saveProjects(next)
  }

  const toggleProjectArchived = (project: StudioProject) => {
    if (project.id === activeProject.id && !project.archived) return
    const next = projects.map((item) => item.id === project.id ? { ...item, archived: !item.archived } : item)
    setProjects(next)
    saveProjects(next)
  }

  const beginNewTask = () => {
    threadSelectionRef.current += 1
    setLoadingThreadId(null)
    setThreadLoadError(null)
    setView('chat')
    resetActiveTaskView()
    setMessages([WELCOME_MESSAGE])
    requestAnimationFrame(() => composerRef.current?.focus())
  }

  const queueTask = (text: string, queuedAttachments: AttachedFile[]) => {
    if (!activeThreadId || !running || !text.trim()) return
    const item: QueuedTask = { id: `queued-${Date.now()}-${Math.random().toString(16).slice(2)}`, text: text.trim(), attachments: queuedAttachments.slice(0, 6), createdAt: Date.now() }
    setQueue((current) => [...current, item].slice(0, 50))
    setComposer('')
    setAttachments([])
    setMessages((current) => [...current, { id: `queue-${item.id}`, role: 'notice', content: `Queued for this task (#${queue.length + 1}). It will start once the current turn finishes.` }])
  }

  const steerTask = async (text: string, steerAttachments: AttachedFile[]) => {
    const identity = activeIdentityRef.current
    if (!identity.threadId || !identity.turnId || !running || !text.trim()) return
    const input: Array<Record<string, unknown>> = [{ type: 'text', text: text.trim() }, ...inputForAttachments(steerAttachments.filter((file) => file.kind === 'image'))]
    try {
      await codexApi.steer(identity.threadId, identity.turnId, input)
      setMessages((current) => [...current, { id: `steer-${Date.now()}`, role: 'user', content: text.trim() }])
      setComposer('')
      setAttachments([])
    } catch (error) {
      setMessages((current) => [...current, { id: `steer-error-${Date.now()}`, role: 'notice', content: error instanceof Error ? error.message : 'The active turn could not be steered.' }])
    }
  }

  const startSideChat = async () => {
    if (!activeThreadId) return
    const parentTitle = taskMetadata[activeThreadId]?.title || 'Current task'
    const snapshot = [
      `Task: ${parentTitle}`,
      `Project: ${activeProject.name}`,
      `Branch: ${taskMetadata[activeThreadId]?.branch || activeProject.branch || settings.defaultBranch}`,
      '',
      ...messages.filter((message) => message.id !== WELCOME_MESSAGE.id && message.role !== 'notice').slice(-8).map((message) => `${message.role === 'user' ? 'User' : 'Code'}: ${sanitizeUserText(message.content, 3_000)}`),
    ].join('\n')
    const boundedSnapshot = snapshot.slice(-12_000)
    try {
      const sideModel = selectedProvider === 'codex' && selectedModel !== 'auto' ? selectedModel : 'gpt-5.6-terra'
      const created = await codexApi.createThread({ cwd: activeProject.cwd, model: sideModel, effort: 'medium', personality: 'friendly', permissionProfile: settings.permissionProfile, fullAccessConfirmed: settings.fullAccessConfirmed, internal: true, internalRole: 'side-chat', parentThreadId: activeThreadId })
      const threadId = created.thread.id
      updateTaskMeta(threadId, { title: 'Side chat', rawUserText: boundedSnapshot, internal: true, internalRole: 'side-chat', parentThreadId: activeThreadId, projectId: activeProject.id, cwd: activeProject.cwd, lastOpenedAt: Date.now() })
      const taskId = await taskIdForThread(threadId)
      setActiveIdentity({ threadId, taskId, executionId: taskId, turnId: null })
      setSideParentThreadId(activeThreadId)
      setRunning(false)
      setStopping(false)
      setWorkflow(null)
      setWorkflowMode(false)
      setSideContext(boundedSnapshot ? `<side_chat_context>\n${boundedSnapshot}\n</side_chat_context>` : null)
      setMessages([{ id: `side-chat-${Date.now()}`, role: 'notice', content: `Side chat opened with a bounded snapshot from the parent task (${activeThreadId}). The parent continues independently.` }])
      setView('chat')
      requestAnimationFrame(() => composerRef.current?.focus())
    } catch (error) {
      setMessages((current) => [...current, { id: `side-chat-error-${Date.now()}`, role: 'notice', content: error instanceof Error ? error.message : 'Side chat could not be opened.' }])
    }
  }

  const commitActiveChanges = async () => {
    if (!activeThreadId) return
    try {
      const snapshot = await codexApi.projectChanges(activeProject.cwd)
      if (!snapshot.repository || !snapshot.changes.length) {
        setMessages((current) => [...current, { id: `commit-empty-${Date.now()}`, role: 'notice', content: 'There are no Git changes to commit in this project.' }])
        return
      }
      const message = window.prompt('Commit message', taskMetadata[activeThreadId]?.title || 'Update project')?.trim()
      if (!message || !window.confirm(`Create commit “${message}” with ${snapshot.changes.length} changed file${snapshot.changes.length === 1 ? '' : 's'}?`)) return
      await codexApi.gitAction({ cwd: activeProject.cwd, action: 'commit', paths: snapshot.changes.map((change) => change.path), message, confirm: true })
      setMessages((current) => [...current, { id: `commit-${Date.now()}`, role: 'notice', content: `Commit created: ${message}` }])
      setView('changes')
    } catch (error) {
      setMessages((current) => [...current, { id: `commit-error-${Date.now()}`, role: 'notice', content: error instanceof Error ? error.message : 'Commit could not be created.' }])
    }
  }

  const returnToParent = async () => {
    const parentId = sideParentThreadId
    if (!parentId) return
    const parent = bootstrap.threads.find((thread) => thread.id === parentId)
    if (parent) {
      await selectThread(parent)
      return
    }
    try {
      await codexApi.resumeThread(parentId, { cwd: activeProject.cwd })
      const taskId = await taskIdForThread(parentId)
      setActiveIdentity({ threadId: parentId, taskId, executionId: taskId, turnId: null })
      setSideParentThreadId(null)
      setView('chat')
      setMessages([{ id: `parent-return-${Date.now()}`, role: 'notice', content: 'Returned to the parent task.' }])
    } catch (error) {
      setMessages((current) => [...current, { id: `parent-return-error-${Date.now()}`, role: 'notice', content: error instanceof Error ? error.message : 'The parent task could not be reopened.' }])
    }
  }

  const submitTask = async (event?: FormEvent, attachments: AttachedFile[] = []) => {
    event?.preventDefault()
    const rawText = composer.trim()
    const commandMatch = rawText.match(/^\/(plan|chat|review|clear)\b\s*(.*)$/i)
    const command = commandMatch?.[1]?.toLowerCase()
    if (command === 'clear') { setComposer(''); setMessages([WELCOME_MESSAGE]); return }
    const text = commandMatch?.[2]?.trim() || rawText
    const effectiveWorkflowMode = command === 'plan' ? true : command === 'chat' ? false : workflowMode
    const effectiveTeamMode = command === 'chat' ? false : teamMode
    if (command === 'plan') setWorkflowMode(true)
    if (command === 'chat') { setWorkflowMode(false); setTeamMode(false); setTeamConversationId(null) }
    // Team conversations are intentionally allowed while a workflow lane is
    // active. The server gives the user a read-only consultation lane instead
    // of interrupting an active write lane.
    if (!text || (running && !(teamMode && workflow))) return
    setComposer('')
    setMemoryUsage(null)
    const attachmentContext = attachments.length
      ? `\n\nAttached files:\n${attachments.map((file) => file.kind === 'text' ? `--- ${file.name} ---\n${file.content ?? ''}` : `--- ${file.name} (image attached) ---`).join('\n\n')}`
      : ''
    const taskText = `${sideContext ? `${sideContext}\n\n` : ''}${text}${attachmentContext}`
    setSideContext(null)
    setMessages((current) => [
      ...(current.length === 1 && current[0]?.id === WELCOME_MESSAGE.id ? [] : current),
      { id: `user-${Date.now()}`, role: 'user', content: text + (attachments.length ? `\n\nAttached: ${attachments.map((file) => file.name).join(', ')}` : '') },
    ])
    if (connection !== 'ready' && selectedProvider === 'codex') {
      setMessages((current) => [...current, {
        id: `offline-${Date.now()}`,
        role: 'notice',
        content: 'Codex motoru şu anda bağlı değil. Yerel App Server başlatıldığında bu görev gerçek Codex oturumuna gönderilecek.',
      }])
      return
    }

    if (selectedProvider !== 'codex') {
      const provider = providers.find((item) => item.id === selectedProvider)
      const state = provider?.status.state
      if (state !== 'ready') {
        const explanation = state
          ? `${PROVIDER_LABELS[selectedProvider] || selectedProvider} is ${PROVIDER_STATE_LABELS[state] || state}. Refresh Settings and complete the official provider setup before selecting it.`
          : `${PROVIDER_LABELS[selectedProvider] || selectedProvider} is not available yet. Refresh Settings and complete the official provider setup before selecting it.`
        setMessages((current) => [...current, { id: `provider-${Date.now()}`, role: 'notice', content: explanation }])
        return
      }
    }

    const mentionedProfiles = effectiveTeamMode
      ? Array.from(text.matchAll(/(?:^|\s)@([a-z0-9_-]+)/gi)).map((match) => match[1]!.toLowerCase()).filter((id) => TEAM_COMPOSER_PROFILES.some((agent) => agent.id === id))
      : []
    if (effectiveTeamMode && workflow) {
      try {
        const response = await codexApi.teamMessage(workflow.id, {
          message: taskText,
          ...(mentionedProfiles[0] ? { agentId: mentionedProfiles[0] } : {}),
        })
        setWorkflowGraph(response.graph)
        setAttachments([])
      } catch (error) {
        setMessages((current) => [...current, { id: `team-message-error-${Date.now()}`, role: 'notice', content: error instanceof Error ? error.message : 'Team message could not be delivered.' }])
      }
      return
    }
    if (effectiveTeamMode && classifyTeamRequest(text) === 'conversation') {
      setRunning(true)
      try {
        let model = selectedModel
        let effort: string = selectedReasoningEffort
        if (selectedProvider === 'codex' && selectedModel === 'auto') {
          const decision = await codexApi.route(text)
          setRouteDecision(decision)
          model = decision.selectedModel
          effort = decision.effort
        }
        const response = await codexApi.startTeamConversation({
          cwd: activeProject.cwd,
          task: taskText,
          selection: { providerId: selectedProvider, model, effort, timeoutMs: 10 * 60_000 },
          ...(mentionedProfiles[0] ? { agentId: mentionedProfiles[0] } : {}),
          ...(teamConversationId ? { conversationId: teamConversationId } : {}),
        })
        setTeamConversationId(response.conversationId)
        setWorkflowGraph(response.graph)
        setMessages((current) => [...current, {
          id: response.message.id,
          role: 'assistant',
          content: response.message.text || 'Adrian yanıt hazırlayamadı.',
        }])
        setAttachments([])
      } catch (error) {
        setMessages((current) => [...current, { id: `team-conversation-error-${Date.now()}`, role: 'notice', content: error instanceof Error ? error.message : 'Team conversation could not be completed.' }])
      } finally {
        setRunning(false)
        setStopping(false)
      }
      return
    }
    const mentionedPaths = Array.from(text.matchAll(/(?:^|\s)@([^\s`]+)/g)).map((match) => match[1]!.replace(/[),.;:]+$/, '')).filter((path) => !mentionedProfiles.includes(path.toLowerCase())).slice(0, 4)
    const mentionContext = mentionedPaths.length
      ? (await Promise.all(mentionedPaths.map(async (path) => {
        try { const file = await codexApi.projectFile(activeProject.cwd, path); return `\n\n<file_context path="${path}">\n${file.content.slice(0, 120_000)}\n</file_context>` } catch { return `\n\n<file_context path="${path}" unavailable />` }
      }))).join('')
      : ''
    const taskWithMentions = `${taskText}${mentionContext}`

    setRunning(true)
    try {
      const teamRequestMode = effectiveTeamMode ? classifyTeamRequest(text) : 'conversation'
      if (effectiveWorkflowMode || (effectiveTeamMode && teamRequestMode === 'project-task')) {
        let teamSelection: ProviderSelection | null = null
        if (effectiveTeamMode && !effectiveWorkflowMode) {
          let model = selectedModel
          let effort: string = selectedReasoningEffort
          if (selectedProvider === 'codex' && selectedModel === 'auto') {
            const decision = await codexApi.route(text)
            setRouteDecision(decision)
            model = decision.selectedModel
            effort = decision.effort
          }
          teamSelection = {
            providerId: selectedProvider,
            model,
            effort,
            timeoutMs: 10 * 60_000,
          }
        }
        const supervisor = teamSelection ? { ...teamSelection } : workflowSelection(workflowPlanModel, 'gpt-5.6-sol', workflowPlanEffort)
        const worker = teamSelection ? { ...teamSelection } : workflowSelection(workflowBuildModel, 'gpt-5.6-luna', workflowBuildEffort)
        const reviewer = { ...supervisor }
        const unavailableSelection = [supervisor, worker].find((selection) => {
          if (selection.providerId === 'codex') return false
          return providers.find((provider) => provider.id === selection.providerId)?.status.state !== 'ready'
        })
        if (unavailableSelection) {
          const provider = providers.find((item) => item.id === unavailableSelection.providerId)
          setRunning(false)
          setMessages((current) => [...current, {
            id: `workflow-provider-${Date.now()}`,
            role: 'notice',
            content: `${provider?.displayName || unavailableSelection.providerId} is not ready. Complete its official setup in Settings before using it for Plan → build.`,
          }])
          return
        }
        setWorkflowPlanning(true)
        setTeamConversationId(null)
        const created = await codexApi.createWorkflow({ cwd: activeProject.cwd, task: taskWithMentions, supervisor, worker, reviewer, teamMode: effectiveTeamMode })
        setWorkflowPlanning(created.workflow.state === 'draft' || created.workflow.state === 'replanning')
        setRunning(workflowIsActive(created.workflow.state))
        setWorkflow(created.workflow)
        const workflowTaskId = await taskIdForWorkflow(created.workflow.id)
        setActiveIdentity({ threadId: null, taskId: workflowTaskId, executionId: `workflow:${created.workflow.id}`, turnId: null })
        const workflowRuntime = taskRuntimeRegistry.get(workflowTaskId)
        await workflowRuntime.replay(async (url) => {
          const query = new URL(url, window.location.origin).searchParams
          return codexApi.taskEvents(workflowTaskId, Number(query.get('after') ?? -1), Number(query.get('limit') ?? 500))
        }).catch(() => workflowRuntime.state)
        setTaskRuntimeRevision((revision) => revision + 1)
        setAttachments([])
        return
      }
      const intent = classifyTaskIntent(text)
      const shouldUseMemory = settings.memoryContext && shouldRetrieveMemory(text, intent)
      const retrievedMemory = shouldUseMemory
        ? await codexApi.memoryContext(activeProject.cwd, text).catch(() => null)
        : null
      const boundedMemory = retrievedMemory?.context
        ? boundMemoryContext(retrievedMemory.context, retrievedMemory.approximateTokens)
        : null
      setMemoryUsage(retrievedMemory?.sources?.length ? {
        sources: retrievedMemory.sources,
        approximateTokens: boundedMemory?.approximateTokens ?? retrievedMemory.approximateTokens,
        indexedAt: retrievedMemory.indexedAt,
        truncated: boundedMemory?.truncated,
        budget: MEMORY_CONTEXT_TOKEN_BUDGET,
      } : null)
      let decision: RouteDecision | null = null
      if (selectedModel === 'auto') {
        // Route exactly once from the user's intent. Attachments, side-chat snapshots,
        // and file previews are context, not signals that should inflate model choice.
        decision = await codexApi.route(text)
        setRouteDecision(decision)
      } else {
        setRouteDecision(null)
      }
      const enrichedText = boundedMemory?.context
        ? `${taskWithMentions}\n\n<memory_palace>\n${boundedMemory.context}\n</memory_palace>`
        : taskWithMentions

      if (selectedProvider !== 'codex') {
        const providerResult = await codexApi.runProviderTurn({
          providerId: selectedProvider,
          model: selectedModel,
          effort: selectedReasoningEffort,
          cwd: activeProject.cwd,
          prompt: enrichedText,
        })
        setMessages((current) => [...current, {
          id: `provider-${Date.now()}`,
          role: providerResult.result.status === 'completed' ? 'assistant' : 'notice',
          content: providerResult.result.text || `${PROVIDER_LABELS[selectedProvider] || selectedProvider} returned no text.`,
        }])
        setRunning(false)
        setStopping(false)
        setAttachments([])
        return
      }

      const concreteModel = decision?.selectedModel || (selectedModel === 'auto'
        ? models.find((model) => model.id.toLocaleLowerCase().includes('luna'))?.id || FALLBACK_MODELS[0]!.id
        : selectedModel)
      const effort = decision?.effort || selectedReasoningEffort
      let threadId = activeThreadId
      const isNewThread = !threadId
      if (!threadId) {
        const created = await codexApi.createThread({
          cwd: activeProject.cwd,
          model: concreteModel,
          effort,
          personality: 'friendly',
          permissionProfile: settings.permissionProfile,
          fullAccessConfirmed: settings.fullAccessConfirmed,
        })
        threadId = created.thread.id
      }
      updateTaskMeta(threadId, { title: userTaskTitle(text), rawUserText: text, projectId: activeProject.id, cwd: activeProject.cwd, unread: false, lastOpenedAt: Date.now(), environment: 'local', internal: false })
      const taskId = await taskIdForThread(threadId)
      setActiveIdentity({ threadId, taskId, executionId: taskId, turnId: null })
      const turn = await codexApi.startTurn(threadId, {
        text: enrichedText,
        input: [{ type: 'text', text: enrichedText }, ...inputForAttachments(attachments.filter((file) => file.kind === 'image'))],
        // The router already ran above; never ask the App Server to classify this
        // same turn a second time.
        model: concreteModel,
        effort,
        summary: 'detailed',
        cwd: activeProject.cwd,
        ...(isNewThread ? { permissionProfile: settings.permissionProfile, fullAccessConfirmed: settings.fullAccessConfirmed } : {}),
      })
      if (turn.turn?.id) {
        activeIdentityRef.current.turnId = turn.turn.id
        setActiveTurnId(turn.turn.id)
      }
      const runtime = taskRuntimeRegistry.get(taskId)
      await runtime.replay(async (url) => {
        const query = new URL(url, window.location.origin).searchParams
        return codexApi.taskEvents(taskId, Number(query.get('after') ?? -1), Number(query.get('limit') ?? 500))
      }).catch(() => runtime.state)
      if (activeIdentityRef.current.taskId === taskId) {
        setTaskRuntimeRevision((revision) => revision + 1)
        setRunning(runtime.state.events.length > 0 ? runtime.state.running : true)
        setStopping(false)
        setActiveTurnId(runtime.state.activeTurnId ?? activeIdentityRef.current.turnId)
        activeIdentityRef.current.turnId = runtime.state.activeTurnId ?? activeIdentityRef.current.turnId
        if (runtime.state.messages.length) {
          setMessages((current) => [
            ...current.filter((message) => message.role !== 'assistant' || !message.streaming),
            ...runtime.state.messages.map((message) => ({ id: message.id, role: message.role, content: message.content, streaming: message.streaming })),
          ])
        }
      }
      if (turn.routing) setRouteDecision(turn.routing)
      setAttachments([])
    } catch (error) {
      setWorkflowPlanning(false)
      setRunning(false)
      setMessages((current) => [...current, {
        id: `error-${Date.now()}`,
        role: 'notice',
        content: error instanceof Error ? error.message : 'Görev başlatılamadı.',
      }])
    }
  }

  const approveWorkflow = async () => {
    if (!workflow || running) return
    setRunning(true)
    try {
      const next = (await codexApi.approveWorkflow(workflow.id)).workflow
      setWorkflow(next)
      // Approval starts the background runner. Keep the composer in its real
      // working state until a canonical workflow terminal event arrives.
      setRunning(workflowIsActive(next.state))
    } catch (error) {
      setRunning(false)
      setMessages((items) => [...items, { id: `workflow-error-${Date.now()}`, role: 'notice', content: error instanceof Error ? error.message : 'Workflow başlatılamadı.' }])
    }
  }

  const pauseWorkflow = async () => {
    if (!workflow) return
    try {
      const next = (await codexApi.pauseWorkflow(workflow.id, 'paused_by_user')).workflow
      setWorkflow(next)
      setRunning(workflowIsActive(next.state))
    } catch (error) {
      setMessages((items) => [...items, { id: `workflow-pause-error-${Date.now()}`, role: 'notice', content: error instanceof Error ? error.message : 'Workflow could not be paused.' }])
    }
  }

  const resumeWorkflow = async () => {
    if (!workflow) return
    setRunning(true)
    try {
      const next = (await codexApi.resumeWorkflow(workflow.id)).workflow
      setWorkflow(next)
      setRunning(workflowIsActive(next.state))
    } catch (error) {
      setRunning(false)
      setMessages((items) => [...items, { id: `workflow-resume-error-${Date.now()}`, role: 'notice', content: error instanceof Error ? error.message : 'Workflow could not be resumed.' }])
    }
  }

  const requestPlanChanges = async (instruction: string) => {
    if (!workflow) return
    setWorkflowPlanning(true)
    try {
      const next = (await codexApi.reviseWorkflow(workflow.id, instruction)).workflow
      setWorkflow(next)
      setMessages((items) => [...items, { id: `workflow-revised-${Date.now()}`, role: 'notice', content: 'Plan revised against the project and your instruction.' }])
    } catch (error) {
      setMessages((items) => [...items, { id: `workflow-revise-error-${Date.now()}`, role: 'notice', content: error instanceof Error ? error.message : 'The plan could not be revised.' }])
    } finally {
      setWorkflowPlanning(false)
    }
  }

  const messageWorkflowAgent = async (message: string, agentId?: string) => {
    if (!workflow) return
    try {
      const response = await codexApi.messageWorkflowAgent(workflow.id, message, agentId)
      setWorkflowGraph(response.graph)
    } catch (error) {
      setMessages((items) => [...items, { id: `agent-message-error-${Date.now()}`, role: 'notice', content: error instanceof Error ? error.message : 'Agent message could not be delivered.' }])
    }
  }

  const cancelWorkflow = async () => {
    if (!workflow) return
    try {
      const next = (await codexApi.cancelWorkflow(workflow.id, 'cancelled_by_user')).workflow
      setWorkflow(next)
      setRunning(workflowIsActive(next.state))
    } catch { /* The status remains visible. */ }
  }

  const startLogin = async () => {
    setLoginPending(true)
    try {
      const result = await codexApi.startChatGptLogin('browser')
      if (result.authUrl) window.open(result.authUrl, '_blank', 'noopener,noreferrer')
      else if (result.verificationUrl) window.open(result.verificationUrl, '_blank', 'noopener,noreferrer')
      window.setTimeout(refresh, 1800)
    } catch (error) {
      setMessages((current) => [...current, {
        id: `login-${Date.now()}`,
        role: 'notice',
        content: error instanceof Error ? error.message : 'ChatGPT girişi başlatılamadı.',
      }])
    } finally {
      setLoginPending(false)
    }
  }

  const interrupt = async () => {
    const active = activeIdentityRef.current
    if (stopping) return
    if (!active.threadId && !active.executionId) {
      // Workflows can be running before a regular thread/execution identity is
      // assigned. Treat Stop as a real workflow cancellation instead of
      // leaving the composer in a permanent stopping state.
      if (!workflow?.id) return
      setStopping(true)
      try {
        setWorkflow((await codexApi.cancelWorkflow(workflow.id, 'cancelled_by_user')).workflow)
        setRunning(false)
      } catch (error) {
        setMessages((current) => [...current, { id: `cancel-workflow-error-${Date.now()}`, role: 'notice', content: error instanceof Error ? error.message : 'The workflow could not be stopped.' }])
      } finally {
        setStopping(false)
      }
      return
    }
    setStopping(true)
    try {
      const clearActiveRun = () => {
        const identityMatches = active.executionId
          ? activeIdentityRef.current.executionId === active.executionId
          : activeIdentityRef.current.threadId === active.threadId
        if (!identityMatches) {
          setStopping(false)
          return
        }
        activeIdentityRef.current.turnId = null
        setRunning(false)
        setStopping(false)
        setActiveTurnId(null)
      }
      if (active.executionId) {
        const result = await codexApi.cancelExecution(active.executionId)
        if (result.execution.state === 'failed_to_cancel') throw new Error(result.execution.error || 'The task could not be stopped.')
        if (isTerminalExecutionState(result.execution.state)) {
          clearActiveRun()
          return
        }
        for (let attempt = 0; attempt < 20; attempt += 1) {
          await new Promise((resolve) => window.setTimeout(resolve, 250))
          if (activeIdentityRef.current.executionId !== active.executionId) return
          const snapshot = await codexApi.executions()
          const execution = snapshot.executions.find((item) => item.id === active.executionId)
          if (execution && isTerminalExecutionState(execution.state)) {
            clearActiveRun()
            return
          }
        }
        if (activeIdentityRef.current.executionId === active.executionId) {
          setStopping(false)
          setMessages((current) => [...current, { id: `cancel-pending-${Date.now()}`, role: 'notice', content: 'Stop is still pending. The task remains running; try again shortly.' }])
        }
      } else if (active.threadId && active.turnId) {
        await codexApi.interrupt(active.threadId, active.turnId)
        clearActiveRun()
      } else {
        // A stale event can leave a thread id behind for one render while its
        // turn id has already completed. Never leave the Stop control stuck.
        clearActiveRun()
      }
    } catch (error) {
      setStopping(false)
      setMessages((current) => [...current, { id: `cancel-error-${Date.now()}`, role: 'notice', content: error instanceof Error ? error.message : 'The task could not be stopped.' }])
    }
  }

  const resolveApproval = async (decision: 'accept' | 'acceptForSession' | 'decline' | 'cancel') => {
    if (!approvalRequest || approvalPending) return
    const current = approvalRequest
    setApprovalPending(true)
    setApprovalError('')
    try {
      if (decision === 'cancel') {
        await codexApi.respondRequest(current.requestId, {}, { code: -32800, message: 'Cancelled by user' })
      } else {
        const params = current.params
        let result: Record<string, unknown>
        if (current.method === 'item/permissions/requestApproval') {
          result = { permissions: decision === 'decline' ? {} : (params.permissions ?? {}), scope: decision === 'acceptForSession' ? 'session' : 'turn' }
        } else if (current.method === 'mcpServer/elicitation/request') {
          result = { action: decision === 'decline' ? 'decline' : 'accept', content: null, _meta: null }
        } else if (current.method === 'item/tool/requestUserInput') {
          result = { answers: {} }
        } else {
          result = { decision }
        }
        await codexApi.respondRequest(current.requestId, result)
      }
      setApprovalRequest(null)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Onay yanıtı gönderilemedi.'
      setApprovalError(message)
      setMessages((items) => [...items, { id: `approval-${Date.now()}`, role: 'notice', content: message }])
    } finally {
      setApprovalPending(false)
    }
  }

  const workspaceCommands = useMemo<WorkspaceCommand[]>(() => [
    { id: 'new-chat', label: 'New chat', description: 'Start a clean task in the current project.', shortcut: '⌘N', onExecute: beginNewTask },
    { id: 'chat', label: 'Open Chat', description: 'Return to the active task conversation.', shortcut: '⌘1', onExecute: () => setView('chat') },
    { id: 'files', label: 'Open Files', description: 'Browse the current project files.', shortcut: '⌘2', onExecute: () => setView('files') },
    { id: 'changes', label: 'Open Changes', description: 'Review the current Git changes.', shortcut: '⌘3', onExecute: () => setView('changes') },
    { id: 'terminal', label: 'Open Terminal', description: 'Inspect bounded terminal sessions.', shortcut: '⌘4', onExecute: () => setView('terminal') },
    { id: 'memory', label: 'Open Memory', description: 'Search relevant project memory without rereading the repository.', shortcut: '⌘5', onExecute: () => setView('memory') },
    { id: 'tetris', label: 'Play Tetris', description: 'Open the power-enabled cyberpunk Tetris game.', shortcut: '⌘6', onExecute: () => setView('tetris') },
    { id: 'settings', label: 'Open Settings', description: 'Change appearance, permissions, providers, and runtime preferences.', shortcut: '⌘,', onExecute: () => setView('settings') },
    { id: 'stop', label: 'Stop active task', description: 'Request cancellation for the active task or workflow.', shortcut: '⌘.', disabled: !running, onExecute: () => void interrupt() },
  ], [beginNewTask, interrupt, running])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const modifier = event.metaKey || event.ctrlKey
      if (!modifier) return
      const key = event.key.toLowerCase()
      if (key === 'k') {
        event.preventDefault()
        setCommandPaletteOpen(true)
        return
      }
      if (commandPaletteOpen) return
      if (key === 'n') { event.preventDefault(); beginNewTask(); return }
      if (key === ',') { event.preventDefault(); setView('settings'); return }
      if (key === '.') { event.preventDefault(); void interrupt(); return }
      if (['1', '2', '3', '4', '5', '6'].includes(key) && !(event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement)) {
        event.preventDefault()
        const next: View = ({ '1': 'chat', '2': 'files', '3': 'changes', '4': 'terminal', '5': 'memory', '6': 'tetris' } as Record<string, View>)[key]!
        setView(next)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [beginNewTask, commandPaletteOpen, interrupt])

  return (
    <div className={`studio-shell theme-${settings.theme} density-${settings.density} ${settings.reduceMotion ? 'reduce-motion' : ''}`} data-event-transport={eventTransport}>
      <a className="skip-link" href="#studio-main">Ana çalışma alanına geç</a>
      <header className="studio-topbar">
        <div className="studio-brand" aria-label="Code">
          <span className="studio-mark" aria-hidden="true"><span /></span>
          <span className="studio-wordmark">CODE</span>
          <span className="studio-edition">WORKSPACE</span>
        </div>

        <button className="project-switcher" type="button" onClick={() => setProjectModalOpen(true)} aria-label="Projeler">
          <span className="project-avatar" style={{ '--project-accent': activeProject.accent } as React.CSSProperties}>
            {activeProject.name.slice(0, 1).toUpperCase()}
          </span>
          <span className="project-switcher-copy">
            <strong>{activeProject.name}</strong>
            <small>{activeProject.cwd}</small>
          </span>
          <CaretDownIcon size={13} aria-hidden="true" />
        </button>

        <nav className="studio-view-tabs" aria-label="Çalışma görünümleri">
          <ViewTab active={view === 'chat'} onClick={() => setView('chat')} icon={<ChatCircleDotsIcon />} label="Chat" />
          <ViewTab active={view === 'files'} onClick={() => setView('files')} icon={<FolderOpenIcon />} label="Files" />
          <ViewTab active={view === 'changes'} onClick={() => setView('changes')} icon={<GitBranchIcon />} label="Changes" />
          <ViewTab active={view === 'terminal'} onClick={() => setView('terminal')} icon={<TerminalWindowIcon />} label="Terminal" />
          <ViewTab active={view === 'evidence'} onClick={() => setView('evidence')} icon={<FileCodeIcon />} label="Evidence" />
          <ViewTab active={view === 'browser'} onClick={() => setView('browser')} icon={<GlobeHemisphereWestIcon />} label="Browser" />
          <ViewTab active={view === 'tetris'} onClick={() => setView('tetris')} icon={<GameControllerIcon />} label="Tetris" />
        </nav>

        <div className="studio-top-actions">
          <AppModelPicker models={models} providerModels={providerModels} selectedProvider={selectedProvider} selectedModel={selectedModel} selectedEffort={selectedReasoningEffort} onChange={(value) => {
            if (value === 'auto') { setSelectedProvider('codex'); setSelectedModel('auto'); return }
            const separator = value.indexOf(':')
            setSelectedProvider(separator > 0 ? value.slice(0, separator) : 'codex')
            setSelectedModel(separator > 0 ? value.slice(separator + 1) : value)
          }} onEffortChange={setSelectedReasoningEffort} compact />
          <button
            className={`account-chip is-${connection}`}
            type="button"
            onClick={!signedIn && connection === 'ready' ? startLogin : refresh}
            disabled={loginPending}
            title={signedIn ? 'ChatGPT hesabı bağlı' : 'ChatGPT ile bağlan'}
          >
            <span className="account-avatar">{initials(accountEmail)}</span>
            <span className="account-copy">
              <strong>{signedIn ? 'ChatGPT bağlı' : connection === 'checking' ? 'Kontrol ediliyor' : 'Bağlantı yok'}</strong>
              <small>{quotaReached ? 'kota şu an dolu' : planType ? `${planType} planı` : connection === 'ready' ? 'Giriş gerekli' : 'Yerel motor'}</small>
            </span>
          </button>
          <button className="compact-new-chat" type="button" onClick={beginNewTask} aria-label="New chat" title="New chat"><PlusIcon size={17} weight="bold" /></button>
        </div>
      </header>

      <div className="studio-body">
        <aside className="activity-rail" aria-label="Ana araçlar">
          <div className="rail-primary">
            <RailButton active={view === 'chat'} label="Chats" onClick={() => setView('chat')}><ChatCircleDotsIcon /></RailButton>
            <RailButton active={view === 'files'} label="Files" onClick={() => setView('files')}><FolderOpenIcon /></RailButton>
            <RailButton active={view === 'changes'} label="Changes" onClick={() => setView('changes')}><GitBranchIcon /></RailButton>
            <RailButton active={view === 'terminal'} label="Terminal" onClick={() => setView('terminal')}><TerminalWindowIcon /></RailButton>
            <RailButton active={view === 'evidence'} label="Sources and artifacts" onClick={() => setView('evidence')}><FileCodeIcon /></RailButton>
            <RailButton active={view === 'browser'} label="Browser" onClick={() => setView('browser')}><GlobeHemisphereWestIcon /></RailButton>
            <RailButton active={view === 'tetris'} label="Tetris" onClick={() => setView('tetris')}><GameControllerIcon /></RailButton>
            <RailButton active={view === 'extensions'} label="Skills and plugins" onClick={() => setView('extensions')}><PuzzlePieceIcon /></RailButton>
          </div>
          <div className="rail-secondary">
            <RailButton active={view === 'settings'} label="Settings" onClick={() => setView('settings')}><GearSixIcon /></RailButton>
          </div>
        </aside>

        <aside className="task-sidebar" aria-label="Proje ve görevler">
          <div className="sidebar-project-heading">
            <div>
              <span>WORKSPACE</span>
              <strong>{activeProject.name}</strong>
            </div>
            <button className="compact-icon-button" type="button" onClick={() => setProjectModalOpen(true)} aria-label="Yeni proje ekle"><PlusIcon /></button>
          </div>
          <button className="new-task-button" type="button" onClick={beginNewTask}>
            <PlusIcon size={15} weight="bold" />
            New chat
            <kbd>⌘ N</kbd>
          </button>
          <label className="sidebar-search">
            <MagnifyingGlassIcon size={14} />
            <span className="sr-only">Search chats</span>
            <input value={sidebarSearch} onChange={(event) => setSidebarSearch(event.target.value)} placeholder="Search chats" />
          </label>

          <div className="thread-section">
            <div className="thread-section-label"><span>{showArchivedTasks ? 'ARCHIVED' : showInternalTasks ? 'ALL TASKS' : 'RECENT'}</span><div className="thread-section-actions"><button className="thread-section-toggle" type="button" onClick={() => setShowArchivedTasks((value) => !value)}>{showArchivedTasks ? 'Show recent' : 'Archived'}</button><button className="thread-section-toggle" type="button" onClick={() => setShowInternalTasks((value) => !value)}>{showInternalTasks ? 'Hide internal' : 'Internal'}</button></div></div>
            <div className="thread-list">
              {filteredThreads.length ? filteredThreads.map((thread) => (
                <div className="thread-row" key={thread.id}>
                  <button
                    className={`thread-item ${activeThreadId === thread.id ? 'is-active' : ''} ${taskMetadata[thread.id]?.unread ? 'is-unread' : ''}`}
                    type="button"
                    onClick={() => void selectThread(thread)}
                    onDoubleClick={() => renameTask(thread)}
                    title="Double-click to rename"
                  >
                    <span className="thread-status" />
                    <span className="thread-copy"><strong>{taskMetadata[thread.id]?.pinned ? '· ' : ''}{safeThreadTitle(thread, taskMetadata[thread.id])}</strong><small>{thread.preview || thread.cwd || taskMetadata[thread.id]?.branch || activeProject.cwd}</small></span>
                    <time>{formatThreadTime(thread)}</time>
                  </button>
                  <div className="thread-row-actions" aria-label={`${safeThreadTitle(thread, taskMetadata[thread.id])} actions`}>
                    <button type="button" onClick={() => toggleTaskPinned(thread)} aria-label={taskMetadata[thread.id]?.pinned ? 'Unpin task' : 'Pin task'}>{taskMetadata[thread.id]?.pinned ? '★' : '☆'}</button>
                    <button type="button" onClick={() => renameTask(thread)} aria-label="Rename task">···</button>
                    <button type="button" onClick={() => toggleTaskArchived(thread)} aria-label={taskMetadata[thread.id]?.archived ? 'Restore task' : 'Archive task'}>{taskMetadata[thread.id]?.archived ? '↩' : '×'}</button>
                  </div>
                </div>
              )) : (
                <div className="thread-empty">
                  <ClockCounterClockwiseIcon size={20} />
                  <span>{showArchivedTasks ? 'No archived chats.' : connection === 'ready' ? 'No chats in this project yet.' : 'Chats will appear when Codex is connected.'}</span>
                </div>
              )}
            </div>
          </div>

          {remoteChats.length > 0 && <div className="thread-section remote-chat-section">
            <div className="thread-section-label"><span>SHARED CODE</span><span>{remoteChats.length}</span></div>
            <div className="thread-list">
              {remoteChats.slice(0, 12).map((chat) => <button className="thread-item" type="button" key={`remote-${chat.id}`} onClick={() => void selectRemoteChat(chat)}>
                <span className="thread-status is-remote" />
                <span className="thread-copy"><strong>{chat.title}</strong><small>{chat.preview || 'ChatGPT conversation'}</small></span>
                <time>sync</time>
              </button>)}
            </div>
          </div>}

          <nav className="sidebar-tools" aria-label="Workspace tools">
            <button type="button" className={view === 'files' ? 'is-active' : ''} onClick={() => setView('files')}><FolderOpenIcon /> Files</button>
            <button type="button" className={view === 'changes' ? 'is-active' : ''} onClick={() => setView('changes')}><GitBranchIcon /> Changes</button>
            <button type="button" className={view === 'terminal' ? 'is-active' : ''} onClick={() => setView('terminal')}><TerminalWindowIcon /> Terminal</button>
            <button type="button" className={view === 'browser' ? 'is-active' : ''} onClick={() => setView('browser')}><GlobeHemisphereWestIcon /> Browser</button>
            <button type="button" className={view === 'extensions' ? 'is-active' : ''} onClick={() => setView('extensions')}><PuzzlePieceIcon /> Skills & apps</button>
            <button type="button" className="sidebar-remote-button" onClick={() => { setView('chat'); setRemoteRequestNonce((value) => value + 1) }}><ArrowUpRightIcon /> Remote</button>
          </nav>
          <button className={`sidebar-settings ${view === 'settings' ? 'is-active' : ''}`} type="button" onClick={() => setView('settings')}>
            <GearSixIcon />
            <span><strong>Settings</strong><small>{signedIn ? planType ? `${planType} plan` : 'ChatGPT connected' : 'Local workspace'}</small></span>
          </button>

        </aside>

        <main id="studio-main" className={`studio-main view-${view}`}>
          {view === 'chat' && (
            <ChatView
              project={activeProject}
              messages={messages}
              activityState={activeTaskState}
              plan={workflowPlanning ? undefined : workflow ? planFromWorkflow(workflow, workflowGraph) : activeTaskState?.plan}
              attachments={attachments}
              queuedItems={queue}
              composer={composer}
              running={running}
              stopping={stopping}
              connection={connection}
              activeThreadId={activeThreadId}
              taskTitle={activeThreadId ? taskMetadata[activeThreadId]?.title || 'Current task' : 'New task'}
              taskBranch={activeThreadId ? taskMetadata[activeThreadId]?.branch || activeProject.branch || settings.defaultBranch : activeProject.branch || settings.defaultBranch}
              sideParentThreadId={sideParentThreadId}
              quotaUsed={quotaUsed}
              memoryUsage={memoryUsage}
              models={models}
              providerModels={providerModels}
              selectedProvider={selectedProvider}
              selectedModel={selectedModel}
              selectedReasoningEffort={selectedReasoningEffort}
              workflowMode={workflowMode}
              teamMode={teamMode}
              workflowPlanning={workflowPlanning}
              workflowModelOptions={workflowModelCatalog}
              workflowPlanModel={workflowPlanModel}
              workflowBuildModel={workflowBuildModel}
              workflowPlanEffort={workflowPlanEffort}
              workflowBuildEffort={workflowBuildEffort}
              workflow={workflow}
              workflowGraph={workflowGraph}
              remoteOperations={remoteOperations}
              remoteRequestNonce={remoteRequestNonce}
              onRemoteRequestHandled={() => setRemoteRequestNonce(0)}
              composerRef={composerRef}
              messagesEndRef={messagesEndRef}
              onComposerChange={setComposer}
              onModelChange={(value) => {
                if (value === 'auto') { setSelectedProvider('codex'); setSelectedModel('auto'); return }
                const separator = value.indexOf(':')
                setSelectedProvider(separator > 0 ? value.slice(0, separator) : 'codex')
                setSelectedModel(separator > 0 ? value.slice(separator + 1) : value)
              }}
              onReasoningEffortChange={setSelectedReasoningEffort}
              onToggleWorkflow={() => setWorkflowMode((value) => !value)}
              onToggleTeam={() => setTeamMode((value) => {
                if (value) setTeamConversationId(null)
                return !value
              })}
              onWorkflowPlanModelChange={(value) => {
                const effort = resolveWorkflowEffort(value, workflowPlanEffort, 'high')
                setWorkflowPlanModel(value)
                setWorkflowPlanEffort(effort)
                updateSettings({ workflowPlanModel: value, workflowPlanEffort: effort })
              }}
              onWorkflowBuildModelChange={(value) => {
                const effort = resolveWorkflowEffort(value, workflowBuildEffort, 'low')
                setWorkflowBuildModel(value)
                setWorkflowBuildEffort(effort)
                updateSettings({ workflowBuildModel: value, workflowBuildEffort: effort })
              }}
              onWorkflowPlanEffortChange={(value) => {
                const effort = resolveWorkflowEffort(workflowPlanModel, value, 'high')
                setWorkflowPlanEffort(effort)
                updateSettings({ workflowPlanEffort: effort })
              }}
              onWorkflowBuildEffortChange={(value) => {
                const effort = resolveWorkflowEffort(workflowBuildModel, value, 'low')
                setWorkflowBuildEffort(effort)
                updateSettings({ workflowBuildEffort: effort })
              }}
              onApproveWorkflow={() => void approveWorkflow()}
              onPauseWorkflow={() => void pauseWorkflow()}
              onResumeWorkflow={() => void resumeWorkflow()}
              onRequestPlanChanges={requestPlanChanges}
              onAgentMessage={messageWorkflowAgent}
              onQueue={(text, files) => queueTask(text, files)}
              onSteer={(text, files) => void steerTask(text, files)}
              onSideChat={() => void startSideChat()}
              onHandOff={() => void startSideChat()}
              onReturnToParent={() => void returnToParent()}
              onOpenFiles={() => setView('files')}
              onOpenChanges={() => setView('changes')}
              onCommit={() => void commitActiveChanges()}
              onRemoveAttachment={(id) => setAttachments((current) => current.filter((file) => file.id !== id))}
              onAddAttachments={(files) => setAttachments((current) => [...current, ...files].slice(0, 6))}
              onRemoveQueued={(id) => setQueue((current) => current.filter((item) => item.id !== id))}
              onCancelWorkflow={() => void cancelWorkflow()}
              onDecideRemoteOperation={(id, decision) => void decideRemoteOperation(id, decision)}
              showSuggestions={settings.showSuggestions}
              sendWithEnter={settings.sendWithEnter}
              permissionProfile={settings.permissionProfile}
              fullAccessConfirmed={settings.fullAccessConfirmed}
              taskLoading={Boolean(activeThreadId && loadingThreadId === activeThreadId)}
              threadLoadError={activeThreadId && threadLoadError?.threadId === activeThreadId ? threadLoadError.message : undefined}
              onRetryThread={() => {
                const target = activeThreadId ? bootstrap.threads.find((thread) => thread.id === activeThreadId) : undefined
                if (target) void selectThread(target)
              }}
              onPermissionProfileChange={(permissionProfile, fullAccessConfirmed) => updateSettings({ permissionProfile, fullAccessConfirmed })}
              onSubmit={submitTask}
              onInterrupt={interrupt}
            />
          )}
          {view === 'memory' && <MemoryView project={activeProject} onAsk={(artifact, context) => {
            setComposer(context || `Memory Palace içindeki \"${artifact.title}\" kaydını ve ilişkili bağlamı incele: ${artifact.path}`)
            setView('chat')
            requestAnimationFrame(() => composerRef.current?.focus())
          }} />}
          {view === 'files' && <FilesView project={activeProject} />}
          {view === 'changes' && <ChangesView project={activeProject} connection={connection} activeThreadId={activeThreadId} />}
          {view === 'evidence' && <EvidenceView activeTaskId={activeTaskId} connection={connection} />}
          {view === 'browser' && <BrowserView
            activeTaskId={activeTaskId}
            capabilities={browserCapabilities}
            sessions={browserSessions}
            url={browserUrl}
            error={browserError}
            onUrlChange={setBrowserUrl}
            onRefresh={refreshBrowserStatus}
            onOpen={async () => {
              if (!activeTaskId) { setBrowserError('Select a task before opening a browser tab.'); return }
              setBrowserError('')
              try {
                const result = await codexApi.openBrowserSession({ cwd: activeProject.cwd, url: browserUrl, approval: true, taskId: activeTaskId, threadId: activeThreadId || undefined, workflowId: workflow?.id })
                setBrowserSessions((current) => [result.session, ...current.filter((item) => item.id !== result.session.id)])
              } catch (error) { setBrowserError(error instanceof Error ? error.message : 'Browser could not be opened.') }
            }}
            onNavigate={async (id, url) => {
              if (!activeTaskId) { setBrowserError('Select a task before navigating a browser tab.'); return }
              const session = browserSessions.find((item) => item.id === id)
              if (session?.taskId && session.taskId !== activeTaskId) { setBrowserError('This browser tab belongs to another task.'); return }
              try {
                const result = await codexApi.navigateBrowser(id, url, true, activeTaskId)
                setBrowserSessions((current) => current.map((item) => item.id === id ? result.session : item))
              } catch (error) { setBrowserError(error instanceof Error ? error.message : 'Browser navigation failed.') }
            }}
            onScreenshot={async (id) => {
              if (!activeTaskId) { setBrowserError('Select a task before capturing evidence.'); return }
              const session = browserSessions.find((item) => item.id === id)
              if (session?.taskId && session.taskId !== activeTaskId) { setBrowserError('This browser tab belongs to another task.'); return }
              try { await codexApi.screenshotBrowser(id, 'Manual evidence', activeTaskId) } catch (error) { setBrowserError(error instanceof Error ? error.message : 'Screenshot failed.') }
            }}
            onStop={async (id) => {
              if (!activeTaskId) { setBrowserError('Select a task before stopping a browser tab.'); return }
              const session = browserSessions.find((item) => item.id === id)
              if (session?.taskId && session.taskId !== activeTaskId) { setBrowserError('This browser tab belongs to another task.'); return }
              try {
                const result = await codexApi.stopBrowser(id, 'stopped_by_user', activeTaskId)
                setBrowserSessions((current) => current.map((item) => item.id === id ? result.session : item))
              } catch (error) { setBrowserError(error instanceof Error ? error.message : 'Browser stop failed.') }
            }}
          />}
          {view === 'terminal' && <TerminalView project={activeProject} taskId={activeTaskId} connection={connection} eventTransport={eventTransport} events={terminalEvents} />}
          {view === 'tetris' && <TetrisView />}
          {view === 'extensions' && (
            <ExtensionsView
              bootstrap={bootstrap}
              loading={bootstrapLoading}
              error={bootstrapError}
              activeProjectId={activeProject.id}
              query={extensionSearch}
              onQueryChange={setExtensionSearch}
              onRefresh={refresh}
              onToggleSkill={async (path, enabled) => { await codexApi.setSkillEnabled(path, enabled); refresh() }}
            />
          )}
          {view === 'settings' && (
            <SettingsView
              settings={settings}
              models={models}
              providers={providers}
              providerModels={providerModels}
              providerLoading={providerLoading}
              providerError={providerError}
              preflight={preflight}
              connection={connection}
              version={bootstrap.health.version}
              workspace={activeProject.cwd}
              accountEmail={accountEmail}
              planType={planType}
              quotaUsed={quotaUsed}
              signedIn={signedIn}
              loginPending={loginPending}
              onChange={updateSettings}
              onReset={() => { saveSettings(DEFAULT_SETTINGS); setSettings(DEFAULT_SETTINGS); setSelectedModel(DEFAULT_SETTINGS.defaultModel); setWorkflowPlanModel(DEFAULT_SETTINGS.workflowPlanModel || 'codex:gpt-5.6-sol'); setWorkflowBuildModel(DEFAULT_SETTINGS.workflowBuildModel || 'codex:gpt-5.6-luna'); setWorkflowPlanEffort(DEFAULT_SETTINGS.workflowPlanEffort || 'high'); setWorkflowBuildEffort(DEFAULT_SETTINGS.workflowBuildEffort || 'low') }}
              onRefresh={refresh}
              onLogin={startLogin}
            />
          )}
        </main>
      </div>

      <footer className="studio-statusbar">
        <span className={`engine-state is-${connection}`}><i />{connection === 'ready' ? 'Codex App Server hazır' : connection === 'checking' ? 'Codex denetleniyor' : 'Arayüz önizleme modunda'}</span>
        <span className={`engine-events is-${eventTransport}`} aria-live="polite"><i />{eventTransport === 'connected' ? 'Events connected' : eventTransport === 'reconnecting' ? 'Reconnecting events…' : 'Connecting events…'}</span>
        <span><GitBranchIcon size={12} /> codex/memory-palace</span>
        <span className="status-spacer" />
        {typeof quotaUsed === 'number' && <span className={quotaReached ? 'quota-reached' : ''}>Codex kotası <strong>%{Math.round(quotaUsed)} kullanıldı</strong></span>}
      </footer>

      {projectModalOpen && (
        <ProjectDialog
          projects={projects}
          activeProjectId={activeProjectId}
          draft={projectDraft}
          error={projectError}
          workspaceRoots={bootstrap.health.workspaceRoots ?? []}
          onDraftChange={(draft) => { setProjectDraft(draft); setProjectError('') }}
          onChoose={chooseProject}
          onRename={renameProject}
          onTogglePinned={toggleProjectPinned}
          onToggleArchived={toggleProjectArchived}
          onClose={() => { setProjectModalOpen(false); setProjectError('') }}
          onSubmit={createProject}
        />
      )}
      <CommandPalette open={commandPaletteOpen} commands={workspaceCommands} onClose={() => setCommandPaletteOpen(false)} />
      {approvalRequest && <ApprovalDialog request={approvalRequest} onResolve={resolveApproval} busy={approvalPending} error={approvalError} />}
    </div>
  )
}

function ApprovalDialog({ request, onResolve, busy, error }: { request: ApprovalRequest; onResolve: (decision: 'accept' | 'acceptForSession' | 'decline' | 'cancel') => Promise<void>; busy: boolean; error: string }) {
  const dialogRef = useRef<HTMLElement>(null)
  useDialogFocus(dialogRef, true, () => { if (!busy) void onResolve('cancel') })
  const command = Array.isArray(request.params.command)
    ? request.params.command.join(' ')
    : typeof request.params.command === 'string'
      ? request.params.command
      : null
  const safeCommand = command?.replace(/Bearer\s+[^\s]+/gi, 'Bearer [redacted]').replace(/(sk-[A-Za-z0-9_-]+)/g, '[redacted]')
  const isCommand = request.method === 'item/commandExecution/requestApproval' || request.method === 'execCommandApproval'
  const isFile = request.method === 'item/fileChange/requestApproval' || request.method === 'applyPatchApproval'
  const isInput = request.method === 'item/tool/requestUserInput' || request.method === 'mcpServer/elicitation/request'
  const reason = typeof request.params.reason === 'string' ? request.params.reason : null
  const cwd = typeof request.params.cwd === 'string' ? request.params.cwd : null
  const target = typeof request.params.path === 'string' ? request.params.path : typeof request.params.toolName === 'string' ? request.params.toolName : typeof request.params.serverName === 'string' ? request.params.serverName : null
  const risk = typeof request.params.riskLevel === 'string' ? request.params.riskLevel : typeof request.params.risk === 'string' ? request.params.risk : null
  const turnId = typeof request.params.turnId === 'string' ? request.params.turnId : null
  return (
    <div className="approval-backdrop">
      <section ref={dialogRef} className="approval-dialog" role="alertdialog" aria-modal="true" aria-labelledby="approval-title" aria-describedby="approval-description">
        <span className="approval-icon"><TerminalWindowIcon /></span>
        <div className="approval-copy">
          <span className="content-kicker">CODEX NEEDS YOUR APPROVAL</span>
          <h2 id="approval-title">{isCommand ? 'Allow this command?' : isFile ? 'Allow these file changes?' : isInput ? 'Allow this external request?' : 'Review this permission request?'}</h2>
          <p id="approval-description">{reason || (isCommand ? 'Codex wants to run this command to continue the task.' : isFile ? 'Codex wants to apply changes to this workspace.' : 'Codex is requesting an additional capability for this task.')}</p>
        </div>
        {(command || cwd || target || risk || turnId) && <div className="approval-preview">
          {safeCommand && <code>{safeCommand}</code>}
          {target && <small>Target: {target}</small>}
          {cwd && <small>Workspace: {cwd}</small>}
          {risk && <small>Risk: {risk}</small>}
          {turnId && <small>Turn: {turnId}</small>}
        </div>}
        {error && <p className="approval-error" role="alert">{error} Try again.</p>}
        <div className="approval-actions">
          <button type="button" className="approval-cancel" disabled={busy} onClick={() => void onResolve('cancel')}>Cancel</button>
          <button type="button" className="approval-decline" disabled={busy} onClick={() => void onResolve('decline')}>Decline</button>
          <button type="button" className="approval-session" disabled={busy} onClick={() => void onResolve('acceptForSession')}>{busy ? 'Sending…' : 'Allow for session'}</button>
          <button type="button" className="approval-accept" disabled={busy} onClick={() => void onResolve('accept')}>{busy ? 'Sending…' : 'Allow once'}</button>
        </div>
      </section>
    </div>
  )
}

function ViewTab({ active, onClick, icon, label, featured = false }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string; featured?: boolean }) {
  return <button type="button" className={`view-tab ${active ? 'is-active' : ''} ${featured ? 'is-featured' : ''}`} onClick={onClick}>{icon}<span>{label}</span></button>
}

function RailButton({ active = false, label, onClick, featured = false, children }: { active?: boolean; label: string; onClick: () => void; featured?: boolean; children: React.ReactNode }) {
  return <button type="button" className={`rail-button ${active ? 'is-active' : ''} ${featured ? 'is-featured' : ''}`} onClick={onClick} aria-label={label} title={label}>{children}<span className="rail-tooltip">{label}</span></button>
}

/** Keeps modal focus usable with keyboard and returns focus to the launching control. */
function useDialogFocus<T extends HTMLElement>(dialogRef: React.RefObject<T | null>, open = true, onEscape?: () => void) {
  useEffect(() => {
    if (!open) return
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const focusFirst = () => dialogRef.current?.querySelector<HTMLElement>('button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')?.focus()
    const frame = window.requestAnimationFrame(focusFirst)
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && onEscape) {
        event.preventDefault()
        onEscape()
        return
      }
      if (event.key !== 'Tab') return
      const focusable = [...(dialogRef.current?.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])') ?? [])]
      if (!focusable.length) return
      const first = focusable[0]!
      const last = focusable[focusable.length - 1]!
      if (event.shiftKey && (document.activeElement === first || !dialogRef.current?.contains(document.activeElement))) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.cancelAnimationFrame(frame)
      window.removeEventListener('keydown', onKeyDown)
      window.requestAnimationFrame(() => previous?.focus())
    }
  }, [dialogRef, onEscape, open])
}

type AppModelChoice = {
  value: string
  label: string
  providerId: string
  providerLabel: string
  description: string
  featured: boolean
  supportedReasoningEfforts: WorkflowReasoningEffort[]
  defaultReasoningEffort: WorkflowReasoningEffort
}

function AppModelPicker({ models, providerModels, selectedProvider, selectedModel, selectedEffort, onChange, onEffortChange, compact = false }: {
  models: CodexModel[]
  providerModels: ProviderModel[]
  selectedProvider: string
  selectedModel: string
  selectedEffort: WorkflowReasoningEffort
  onChange: (value: string) => void
  onEffortChange?: (value: WorkflowReasoningEffort) => void
  compact?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [activePanel, setActivePanel] = useState<'model' | 'reasoning' | null>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const rawCurrentValue = selectedModel === 'auto' ? 'auto' : `${selectedProvider}:${selectedModel}`
  const chatReasoningEfforts = (value: WorkflowReasoningEffort[], modelId: string) => {
    const normalizedModel = modelId.toLowerCase()
    const filtered = value.filter((effort) => effort !== 'minimal' && (CHAT_REASONING_LABELS[effort] != null) && !(normalizedModel.includes('luna') && effort === 'ultra'))
    return filtered.length ? filtered : ['low', 'medium', 'high', 'xhigh', 'max'].filter((effort) => !(normalizedModel.includes('luna') && effort === 'ultra')) as WorkflowReasoningEffort[]
  }
  const choices = useMemo<AppModelChoice[]>(() => {
    const codexChoices = models.map((model) => {
      const supportedReasoningEfforts = chatReasoningEfforts(workflowEffortsForModel(model, 'codex', model.id), model.id)
      return {
        value: `codex:${model.id}`,
        label: modelName(model),
        providerId: 'codex',
        providerLabel: PROVIDER_LABELS.codex || 'Codex',
        description: model.description || 'Codex çalışma modeli',
        featured: FEATURED_CHATGPT_MODELS.has(model.id.toLowerCase()),
        supportedReasoningEfforts,
        defaultReasoningEffort: supportedReasoningEfforts.includes(model.defaultReasoningEffort as WorkflowReasoningEffort) ? model.defaultReasoningEffort as WorkflowReasoningEffort : supportedReasoningEfforts.includes('medium') ? 'medium' : supportedReasoningEfforts[0] || 'medium',
      }
    })
    const providerChoices = providerModels
      .filter((model) => model.providerId !== 'codex' && model.available)
      .map((model) => {
        const modelId = model.providerModelId || model.id.split(':').slice(1).join(':') || model.id
        const supportedReasoningEfforts = chatReasoningEfforts(workflowEffortsForModel(model, model.providerId, modelId), modelId)
        return {
          value: model.id.includes(':') ? model.id : `${model.providerId}:${model.id}`,
          label: modelName(model),
          providerId: model.providerId,
          providerLabel: PROVIDER_LABELS[model.providerId] || model.providerId,
          description: model.description || 'Bağlı sağlayıcı modeli',
          featured: false,
          supportedReasoningEfforts,
          defaultReasoningEffort: supportedReasoningEfforts.includes(model.defaultReasoningEffort as WorkflowReasoningEffort) ? model.defaultReasoningEffort as WorkflowReasoningEffort : supportedReasoningEfforts.includes('medium') ? 'medium' : supportedReasoningEfforts[0] || 'medium',
        }
      })
    return [...codexChoices, ...providerChoices]
  }, [models, providerModels])
  const defaultVisibleChoice = choices.find((choice) => choice.value === 'codex:gpt-5.6-terra') || choices.find((choice) => choice.featured) || choices[0]
  const visibleCurrentValue = rawCurrentValue === 'auto' ? defaultVisibleChoice?.value || rawCurrentValue : rawCurrentValue
  const current = choices.find((choice) => choice.value === visibleCurrentValue) || {
    value: visibleCurrentValue,
    label: selectedModel === 'auto' ? 'GPT-5.6 Terra' : selectedModel,
    providerId: selectedProvider,
    providerLabel: PROVIDER_LABELS[selectedProvider] || selectedProvider,
    description: 'Seçili model',
    featured: false,
    supportedReasoningEfforts: ['low', 'medium', 'high', 'xhigh', 'max', 'ultra'] as WorkflowReasoningEffort[],
    defaultReasoningEffort: 'medium' as WorkflowReasoningEffort,
  }
  const reasoningOptions = current.supportedReasoningEfforts
  const activeEffort = reasoningOptions.includes(selectedEffort) ? selectedEffort : current.defaultReasoningEffort
  const conciseModelLabel = (label: string) => label.replace(/^GPT[-\s]?/i, '').replace(/^(\d+\.\d+)-/, '$1 ')
  const shortModelLabel = conciseModelLabel(current.label)
  const effortLabel = CHAT_REASONING_LABELS[activeEffort]

  const selectChoice = (choice: AppModelChoice) => {
    onChange(choice.value)
    if (onEffortChange) onEffortChange(choice.supportedReasoningEfforts.includes(selectedEffort) ? selectedEffort : choice.defaultReasoningEffort)
    setOpen(false)
  }
  const resetToDefaults = () => {
    if (!defaultVisibleChoice) return
    onChange(defaultVisibleChoice.value)
    onEffortChange?.(defaultVisibleChoice.defaultReasoningEffort)
    setOpen(false)
  }
  const toggleOpen = () => {
    if (!open) setActivePanel(null)
    setOpen((value) => !value)
  }

  useEffect(() => {
    if (!open) return
    const close = (event: MouseEvent) => {
      if (event.target instanceof Node && !wrapperRef.current?.contains(event.target)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])
  useEffect(() => {
    if (!open) return
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        setOpen(false)
      }
    }
    document.addEventListener('keydown', closeOnEscape)
    return () => document.removeEventListener('keydown', closeOnEscape)
  }, [open])

  return (
    <div ref={wrapperRef} className={`app-model-picker ${compact ? 'is-compact is-top' : 'is-composer'}`}>
      <button
        type="button"
        className="app-model-trigger"
        data-testid={compact ? undefined : 'composer-model-trigger'}
        aria-label={`Model: ${current.label}, çaba: ${effortLabel}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Model ve çaba ayarlarını aç"
        onClick={toggleOpen}
        onKeyDown={(event) => {
          if ((event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') && !open) {
            event.preventDefault()
            setActivePanel(null)
            setOpen(true)
          }
        }}
      >
        <span className="app-model-inline-copy">
          <strong>{shortModelLabel}</strong>
          <small>{effortLabel}</small>
        </span>
        <CaretDownIcon className="app-model-caret" size={12} aria-hidden="true" />
      </button>
      {open && <div className={`app-model-menu ${activePanel ? 'has-side-panel' : ''}`} role="dialog" aria-label="Model ayarları">
        <div className="app-model-control-sheet">
          <button type="button" className={`app-model-control-row ${activePanel === 'model' ? 'is-active' : ''}`} onMouseEnter={() => setActivePanel('model')} onFocus={() => setActivePanel('model')} onClick={() => setActivePanel('model')}>
            <strong>Model</strong><span>{shortModelLabel}</span><CaretDownIcon className="app-model-row-caret" size={15} aria-hidden="true" />
          </button>
          <button type="button" className={`app-model-control-row ${activePanel === 'reasoning' ? 'is-active' : ''}`} onMouseEnter={() => setActivePanel('reasoning')} onFocus={() => setActivePanel('reasoning')} onClick={() => setActivePanel('reasoning')}>
            <strong>Çaba</strong><span>{effortLabel}</span><CaretDownIcon className="app-model-row-caret" size={15} aria-hidden="true" />
          </button>
          <button type="button" className="app-model-reset" onClick={resetToDefaults}><span>Varsayılana sıfırla</span><ArrowClockwiseIcon size={17} aria-hidden="true" /></button>
        </div>
        {activePanel && <div className="app-model-side-menu" role="listbox" aria-label={activePanel === 'model' ? 'Model seç' : 'Çaba seç'}>
          {activePanel === 'reasoning' && <strong className="app-model-side-title">Çaba</strong>}
          {activePanel === 'model'
            ? choices.map((choice) => <button type="button" role="option" aria-selected={choice.value === visibleCurrentValue} className={`app-model-side-option ${choice.value === visibleCurrentValue ? 'is-selected' : ''}`} key={choice.value} onClick={() => selectChoice(choice)}>
              <span>{conciseModelLabel(choice.label)}</span>{choice.value === visibleCurrentValue && <CheckIcon size={18} aria-hidden="true" />}
            </button>)
            : reasoningOptions.map((effort) => <button key={effort} type="button" role="option" className={`app-model-side-option ${effort === activeEffort ? 'is-selected' : ''}`} aria-selected={effort === activeEffort} onClick={() => { onEffortChange?.(effort); setOpen(false) }}>
              <span>{CHAT_REASONING_LABELS[effort]}</span>{effort === activeEffort && <CheckIcon size={18} aria-hidden="true" />}
            </button>)}
        </div>}
      </div>}
    </div>
  )
}

function PermissionProfilePicker({ value, fullAccessConfirmed, onChange }: {
  value: PermissionProfileId
  fullAccessConfirmed: boolean
  onChange: (profile: PermissionProfileId, fullAccessConfirmed: boolean) => void
}) {
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const current = PERMISSION_PROFILE_OPTIONS.find((option) => option.value === value) || PERMISSION_PROFILE_OPTIONS[1]!
  const selectProfile = (next: PermissionProfileId) => {
    if (next === 'full-access' && !fullAccessConfirmed && !window.confirm('Full access can run commands and edit files without per-action approval. Enable it for new tasks?')) return
    onChange(next, next === 'full-access')
    setOpen(false)
  }

  useEffect(() => {
    if (!open) return
    const close = (event: MouseEvent) => {
      if (event.target instanceof Node && !wrapperRef.current?.contains(event.target)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])
  useEffect(() => {
    if (!open) return
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        setOpen(false)
      }
    }
    document.addEventListener('keydown', closeOnEscape)
    return () => document.removeEventListener('keydown', closeOnEscape)
  }, [open])

  return <div ref={wrapperRef} className={`composer-access-picker is-${value}`}>
    <button type="button" className="composer-access-trigger" data-testid="permission-trigger" aria-label={`İşlem izni: ${current.label}`} aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((active) => !active)}>
      <PermissionProfileGlyph profile={value} compact />
      <span className="access-level-copy"><strong>{current.label}</strong></span>
      <CaretDownIcon className="access-level-caret" size={13} aria-hidden="true" />
    </button>
    {open && <div className="composer-access-menu" role="listbox" aria-label="İşlem izni">
      <div className="composer-access-menu-header"><strong>Code işlemleri nasıl onaylanmalı?</strong><span>Daha fazla bilgi</span></div>
      {PERMISSION_PROFILE_OPTIONS.map((option) => <button type="button" role="option" aria-selected={option.value === value} className={`composer-access-option ${option.value === value ? 'is-selected' : ''} is-${option.value}`} key={option.value} onClick={() => selectProfile(option.value)}>
        <PermissionProfileGlyph profile={option.value} />
        <span><strong>{option.label}</strong><small>{option.description}</small></span>
        {option.value === value && <CheckCircleIcon weight="fill" aria-hidden="true" />}
      </button>)}
    </div>}
  </div>
}

function PermissionProfileGlyph({ profile, compact = false }: { profile: PermissionProfileId; compact?: boolean }) {
  const size = compact ? 20 : 27
  if (profile === 'read-only') return <span className="permission-profile-glyph is-read-only" aria-hidden="true"><EyeIcon size={size} /></span>
  if (profile === 'granular') return <span className="permission-profile-glyph is-granular" aria-hidden="true"><HandPalmIcon size={size} /></span>
  if (profile === 'workspace-auto') return <span className="permission-profile-glyph is-workspace-auto" aria-hidden="true"><ShieldCheckIcon size={size} /></span>
  return <span className="permission-profile-glyph is-full-access" aria-hidden="true"><ShieldWarningIcon size={size} /></span>
}

function WorkflowModelPicker({ role, value, effort, options, onChange, onEffortChange }: {
  role: 'plan' | 'build'
  value: string
  effort: WorkflowReasoningEffort
  options: WorkflowModelOption[]
  onChange: (value: string) => void
  onEffortChange: (value: WorkflowReasoningEffort) => void
}) {
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const fallback = role === 'plan' ? 'Sol' : 'Luna'
  const shortLabel = shortWorkflowModelLabel(value, options, fallback)
  const roleLabel = role === 'plan' ? 'Planning model' : 'Build model'
  const selectedOption = options.find((option) => option.value === value)
  const effortOptions = selectedOption?.supportedReasoningEfforts || []
  useEffect(() => {
    if (!open) return
    const close = (event: MouseEvent) => {
      if (event.target instanceof Node && !wrapperRef.current?.contains(event.target)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])
  useEffect(() => {
    if (!open) return
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        setOpen(false)
      }
    }
    document.addEventListener('keydown', closeOnEscape)
    return () => document.removeEventListener('keydown', closeOnEscape)
  }, [open])
  return (
    <div ref={wrapperRef} className="workflow-config-lane">
      <div className="workflow-model-row">
        <span className="workflow-role-label">{role === 'plan' ? 'PLAN' : 'BUILD'}</span>
        <button type="button" className="workflow-model-control" aria-label={`${roleLabel}: ${shortLabel}`} aria-haspopup="listbox" aria-expanded={open} title={role === 'plan' ? 'The stronger model researches and creates the implementation plan' : 'The execution model applies approved steps and runs checks'} onClick={() => setOpen((current) => !current)}>
          <strong>{shortLabel}</strong><CaretDownIcon />
        </button>
      </div>
      <label className="workflow-effort-control">
        <span>Reasoning</span>
        <select aria-label={`${roleLabel} reasoning`} value={effort} disabled={!effortOptions.length} onChange={(event) => onEffortChange(event.target.value as WorkflowReasoningEffort)}>
          {effortOptions.map((option) => <option key={option} value={option}>{WORKFLOW_EFFORT_LABELS[option]}</option>)}
          {!effortOptions.length && <option value={effort}>{WORKFLOW_EFFORT_LABELS[effort]}</option>}
        </select>
        <CaretDownIcon />
      </label>
      {open && <div className="workflow-model-menu" role="listbox" aria-label={`${roleLabel} options`}>
        {options.map((option) => <button type="button" role="option" aria-selected={option.value === value} key={option.value} onClick={() => { onChange(option.value); setOpen(false) }}>
          <span><strong>{option.label}</strong><small>{option.providerId === 'codex' ? 'Codex' : option.providerId}</small></span>
          {option.value === value && <CheckCircleIcon weight="fill" aria-hidden="true" />}
        </button>)}
        {!options.length && <span className="workflow-model-empty">No verified models available</span>}
      </div>}
    </div>
  )
}

function ComposerMentionPreview({ value, scrollTop, agents }: { value: string; scrollTop: number; agents: TeamComposerProfile[] }) {
  const mentionPattern = new RegExp(`(@(?:${agents.map((agent) => agent.id.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})\\b)`, 'gi')
  const accentForMention = (mention: string) => agents.find((agent) => agent.id === mention.slice(1).toLocaleLowerCase('tr-TR'))?.accent
  return <div className="composer-mention-overlay" aria-hidden="true" style={{ transform: `translateY(-${scrollTop}px)` }}>{value.split(mentionPattern).map((part, index) => {
    const accent = accentForMention(part)
    return accent ? <span className="composer-agent-mention" style={{ '--agent-accent': accent } as React.CSSProperties} key={`${part}-${index}`}>{part}</span> : <span key={`${part}-${index}`}>{part}</span>
  })}</div>
}

function TeamComposerDeck({ agents, onMention }: { agents: TeamComposerProfile[]; onMention: (agent: TeamComposerProfile) => void }) {
  const [overflowOpen, setOverflowOpen] = useState(false)
  const overflowAgents = agents.slice(6)
  return (
    <section className="team-composer-deck" data-testid="team-composer-deck" aria-label={`Team agents: ${agents.map((agent) => agent.name).join(', ')}`}>
      <div className="team-composer-orbit" aria-hidden="true" />
      <div className="team-composer-avatars">
        {agents.map((agent, index) => <button type="button" className={`team-composer-avatar ${agent.id === 'adrian' ? 'is-lead' : ''} ${agent.state === 'running' ? 'is-working' : ''}`} style={{ '--team-accent': agent.accent, '--team-index': index } as React.CSSProperties} key={agent.id} onClick={() => onMention(agent)} aria-label={`@${agent.id} ekle — ${agent.name}, ${agent.role}${agent.state === 'running' ? ', çalışıyor' : ''}`}>
          <img src={agent.asset} alt="" width={54} height={54} />
          <i />
          <span className="team-composer-agent-card"><img src={agent.asset} alt="" width={80} height={100} /><span><strong>{agent.name}</strong><small>{agent.role}</small><em>{agent.detail}</em></span></span>
        </button>)}
        {overflowAgents.length > 0 && <div className="team-composer-overflow">
          <button type="button" className="team-composer-overflow-trigger" aria-label={`Show more team agents (${overflowAgents.length})`} aria-expanded={overflowOpen} aria-haspopup="menu" onClick={() => setOverflowOpen((open) => !open)}>+{overflowAgents.length}</button>
          {overflowOpen && <div className="team-composer-overflow-menu" role="menu" aria-label="More team agents">
            {overflowAgents.map((agent) => <button type="button" role="menuitem" key={agent.id} onClick={() => { onMention(agent); setOverflowOpen(false) }}><img src={agent.asset} alt="" width={30} height={30} /><span><strong>{agent.name}</strong><small>{agent.role}</small></span></button>)}
          </div>}
        </div>}
      </div>
    </section>
  )
}

function ChatView({ project, messages, activityState, plan, composer, attachments, queuedItems, running, stopping, connection, activeThreadId, taskTitle, taskBranch, sideParentThreadId, quotaUsed, memoryUsage, models, providerModels, selectedProvider, selectedModel, selectedReasoningEffort, workflowMode, teamMode, workflowPlanning, workflowModelOptions, workflowPlanModel, workflowBuildModel, workflowPlanEffort, workflowBuildEffort, workflow, workflowGraph, remoteOperations, remoteRequestNonce, onRemoteRequestHandled, composerRef, messagesEndRef, showSuggestions, sendWithEnter, permissionProfile, fullAccessConfirmed, taskLoading, threadLoadError, onRetryThread, onPermissionProfileChange, onComposerChange, onModelChange, onReasoningEffortChange, onToggleWorkflow, onToggleTeam, onWorkflowPlanModelChange, onWorkflowBuildModelChange, onWorkflowPlanEffortChange, onWorkflowBuildEffortChange, onApproveWorkflow, onPauseWorkflow, onResumeWorkflow, onRequestPlanChanges, onAgentMessage, onQueue, onSteer, onSideChat, onHandOff, onReturnToParent, onOpenFiles, onOpenChanges, onCommit, onRemoveAttachment, onRemoveQueued, onAddAttachments, onCancelWorkflow, onDecideRemoteOperation, onSubmit, onInterrupt }: {
  project: StudioProject
  messages: ChatMessage[]
  activityState?: TaskState
  plan?: PlanState
  composer: string
  attachments: AttachedFile[]
  queuedItems: QueuedTask[]
  running: boolean
  stopping: boolean
  connection: 'checking' | 'ready' | 'offline'
  activeThreadId: string | null
  taskTitle: string
  taskBranch: string
  sideParentThreadId: string | null
  quotaUsed?: number
  memoryUsage: MemoryUsage | null
  models: CodexModel[]
  providerModels: ProviderModel[]
  selectedProvider: string
  selectedModel: string
  selectedReasoningEffort: WorkflowReasoningEffort
  workflowMode: boolean
  teamMode: boolean
  workflowPlanning: boolean
  workflowModelOptions: WorkflowModelOption[]
  workflowPlanModel: string
  workflowBuildModel: string
  workflowPlanEffort: WorkflowReasoningEffort
  workflowBuildEffort: WorkflowReasoningEffort
  workflow: WorkflowPlan | null
  workflowGraph: AgentGraph | null
  remoteOperations: RemoteOperation[]
  remoteRequestNonce: number
  onRemoteRequestHandled: () => void
  showSuggestions: boolean
  sendWithEnter: boolean
  permissionProfile: PermissionProfileId
  fullAccessConfirmed: boolean
  taskLoading: boolean
  threadLoadError?: string
  onRetryThread: () => void
  onPermissionProfileChange: (profile: PermissionProfileId, confirmed: boolean) => void
  composerRef: React.RefObject<HTMLTextAreaElement | null>
  messagesEndRef: React.RefObject<HTMLDivElement | null>
  onComposerChange: (value: string) => void
  onModelChange: (value: string) => void
  onReasoningEffortChange: (value: WorkflowReasoningEffort) => void
  onToggleWorkflow: () => void
  onToggleTeam: () => void
  onWorkflowPlanModelChange: (value: string) => void
  onWorkflowBuildModelChange: (value: string) => void
  onWorkflowPlanEffortChange: (value: WorkflowReasoningEffort) => void
  onWorkflowBuildEffortChange: (value: WorkflowReasoningEffort) => void
  onApproveWorkflow: () => void
  onPauseWorkflow: () => void
  onResumeWorkflow: () => void
  onRequestPlanChanges: (instruction: string) => void
  onAgentMessage: (message: string, agentId?: string) => void
  onQueue: (text: string, attachments: AttachedFile[]) => void
  onSteer: (text: string, attachments: AttachedFile[]) => void
  onSideChat: () => void
  onHandOff: () => void
  onReturnToParent: () => void
  onOpenFiles: () => void
  onOpenChanges: () => void
  onCommit: () => void
  onRemoveAttachment: (id: string) => void
  onAddAttachments: (files: AttachedFile[]) => void
  onRemoveQueued: (id: string) => void
  onCancelWorkflow: () => void
  onDecideRemoteOperation: (id: string, decision: 'approve' | 'decline') => void
  onSubmit: (event?: FormEvent, attachments?: AttachedFile[]) => void
  onInterrupt: () => void
}) {
  const attachmentInputRef = useRef<HTMLInputElement>(null)
  const [remotePairing, setRemotePairing] = useState<RemotePairing | null>(null)
  const [remoteError, setRemoteError] = useState('')
  const [remoteLoading, setRemoteLoading] = useState(false)
  const [remoteNow, setRemoteNow] = useState(() => Date.now())
  const remoteDialogRef = useRef<HTMLElement>(null)
  const composerMenuRef = useRef<HTMLDivElement>(null)
  const [composerMenuOpen, setComposerMenuOpen] = useState(false)
  const [planHelpOpen, setPlanHelpOpen] = useState(false)
  const [composerScrollTop, setComposerScrollTop] = useState(0)
  const composerDockRef = useRef<HTMLDivElement>(null)
  const [composerClearance, setComposerClearance] = useState(0)
  const conversationRef = useRef<HTMLDivElement>(null)
  const nearBottomRef = useRef(true)
  const [newActivity, setNewActivity] = useState(false)
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null)
  const messageTimesRef = useRef(new Map<string, number>())
  const messageTime = (message: ChatMessage) => {
    if (message.createdAt) return formatMessageTime(message.createdAt)
    const cached = messageTimesRef.current.get(message.id)
    if (cached) return formatMessageTime(cached)
    const now = Date.now()
    messageTimesRef.current.set(message.id, now)
    return formatMessageTime(now)
  }
  const copyMessage = async (message: ChatMessage) => {
    try {
      if (!navigator.clipboard) return
      await navigator.clipboard.writeText(message.content)
      setCopiedMessageId(message.id)
      window.setTimeout(() => setCopiedMessageId((current) => current === message.id ? null : current), 1_400)
    } catch {
      setCopiedMessageId(null)
    }
  }
  const isEmpty = !activeThreadId && messages.length === 1 && messages[0]?.id === WELCOME_MESSAGE.id
  const workflowPlanLabel = shortWorkflowModelLabel(workflowPlanModel, workflowModelOptions, 'Sol')
  const workflowBuildLabel = shortWorkflowModelLabel(workflowBuildModel, workflowModelOptions, 'Luna')
  const boundedMessages = useMemo(() => messages.length > MAX_FALLBACK_MESSAGES ? messages.slice(-MAX_FALLBACK_MESSAGES) : messages, [messages])
  const activeTeamAgents = plan?.team?.agents ?? activityState?.plan?.team?.agents ?? workflowGraph?.agents ?? []
  const teamComposerAgents = useMemo(() => {
    const markPlanningLead = (agents: TeamComposerProfile[]) => workflowPlanning
      ? agents.map((agent) => agent.id === 'adrian' ? { ...agent, state: 'running' } : agent)
      : agents
    if (!activeTeamAgents.length) return markPlanningLead(TEAM_COMPOSER_PROFILES)
    const seen = new Set<string>()
    const resolved = activeTeamAgents.flatMap((agent) => {
      const profileId = typeof agent.profileId === 'string' ? agent.profileId : agent.id
      if (seen.has(profileId)) return []
      seen.add(profileId)
      const profile = TEAM_COMPOSER_PROFILES.find((candidate) => candidate.id === profileId)
      const asset = typeof agent.avatar?.asset === 'string' ? agent.avatar.asset : profile?.asset
      if (!asset) return []
      return [{
        id: profile?.id || profileId,
        name: agent.displayName || agent.title || profile?.name || 'Agent',
        role: agent.roleLabel || agent.role || profile?.role || 'Specialist',
        detail: profile?.detail || agent.statusSummary || 'Bu görev için gerektiğinde uzman katkısı sağlar.',
        asset,
        accent: typeof agent.avatar?.accent === 'string' ? agent.avatar.accent : profile?.accent || '#8ea6b8',
        state: typeof agent.state === 'string' ? agent.state : undefined,
      }]
    })
    const agents = resolved.length ? resolved.sort((left, right) => Number(right.id === 'adrian') - Number(left.id === 'adrian')) : TEAM_COMPOSER_PROFILES
    return markPlanningLead(agents)
  }, [activeTeamAgents, workflowPlanning])
  const addAgentMention = (agent: TeamComposerProfile) => {
    const input = composerRef.current
    const start = input?.selectionStart ?? composer.length
    const end = input?.selectionEnd ?? composer.length
    const before = composer.slice(0, start)
    const after = composer.slice(end)
    const prefix = before && !/\s$/.test(before) ? ' ' : ''
    const suffix = /^\s/.test(after) ? '' : ' '
    const mention = `@${agent.id}`
    const next = `${before}${prefix}${mention}${suffix}${after}`
    onComposerChange(next)
    window.requestAnimationFrame(() => {
      const cursor = before.length + prefix.length + mention.length + suffix.length
      input?.focus()
      input?.setSelectionRange(cursor, cursor)
    })
  }
  const liveAnnouncement = stopping ? 'Stopping the task.' : running ? 'Task is running.' : activityState ? 'Task is ready.' : ''
  // Provider adapters return one completed response rather than Codex task
  // events. Keep the same visual feedback during that gap without pretending
  // to expose hidden chain-of-thought.
  const pendingProviderActivity = running && !activityState?.events.length && !plan
  const activityFingerprint = useMemo(() => {
    const lastEvent = activityState?.events.at(-1)
    const lastMessage = activityState?.messages.at(-1)
    const activeStep = plan?.steps.find((step) => step.id === plan.activeStepId)
    return [
      activityState?.events.length ?? 0,
      lastEvent?.eventId ?? '',
      lastEvent?.sequence ?? 0,
      lastEvent?.method ?? '',
      lastMessage?.id ?? '',
      lastMessage?.content.length ?? 0,
      lastMessage?.streaming ? 'streaming' : '',
      plan?.status ?? '',
      plan?.activeStepId ?? '',
      activeStep?.status ?? '',
    ].join('|')
  }, [activityState?.events, activityState?.messages, plan])
  const remoteOpen = remoteLoading || Boolean(remotePairing) || Boolean(remoteError)
  const closeRemote = useCallback(() => { setRemotePairing(null); setRemoteError('') }, [])
  useDialogFocus(remoteDialogRef, remoteOpen, closeRemote)

  useEffect(() => {
    if (!composerMenuOpen) return
    const close = (event: MouseEvent) => {
      if (event.target instanceof Node && !composerMenuRef.current?.contains(event.target)) setComposerMenuOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [composerMenuOpen])

  useEffect(() => {
    const dock = composerDockRef.current
    if (!dock) return
    const updateClearance = () => setComposerClearance(Math.ceil(dock.getBoundingClientRect().height) + 20)
    updateClearance()
    const observer = new ResizeObserver(updateClearance)
    observer.observe(dock)
    return () => observer.disconnect()
  }, [attachments.length, teamMode, workflowMode])

  useEffect(() => {
    if (!remotePairing) return
    setRemoteNow(Date.now())
    const timer = window.setInterval(() => setRemoteNow(Date.now()), 1_000)
    return () => window.clearInterval(timer)
  }, [remotePairing])

  useEffect(() => {
    const element = conversationRef.current
    if (!element) return
    if (nearBottomRef.current) {
      // Streaming events can arrive several times per second. Smooth scrolling
      // emits intermediate scroll events and can falsely detach live-follow
      // before the animation reaches the bottom.
      messagesEndRef.current?.scrollIntoView({ block: 'end', behavior: 'auto' })
      setNewActivity(false)
    } else {
      setNewActivity(true)
    }
  }, [activityFingerprint, messages, messagesEndRef, running])

  const handleConversationScroll = () => {
    const element = conversationRef.current
    if (!element) return
    nearBottomRef.current = element.scrollHeight - element.scrollTop - element.clientHeight < 80
    if (nearBottomRef.current) setNewActivity(false)
  }

  const selectAttachments = async (files: FileList | null) => {
    const selected = Array.from(files ?? []).slice(0, 6 - attachments.length)
    const allowedText = new Set(['.md', '.txt', '.json', '.ts', '.tsx', '.js', '.jsx', '.css', '.html', '.py', '.yml', '.yaml', '.toml', '.xml', '.csv', '.java', '.go', '.rs', '.rb', '.php', '.sql', '.sh'])
    const loaded: AttachedFile[] = []
    for (const file of selected) {
      const extension = file.name.includes('.') ? `.${file.name.split('.').pop()!.toLowerCase()}` : ''
      const id = `attachment-${Date.now()}-${Math.random().toString(16).slice(2)}`
      if (file.size > 2_000_000 && !file.type.startsWith('image/')) {
        loaded.push({ id, name: file.name, mime: file.type || 'application/octet-stream', size: file.size, kind: 'text', error: 'Text files must be smaller than 2 MB.' })
        continue
      }
      if (file.type.startsWith('image/')) {
        if (file.size > 700_000) { loaded.push({ id, name: file.name, mime: file.type, size: file.size, kind: 'image', error: 'Images must be smaller than 700 KB for safe inline upload.' }); continue }
        const dataUrl = await new Promise<string>((resolve, reject) => { const reader = new FileReader(); reader.onload = () => resolve(String(reader.result)); reader.onerror = () => reject(reader.error); reader.readAsDataURL(file) }).catch(() => '')
        loaded.push({ id, name: file.name, mime: file.type, size: file.size, kind: 'image', dataUrl: dataUrl || undefined, error: dataUrl ? undefined : 'Image could not be read.' })
        continue
      }
      if (!(file.type.startsWith('text/') || allowedText.has(extension))) {
        loaded.push({ id, name: file.name, mime: file.type || 'application/octet-stream', size: file.size, kind: 'text', error: 'This file type is not supported as a text attachment.' })
        continue
      }
      const content = (await file.text()).slice(0, 120_000)
      loaded.push({ id, name: file.name, mime: file.type || 'text/plain', size: file.size, kind: 'text', content })
    }
    onAddAttachments(loaded)
  }

  const openRemote = async () => {
    setRemotePairing(null)
    setRemoteError('')
    setRemoteLoading(true)
    try {
      setRemotePairing(await codexApi.remotePairing())
    } catch (error) {
      setRemoteError(error instanceof Error ? error.message : 'Remote pairing is not available.')
    } finally {
      setRemoteLoading(false)
    }
  }

  useEffect(() => {
    if (remoteRequestNonce > 0) {
      onRemoteRequestHandled()
      void openRemote()
    }
    // The nonce is the intentional trigger; openRemote closes over current task UI state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteRequestNonce])

  return (
    <section className={`chat-view ${isEmpty ? 'is-empty' : 'has-conversation'} ${teamMode ? 'is-team' : ''}`} aria-label="Codex workspace" style={composerClearance ? { '--composer-clearance': `${composerClearance}px` } as React.CSSProperties : undefined}>
      <div className="sr-only" aria-live="polite" aria-atomic="true">{liveAnnouncement}</div>

      <div ref={conversationRef} className="conversation-scroll" onScroll={handleConversationScroll}>
        <div className="conversation-column">
          {taskLoading && <div className="task-history-status is-loading" role="status" aria-live="polite"><CircleNotchIcon className="spin" /><span><strong>Loading task history</strong><small>Restoring the conversation and its latest activity…</small></span></div>}
          {threadLoadError && !taskLoading && <div className="task-history-status is-error" role="alert"><WarningIcon /><span><strong>Could not load this task</strong><small>{threadLoadError}</small></span><button type="button" onClick={onRetryThread}>Retry</button></div>}
          {sideParentThreadId && <div className="side-chat-banner" role="status"><ChatCircleDotsIcon /><span><strong>Side chat</strong><small>Bounded snapshot from the parent task · parent continues independently.</small></span><button type="button" onClick={onReturnToParent}>Return to parent</button></div>}
          {typeof quotaUsed === 'number' && quotaUsed >= 100 && (
            <div className="quota-warning"><span>!</span><div><strong>Codex kullanım kotası şu anda dolu.</strong><p>Arayüz ve geçmiş çalışır; yeni bir gerçek görev, kota yenilenene kadar Codex tarafından reddedilebilir.</p></div></div>
          )}
          {memoryUsage && <div className="memory-context-note" role="status"><BrainIcon /><span><strong>{memoryUsage.sources?.length ?? 0} relevant memory records</strong><small>Added to this task · about {memoryUsage.approximateTokens} tokens{memoryUsage.truncated ? ' · bounded' : ''}</small></span></div>}
          {memoryUsage?.sources?.length ? <div className="memory-context-sources" aria-label="Memory sources">{memoryUsage.sources.slice(0, 4).map((source) => <span key={source.id}><i />{source.title || source.path}<small>{source.type}</small></span>)}</div> : null}
          {workflowMode && <div className="workflow-mode-banner" role="status" aria-label="Plan and build mode"><SparkleIcon /><span><strong>Plan Builder</strong><small>{workflowPlanLabel} inspects the project and writes a file-aware plan; {workflowBuildLabel} applies only the approved steps.</small></span></div>}
          {isEmpty && (
            <div className="task-welcome">
              <span className="codex-cloud" aria-hidden="true"><TerminalWindowIcon /></span>
              <h2>{activeThreadId ? 'Continue building' : `${project.name} project: what shall we build?`}</h2>
              <p>Describe the outcome. Choose a model and its intelligence level for each task.</p>
              {showSuggestions && <div className="starter-grid">
                {([
                  ['Review project', 'Review the architecture, identify weak points, and propose a plan.'],
                  ['Build a feature', 'Implement the requested feature, test it, and explain the result.'],
                  ['Fix a problem', 'Find the root cause and fix the problem safely.'],
                  ['Make a plan', 'Break the work into steps, identify risks, and propose an implementation plan.'],
                ] as const).map(([title, prompt]) => <button key={title} type="button" onClick={() => onComposerChange(prompt)}><strong>{title}</strong><span>{prompt}</span><ArrowUpRightIcon /></button>)}
              </div>}
            </div>
          )}

          <div className="message-list" aria-live="polite">
            {/*
             * Do not switch to the activity renderer merely because a request
             * has started. Provider turns (and the short gap before the first
             * Codex event) have no TaskState yet; ActivityTimeline would return
             * null in that state and make the whole conversation disappear
             * until the response completes. Keep the normal transcript visible
             * until there is actual activity or a plan to render.
             */}
            {activityState?.events.length || plan ? (
              <ActivityTimeline
                state={activityState}
                plan={plan}
                hidePlan={workflowPlanning}
                isRunning={running}
                fallbackMessages={isEmpty ? [] : messages}
                onApprove={workflow ? onApproveWorkflow : undefined}
                onPause={workflow ? onPauseWorkflow : undefined}
                onResume={workflow ? onResumeWorkflow : undefined}
                onCancel={workflow ? onCancelWorkflow : undefined}
                onRequestChanges={workflow ? onRequestPlanChanges : undefined}
                onAgentMessage={workflow ? onAgentMessage : undefined}
              />
            ) : <>
              {(isEmpty ? [] : boundedMessages).map((message) => (
                <article key={message.id} className={`message is-${message.role}`}>
                  <span className="message-avatar">{message.role === 'user' ? 'S' : message.role === 'notice' ? '!' : <span className="mini-loci-mark" />}</span>
                  <div className="message-body">
                    <div className="message-meta"><strong>{message.role === 'user' ? 'You' : message.role === 'notice' ? 'System' : 'Codex'}</strong>{message.streaming && <span><CircleNotchIcon className="spin" /> working</span>}</div>
                    <p>{message.content}</p>
                    {message.role === 'assistant' && <div className="message-actions" aria-label="Message actions">
                      <button type="button" className="message-action-button" aria-label="Copy message" title={copiedMessageId === message.id ? 'Copied' : 'Copy'} onClick={() => void copyMessage(message)}>
                        <CopyIcon aria-hidden="true" />
                        <span className="sr-only">{copiedMessageId === message.id ? 'Copied' : 'Copy'}</span>
                      </button>
                      <time dateTime={message.createdAt ? new Date(message.createdAt).toISOString() : undefined}>{messageTime(message)}</time>
                    </div>}
                  </div>
                </article>
              ))}
              {pendingProviderActivity && <div className="activity-working provider-working" role="status" aria-live="polite" aria-label="Current activity"><span aria-hidden="true">{PROVIDER_LABELS[selectedProvider] || selectedProvider} yanıtı hazırlıyor…</span><span className="sr-only">{PROVIDER_LABELS[selectedProvider] || selectedProvider} yanıtı hazırlanıyor</span></div>}
            </>}
            {!isEmpty && boundedMessages.length < messages.length && <div className="message-window-note" role="status">Showing the latest {MAX_FALLBACK_MESSAGES} messages to keep this task responsive.</div>}
            {remoteOperations.filter((item) => item.status === 'awaiting_approval').map((item) => <div className="workflow-card remote-operation-card" key={item.id}><div className="workflow-card-head"><div><span className="workflow-kicker">CHAT REMOTE REQUEST</span><strong>{item.kind === 'terminal' ? 'Terminal command approval' : 'File write approval'}</strong></div><span className="workflow-state is-awaiting_approval">awaiting approval</span></div><code>{item.command || item.path}</code><p>This request came from ChatGPT. It will run locally only after you approve it here.</p><div className="workflow-actions"><button type="button" onClick={() => onDecideRemoteOperation(item.id, 'approve')}>Approve & run</button><button type="button" className="is-muted" onClick={() => onDecideRemoteOperation(item.id, 'decline')}>Decline</button></div></div>)}
            <div ref={messagesEndRef} />
          </div>
        </div>
      </div>
      {newActivity && <button type="button" className="activity-new-button" onClick={() => { nearBottomRef.current = true; messagesEndRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' }); setNewActivity(false) }}>New activity ↓</button>}

      <div ref={composerDockRef} className={`composer-dock ${teamMode ? 'is-team' : ''}`}>
        {workflowMode && <div className="workflow-config-shelf" role="region" aria-label="Plan and build model configuration">
          <WorkflowModelPicker role="plan" value={workflowPlanModel} effort={workflowPlanEffort} options={workflowModelOptions} onChange={onWorkflowPlanModelChange} onEffortChange={onWorkflowPlanEffortChange} />
          <span className="workflow-config-arrow" aria-hidden="true">→</span>
          <WorkflowModelPicker role="build" value={workflowBuildModel} effort={workflowBuildEffort} options={workflowModelOptions} onChange={onWorkflowBuildModelChange} onEffortChange={onWorkflowBuildEffortChange} />
          <span className="workflow-help-wrap">
            <button type="button" className="workflow-help-button" aria-label="What is Plan and Build?" aria-expanded={planHelpOpen} onClick={() => setPlanHelpOpen((value) => !value)}>?</button>
            {planHelpOpen && <div className="workflow-help-popover" role="dialog" aria-label="Plan and Build information"><strong>Plan with {workflowPlanLabel}. Build with {workflowBuildLabel}.</strong><p>{workflowPlanLabel} researches the task and creates small, verifiable steps. {workflowBuildLabel} applies one approved step at a time, runs checks, and reports compact evidence for review.</p><small>Usage varies by task. Strong-model usage is reduced by sending only relevant context and evidence.</small></div>}
          </span>
        </div>}
        {teamMode && <TeamComposerDeck agents={teamComposerAgents} onMention={addAgentMention} />}
        <form className="composer-card" onSubmit={(event) => onSubmit(event, attachments.filter((file) => !file.error))}>
          <input ref={attachmentInputRef} className="file-input" type="file" multiple accept="text/*,image/*,.md,.txt,.json,.ts,.tsx,.js,.jsx,.css,.html,.py,.yml,.yaml,.toml,.xml,.csv,.java,.go,.rs,.rb,.php,.sql,.sh" onChange={(event) => { void selectAttachments(event.target.files); event.currentTarget.value = '' }} />
          <div className={`composer-input-layer ${composer ? 'has-value' : ''}`}>
            {composer && <ComposerMentionPreview value={composer} scrollTop={composerScrollTop} agents={teamComposerAgents} />}
            <textarea
              ref={composerRef}
              value={composer}
              disabled={taskLoading}
              onChange={(event) => { onComposerChange(event.target.value); setComposerScrollTop(event.currentTarget.scrollTop) }}
              onScroll={(event) => setComposerScrollTop(event.currentTarget.scrollTop)}
              onKeyDown={(event) => {
                const commandSend = event.metaKey || event.ctrlKey
                if (event.key === 'Enter' && !event.shiftKey && (sendWithEnter || commandSend) && !attachments.some((file) => file.error)) { event.preventDefault(); onSubmit(undefined, attachments.filter((file) => !file.error)) }
              }}
              placeholder={connection === 'offline' ? 'Write your task when Codex reconnects…' : 'Tell Code what to build…'}
              rows={2}
            />
          </div>
          <div className="composer-toolbar">
            <div className="composer-controls">
              <div ref={composerMenuRef} className="composer-plus-menu">
                <button type="button" className={`composer-icon-button ${composerMenuOpen ? 'is-active' : ''}`} aria-label="Attach files" aria-expanded={composerMenuOpen} title="Attach files or open workflow options" disabled={taskLoading} onClick={() => setComposerMenuOpen((value) => !value)}><PlusIcon /></button>
                {composerMenuOpen && <div className="composer-menu-popover" role="menu">
                  <button type="button" role="menuitem" onClick={() => { setComposerMenuOpen(false); attachmentInputRef.current?.click() }}><PaperclipIcon /><span><strong>Attach files</strong><small>Text and images</small></span></button>
                  <button type="button" role="menuitemcheckbox" aria-checked={workflowMode} className={workflowMode ? 'is-selected' : ''} onClick={() => { onToggleWorkflow(); setComposerMenuOpen(false) }}><SparkleIcon /><span><strong>Plan → build</strong><small>Sol plans · Luna builds</small></span>{workflowMode && <CheckCircleIcon weight="fill" />}</button>
                  <button type="button" role="menuitemcheckbox" aria-checked={teamMode} className={teamMode ? 'is-selected is-team' : ''} onClick={() => { onToggleTeam(); setComposerMenuOpen(false) }}><UsersThreeIcon /><span><strong>Team Mode</strong><small>Adrian answers questions and plans project work</small></span>{teamMode && <CheckCircleIcon weight="fill" />}</button>
                </div>}
              </div>
              <PermissionProfilePicker value={permissionProfile} fullAccessConfirmed={fullAccessConfirmed} onChange={onPermissionProfileChange} />
              {!workflowMode && <AppModelPicker models={models} providerModels={providerModels} selectedProvider={selectedProvider} selectedModel={selectedModel} selectedEffort={selectedReasoningEffort} onChange={onModelChange} onEffortChange={onReasoningEffortChange} />}
            </div>
            {running ? (
              <div className="running-actions">
                <button type="button" className="running-action-button" disabled={!composer.trim() || stopping || attachments.some((file) => file.error)} onClick={() => onSteer(composer, attachments.filter((file) => !file.error))}>Steer</button>
                <button type="button" className="running-action-button" disabled={!activeThreadId || !composer.trim() || stopping || attachments.some((file) => file.error)} onClick={() => onQueue(composer, attachments.filter((file) => !file.error))}>Queue{queuedItems.length ? ` (${queuedItems.length})` : ''}</button>
                <button type="button" className="send-button is-stop" onClick={onInterrupt} disabled={stopping || taskLoading} aria-label={stopping ? 'Stopping task' : 'Stop task'} title={stopping ? 'Stopping…' : 'Stop task'}><StopIcon weight="fill" /></button>
              </div>
            ) : (
              <button type="submit" className="send-button" disabled={taskLoading || !composer.trim() || attachments.some((file) => file.error)} aria-label="Send message"><PaperPlaneRightIcon weight="fill" /></button>
            )}
          </div>
          {attachments.length > 0 && <div className="attachment-strip">{attachments.map((file) => <span key={file.id} className={file.error ? 'has-error' : ''}>{file.kind === 'image' && file.dataUrl ? <img src={file.dataUrl} alt="" /> : <FileCodeIcon />}{file.name}{file.error ? <small>{file.error}</small> : null}<button type="button" aria-label={`Remove ${file.name}`} onClick={() => onRemoveAttachment(file.id)}><XIcon /></button></span>)}</div>}
          {queuedItems.length > 0 && <div className="queue-strip" aria-label="Queued messages"><strong>Queued</strong>{queuedItems.slice(0, 5).map((item, index) => <span key={item.id}><b>{index + 1}</b>{item.text.slice(0, 90)}<button type="button" aria-label={`Remove queued message ${index + 1}`} onClick={() => onRemoveQueued(item.id)}><XIcon /></button></span>)}</div>}
        </form>
        <p>Code can make mistakes. Review changes before you apply them.</p>
      </div>
      {remoteOpen && (
        <div className="remote-backdrop" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target) closeRemote() }}>
          <section ref={remoteDialogRef} className="remote-dialog" role="dialog" aria-modal="true" aria-labelledby="remote-title">
            <button className="remote-close" type="button" aria-label="Close Remote" onClick={closeRemote}><XIcon /></button>
            <span className="remote-kicker">CODE REMOTE</span>
            <h2 id="remote-title">Connect your phone</h2>
            {remoteLoading && <p>Preparing a secure pairing code…</p>}
            {remoteError && <><p className="remote-error">{remoteError}</p><small>Start the Code Remote agent, then try again.</small></>}
            {remotePairing && <>
              {remotePairing.qrUrl || remotePairing.qrFallbackUrl ? <img className="remote-qr" src={remotePairing.qrUrl || remotePairing.qrFallbackUrl || undefined} alt="QR code for Code Remote pairing" /> : <div className="remote-qr is-unavailable">QR unavailable</div>}
              <p>Scan this code from your phone, or enter the pairing code in the Code Remote app in ChatGPT.</p>
              {remotePairing.publicGatewayConfigured === false && <p className="remote-warning" role="status">A public HTTPS gateway is not configured. The local QR is available for validation, but a phone outside this computer cannot connect until the published relay URL is configured.</p>}
              <code className="remote-code">{remotePairing.pairCode}</code>
              <button className="remote-copy" type="button" onClick={() => void navigator.clipboard?.writeText(remotePairing.pairCode)}>Copy pairing code</button>
              <small className={remotePairing.expiresAt <= remoteNow ? 'remote-expired' : ''}>{remotePairing.expiresAt <= remoteNow ? 'Pairing expired. Close Remote and generate a new code.' : `Expires in ${Math.ceil((remotePairing.expiresAt - remoteNow) / 1_000)}s (${new Intl.DateTimeFormat('en', { hour: 'numeric', minute: '2-digit' }).format(remotePairing.expiresAt)}).`} Remote tasks still request approval on this desktop.</small>
            </>}
          </section>
        </div>
      )}
    </section>
  )
}

function SettingsView({ settings, models, providers, providerModels, providerLoading, providerError, preflight, connection, version, workspace, accountEmail, planType, quotaUsed, signedIn, loginPending, onChange, onReset, onRefresh, onLogin }: {
  settings: StudioSettings
  models: CodexModel[]
  providers: ProviderDescriptor[]
  providerModels: ProviderModel[]
  providerLoading: boolean
  providerError: string
  preflight: RuntimePreflight | null
  connection: 'checking' | 'ready' | 'offline'
  version?: string
  workspace: string
  accountEmail?: string
  planType?: string | null
  quotaUsed?: number
  signedIn: boolean
  loginPending: boolean
  onChange: (patch: Partial<StudioSettings>) => void
  onReset: () => void
  onRefresh: () => void
  onLogin: () => Promise<void>
}) {
  return (
    <section className="settings-view" aria-label="Settings">
      <header className="settings-header">
        <div><span>CODE WORKSPACE</span><h1>Settings</h1><p>Preferences apply immediately and stay on this {preflight?.platform.name === 'win32' ? 'Windows' : preflight?.platform.name === 'darwin' ? 'Mac' : 'computer'}.</p></div>
        <button type="button" onClick={onReset}>Reset to defaults</button>
      </header>

      <div className="settings-scroll">
        <div className="settings-column">
          <section className="settings-group" aria-labelledby="settings-general">
            <div className="settings-group-heading"><GearSixIcon /><div><h2 id="settings-general">General</h2><p>Choose how Code behaves when you start and write tasks.</p></div></div>
            <SettingRow title="Default model" description="Used for new chats. Auto routes each task to an appropriate model.">
              <label className="settings-select"><span className="sr-only">Default model</span><select value={settings.defaultModel} onChange={(event) => onChange({ defaultModel: event.target.value })}><option value="auto">Auto</option>{models.map((model) => <option key={model.id} value={model.id}>{modelName(model)}</option>)}</select><CaretDownIcon /></label>
            </SettingRow>
            <SettingRow title="Send messages with Enter" description="When off, use Command+Enter to send and Enter to add a new line.">
              <Switch checked={settings.sendWithEnter} label="Send messages with Enter" onChange={(checked) => onChange({ sendWithEnter: checked })} />
            </SettingRow>
            <SettingRow title="Suggested tasks" description="Show useful starting points before the first message.">
              <Switch checked={settings.showSuggestions} label="Suggested tasks" onChange={(checked) => onChange({ showSuggestions: checked })} />
            </SettingRow>
          </section>

          <section className="settings-group" aria-labelledby="settings-project-defaults">
            <div className="settings-group-heading"><FolderOpenIcon /><div><h2 id="settings-project-defaults">Project defaults</h2><p>Choose the starting environment for new tasks in this workspace.</p></div></div>
            <SettingRow title="Default environment" description="Local uses the selected folder; worktree is unavailable until isolated worktrees are provisioned.">
              <label className="settings-select"><span className="sr-only">Default environment</span><select value={settings.defaultEnvironment} onChange={(event) => onChange({ defaultEnvironment: event.target.value as StudioSettings['defaultEnvironment'] })}><option value="local">Local workspace</option><option value="worktree" disabled>Worktree (unavailable)</option></select><CaretDownIcon /></label>
            </SettingRow>
            <SettingRow title="Default branch" description="Used as a label for future project actions; it never changes Git without an explicit action.">
              <input className="settings-text-input" value={settings.defaultBranch} onChange={(event) => onChange({ defaultBranch: event.target.value.replace(/[^A-Za-z0-9._/-]/g, '').slice(0, 120) })} aria-label="Default branch" />
            </SettingRow>
          </section>

          <section className="settings-group" aria-labelledby="settings-terminal">
            <div className="settings-group-heading"><TerminalWindowIcon /><div><h2 id="settings-terminal">Terminal and notifications</h2><p>Keep command execution local and control how status reaches you.</p></div></div>
            <SettingRow title="Terminal shell" description="System follows the platform default and keeps shell selection explicit.">
              <label className="settings-select"><span className="sr-only">Terminal shell</span><select value={settings.terminalShell} onChange={(event) => onChange({ terminalShell: event.target.value as StudioSettings['terminalShell'] })}><option value="system">System default</option><option value="zsh">zsh (Mac/Linux)</option><option value="bash">bash (Mac/Linux)</option><option value="powershell">PowerShell (Windows)</option></select><CaretDownIcon /></label>
            </SettingRow>
            <SettingRow title="Desktop notifications" description="Show completion and approval notifications when the app is not focused.">
              <Switch checked={settings.notifications} label="Desktop notifications" onChange={(checked) => onChange({ notifications: checked })} />
            </SettingRow>
          </section>

          <section className="settings-group" aria-labelledby="settings-context">
            <div className="settings-group-heading"><BrainIcon /><div><h2 id="settings-context">Context and memory</h2><p>Control how project context is prepared before a task starts.</p></div></div>
            <SettingRow title="Use project memory" description="Retrieve only relevant indexed project context instead of rereading the entire repository.">
              <Switch checked={settings.memoryContext} label="Use project memory" onChange={(checked) => onChange({ memoryContext: checked })} />
            </SettingRow>
            <SettingRow title="Permission profile" description="Applies to new tasks. Existing threads keep the policy they started with.">
              <label className="settings-select"><span className="sr-only">Permission profile</span><select value={settings.permissionProfile} onChange={(event) => {
                const next = event.target.value as StudioSettings['permissionProfile']
                if (next === 'full-access' && !settings.fullAccessConfirmed && !window.confirm('Full access disables per-action approvals and can modify files or run commands. Enable it for new tasks?')) return
                onChange({ permissionProfile: next, fullAccessConfirmed: next === 'full-access' })
              }}><option value="read-only">Read only</option><option value="workspace-auto">Workspace auto</option><option value="granular">Granular approvals</option><option value="full-access">Full access</option></select><CaretDownIcon /></label>
            </SettingRow>
            <SettingRow title="Approval reviewer" description="Automatic review is feature-gated until the installed App Server exposes its reviewer lifecycle.">
              <label className="settings-select"><span className="sr-only">Approval reviewer</span><select value="user" aria-label="Approval reviewer" disabled><option value="user">User</option><option value="auto">Auto review (unavailable)</option></select><CaretDownIcon /></label>
            </SettingRow>
            <div className={`security-profile-note is-${settings.permissionProfile}`}><CheckCircleIcon weight="fill" /><span><strong>{settings.permissionProfile === 'full-access' ? 'Full access is enabled for new tasks.' : 'Policy is active for new tasks.'}</strong> {settings.permissionProfile === 'full-access' ? 'You can revoke it here at any time.' : 'Requests remain bound to their thread, turn, and approval session.'}</span></div>
          </section>

          <section className="settings-group" aria-labelledby="settings-providers">
            <div className="settings-group-heading"><SparkleIcon /><div><h2 id="settings-providers">Providers</h2><p>Use only providers that report a real local connection. Code never asks for provider passwords or tokens.</p></div></div>
            {providerError && <div className="provider-inline-error" role="alert">{providerError}</div>}
            {providerLoading && !providers.length && <div className="provider-loading"><CircleNotchIcon className="spin" /> Checking local providers…</div>}
            {!providerLoading && !providers.length && !providerError && <div className="provider-empty">Provider status is available when the local App Server is connected.</div>}
            {providers.length > 0 && <div className="provider-grid">
              {providers.map((provider) => {
                const state = provider.status.state
                const modelCount = providerModels.filter((model) => model.providerId === provider.id && model.available).length
                const isAntigravity = provider.id === 'antigravity-local'
                return <article className={`provider-card is-${state}`} key={provider.id}>
                  <div className="provider-card-header">
                    <span className="provider-card-icon">{isAntigravity ? <SparkleIcon /> : <TerminalWindowIcon />}</span>
                    <div><strong>{provider.displayName}</strong><small>{provider.kind}</small></div>
                    <span className={`provider-state is-${state}`}><i />{PROVIDER_STATE_LABELS[state] || state}</span>
                  </div>
                  <p className="provider-message">{provider.status.message}</p>
                  {provider.status.warning && <p className="provider-warning" role="status">Warning: {provider.status.warning}</p>}
                  <dl className="provider-meta"><div><dt>Version</dt><dd>{provider.status.version || '—'}</dd></div><div><dt>Models</dt><dd>{modelCount || (isAntigravity ? 'Not discovered' : 'Available from Codex')}</dd></div><div><dt>Checked</dt><dd>{new Intl.DateTimeFormat('en', { hour: 'numeric', minute: '2-digit' }).format(new Date(provider.status.checkedAt))}</dd></div></dl>
                  <div className="provider-actions">
                    <button type="button" onClick={onRefresh} disabled={providerLoading}>{providerLoading ? 'Checking…' : 'Refresh'}</button>
                    {isAntigravity
                      ? <button type="button" onClick={() => window.open('https://developers.google.com/', '_blank', 'noopener,noreferrer')}>{state === 'ready' ? 'Help' : state === 'needs_login' ? 'Sign-in guide' : 'Installation help'}</button>
                      : <button type="button" onClick={() => window.alert('Codex account disconnect is managed by the local App Server. Use its account controls to sign out safely.')}>Disconnect help</button>}
                  </div>
                </article>
              })}
            </div>}
          </section>

          <section className="settings-group" aria-labelledby="settings-appearance">
            <div className="settings-group-heading"><SparkleIcon /><div><h2 id="settings-appearance">Appearance</h2><p>Keep the workspace dense enough for real development work.</p></div></div>
            <SettingRow title="Theme" description="Choose the workspace surface. System follows the operating system preference.">
              <div className="settings-segmented" role="group" aria-label="Theme"><button type="button" className={settings.theme === 'dark' ? 'is-active' : ''} onClick={() => onChange({ theme: 'dark' })}>Dark</button><button type="button" className={settings.theme === 'light' ? 'is-active' : ''} onClick={() => onChange({ theme: 'light' })}>Light</button><button type="button" className={settings.theme === 'system' ? 'is-active' : ''} onClick={() => onChange({ theme: 'system' })}>System</button></div>
            </SettingRow>
            <SettingRow title="Interface density" description="Compact matches Codex more closely; Comfortable adds extra spacing.">
              <div className="settings-segmented" role="group" aria-label="Interface density"><button type="button" className={settings.density === 'compact' ? 'is-active' : ''} onClick={() => onChange({ density: 'compact' })}>Compact</button><button type="button" className={settings.density === 'comfortable' ? 'is-active' : ''} onClick={() => onChange({ density: 'comfortable' })}>Comfortable</button></div>
            </SettingRow>
            <SettingRow title="Reduce motion" description="Disable non-essential transitions and animations.">
              <Switch checked={settings.reduceMotion} label="Reduce motion" onChange={(checked) => onChange({ reduceMotion: checked })} />
            </SettingRow>
          </section>

          <section className="settings-group" aria-labelledby="settings-updates-data">
            <div className="settings-group-heading"><ArrowClockwiseIcon /><div><h2 id="settings-updates-data">Updates, logs and data</h2><p>Control update checks and local diagnostic retention. Credentials are never included.</p></div></div>
            <SettingRow title="Automatic updates" description="Check for application updates when Code starts; installation remains explicit.">
              <Switch checked={settings.autoUpdate} label="Automatic updates" onChange={(checked) => onChange({ autoUpdate: checked })} />
            </SettingRow>
            <SettingRow title="Diagnostic reports" description="Allow bounded, redacted diagnostics for troubleshooting. Disabled by default.">
              <Switch checked={settings.diagnosticsEnabled} label="Diagnostic reports" onChange={(checked) => onChange({ diagnosticsEnabled: checked })} />
            </SettingRow>
            <SettingRow title="Log retention" description="Local logs are capped and stored only on this device.">
              <label className="settings-select"><span className="sr-only">Log retention</span><select value={settings.logRetention} onChange={(event) => onChange({ logRetention: event.target.value as StudioSettings['logRetention'] })}><option value="session">This session</option><option value="30-days">30 days</option><option value="forever">Until deleted</option></select><CaretDownIcon /></label>
            </SettingRow>
          </section>

          <section className="settings-group" aria-labelledby="settings-accessibility-remote">
            <div className="settings-group-heading"><SparkleIcon /><div><h2 id="settings-accessibility-remote">Accessibility and Remote</h2><p>Keep announcements available for assistive technology and control the paired-device surface.</p></div></div>
            <SettingRow title="Screen-reader announcements" description="Announce task state changes and new assistant output through live regions.">
              <Switch checked={settings.screenReaderAnnouncements} label="Screen-reader announcements" onChange={(checked) => onChange({ screenReaderAnnouncements: checked })} />
            </SettingRow>
            <SettingRow title="Remote devices" description="Show the Remote control and allow the local agent to maintain its outbound connection.">
              <Switch checked={settings.remoteEnabled} label="Remote devices" onChange={(checked) => onChange({ remoteEnabled: checked })} />
            </SettingRow>
            <div className="settings-inline-note"><CheckCircleIcon weight="fill" /><span>Remote still requires a short-lived pairing code and keeps dangerous terminal/file actions approval-gated on this desktop.</span></div>
          </section>

          <section className="settings-group" aria-labelledby="settings-account">
            <div className="settings-group-heading"><TerminalWindowIcon /><div><h2 id="settings-account">Account and runtime</h2><p>Live status from the local Codex App Server.</p></div></div>
            <div className="runtime-card">
              <span className={`runtime-state is-${connection}`}><i />{connection === 'ready' ? 'Connected' : connection === 'checking' ? 'Checking' : 'Offline'}</span>
              <dl><div><dt>Account</dt><dd>{signedIn ? accountEmail || 'ChatGPT connected' : 'Not signed in'}</dd></div><div><dt>Plan</dt><dd>{planType || '—'}</dd></div><div><dt>Usage</dt><dd>{typeof quotaUsed === 'number' ? `${Math.round(quotaUsed)}%` : '—'}</dd></div><div><dt>Codex</dt><dd>{version || 'Unknown'}</dd></div><div><dt>Workspace</dt><dd>{workspace}</dd></div></dl>
              <div className="runtime-actions"><button type="button" onClick={onRefresh}>Refresh status</button>{!signedIn && <button type="button" onClick={() => void onLogin()} disabled={loginPending}>{loginPending ? 'Connecting…' : 'Connect ChatGPT'}</button>}</div>
            </div>
            {preflight && <div className="preflight-card">
              <div className="preflight-heading"><strong>{preflight.ok ? 'Runtime preflight passed' : 'Runtime preflight needs attention'}</strong><span className={preflight.ok ? 'is-good' : 'is-warn'}>{preflight.ok ? 'READY' : 'CHECK'}</span></div>
              <p>Loopback bridge, workspace permissions and credential boundaries are checked locally. Antigravity is optional and is never bundled.</p>
              <div className="preflight-badges"><span>{preflight.platform.name} · {preflight.platform.arch}</span><span>{preflight.workspace.writable ? 'Workspace writable' : 'Workspace access needs attention'}</span><span>{preflight.security.loopbackOnly && preflight.security.credentialsInApp === false ? 'Local security checks on' : 'Security checks incomplete'}</span></div>
            </div>}
          </section>
        </div>
      </div>
    </section>
  )
}

function SettingRow({ title, description, children }: { title: string; description: string; children: React.ReactNode }) {
  return <div className="setting-row"><div><strong>{title}</strong><p>{description}</p></div><div className="setting-control">{children}</div></div>
}

function Switch({ checked, label, onChange }: { checked: boolean; label: string; onChange: (checked: boolean) => void }) {
  return <button type="button" role="switch" aria-checked={checked} aria-label={label} className={`settings-switch ${checked ? 'is-on' : ''}`} onClick={() => onChange(!checked)}><span /></button>
}

function MemoryView({ project, onAsk }: { project: StudioProject; onAsk: (artifact: MemoryArtifact, context?: string) => void }) {
  const [index, setIndex] = useState<Awaited<ReturnType<typeof codexApi.memoryIndex>> | null>(null)
  const [health, setHealth] = useState<MemoryHealth | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const requestIdRef = useRef(0)

  const load = useCallback(async (force = false) => {
    const requestId = ++requestIdRef.current
    setLoading(true)
    setError('')
    try {
      const next = await codexApi.memoryIndex(project.cwd, force)
      if (requestId !== requestIdRef.current) return
      setIndex(next)
      const nextHealth = next.health ?? await codexApi.memoryHealth(project.cwd).catch(() => null)
      if (requestId !== requestIdRef.current) return
      setHealth(nextHealth)
    } catch (cause) {
      if (requestId !== requestIdRef.current) return
      setError(cause instanceof Error ? cause.message : 'The memory index could not be loaded.')
    } finally {
      if (requestId === requestIdRef.current) setLoading(false)
    }
  }, [project.cwd])

  useEffect(() => { void load() }, [load])

  const universe = index?.universe ?? EMPTY_MEMORY
  const repository = useMemo(() => createUniverseIndex(universe), [universe])
  const matches = useMemo(() => (query.trim() ? searchArtifacts(universe, repository, query, 220) : universe.artifacts.slice(0, 220)), [query, repository, universe])
  const selected = selectedId ? repository.getArtifact(selectedId) ?? null : null
  const related = useMemo(() => selected
    ? repository.getRelationsFor(selected.id).slice(0, 8).map((relation) => {
      const otherId = relation.sourceId === selected.id ? relation.targetId : relation.sourceId
      return { relation, artifact: repository.getArtifact(otherId) }
    }).filter((item): item is { relation: (typeof universe.relations)[number]; artifact: MemoryArtifact } => Boolean(item.artifact))
    : [], [repository, selected, universe.relations])
  const deleteSelected = async () => {
    if (!selected || !['conversation', 'provider', 'task'].includes(selected.sourceKind ?? '')) return
    if (!window.confirm('Delete this captured memory?')) return
    try {
      await codexApi.deleteMemoryArtifact(project.cwd, selected.id)
      setSelectedId(null)
      await load(true)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The memory could not be deleted.')
    }
  }
  const groups = useMemo(() => {
    const counts = new Map<string, number>()
    for (const artifact of universe.artifacts) counts.set(artifact.type, (counts.get(artifact.type) ?? 0) + 1)
    return ['Code', 'Decision', 'Error', 'Conversation', 'Test', 'Document'].map((type) => ({ type, count: counts.get(type) ?? 0 })).filter((item) => item.count)
  }, [universe.artifacts])

  return (
    <section className="memory-palace-view" aria-label="Memory">
      <header className="content-header">
        <div className="content-title-row"><span className="content-icon"><BrainIcon /></span><div><span className="content-kicker">PROJECT CONTEXT</span><h1 aria-label="Memory Palace">Memory</h1></div></div>
        <div className="content-header-actions"><span className={`memory-status ${health?.status === 'stale' || (health?.conflictCount ?? 0) > 0 ? 'is-warn' : index ? 'is-ready' : ''}`}>{loading ? 'Indexing…' : health?.status === 'stale' ? `${health.staleCount} stale` : (health?.conflictCount ?? 0) > 0 ? `${health?.conflictCount} conflicts` : index?.source === 'cache' ? 'Local cache' : 'Live index'}</span><button type="button" className="subtle-button" onClick={() => void load(true)} disabled={loading}><ClockCounterClockwiseIcon /> Reindex</button></div>
      </header>
      <div className="memory-palace-layout">
        <aside className="memory-palace-sidebar">
          <div className="memory-sidebar-heading"><strong>Index</strong><span>{universe.artifacts.length} records</span></div>
          <dl className="memory-metrics"><div><dt>Files</dt><dd>{index?.stats.files ?? '—'}</dd></div><div><dt>Links</dt><dd>{index?.stats.relations ?? '—'}</dd></div><div><dt>Chats</dt><dd>{index?.stats.conversations ?? '—'}</dd></div><div><dt>Stale</dt><dd>{health?.staleCount ?? 0}</dd></div><div><dt>Conflicts</dt><dd>{health?.conflictCount ?? index?.stats.conflicts ?? 0}</dd></div></dl>
          <div className="memory-type-list">{groups.map((group) => <span key={group.type}><i className={`type-dot is-${group.type.toLowerCase()}`} />{group.type}<strong>{group.count}</strong></span>)}</div>
          <small>Only relevant records are added to a task. Secrets, dependencies and excluded files are never indexed.</small>
          {index && <div className="memory-exclusion-proof" aria-label="Memory index exclusions"><span><strong>{index.stats.excludedSecrets ?? 0}</strong> secrets excluded</span><span><strong>{index.stats.excludedDependencies ?? 0}</strong> dependency folders skipped</span></div>}
        </aside>
        <div className="memory-records">
          <label className="memory-search"><MagnifyingGlassIcon /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search files, errors, decisions, or conversations…" /></label>
          {error && <div className="memory-error" role="alert">{error}</div>}
          {!loading && !error && !matches.length && <div className="memory-empty">No indexed records yet.</div>}
          <div className="memory-record-list">
            {matches.map((artifact) => <button type="button" key={artifact.id} className={selectedId === artifact.id ? 'is-active' : ''} onClick={() => setSelectedId(artifact.id)}><span className={`artifact-type type-${artifact.type.toLowerCase()}`}>{artifact.type}</span><span><strong>{artifact.title}</strong><small>{artifact.path}</small></span><time>{Math.round(artifact.importance * 100)}%</time></button>)}
          </div>
        </div>
        <aside className="memory-detail" aria-live="polite">
          {selected ? <><span className={`artifact-type type-${selected.type.toLowerCase()}`}>{selected.type}</span><h2>{selected.title}</h2><p>{selected.content}</p><dl><div><dt>Source</dt><dd>{selected.sourcePath ?? selected.path}</dd></div><div><dt>Freshness</dt><dd>{selected.freshness ?? 'fresh'}</dd></div><div><dt>Relations</dt><dd>{repository.getRelationsFor(selected.id).length} connected records</dd></div></dl>{related.length > 0 && <div className="memory-related"><span className="memory-section-label">Connected records</span>{related.map(({ relation, artifact }) => <button type="button" key={relation.id} onClick={() => setSelectedId(artifact.id)}><span>{relation.type.replace('_', ' ')}</span><strong>{artifact.title}</strong></button>)}</div>}<div className="drawer-tags">{selected.tags.map((tag) => <span key={tag}>#{tag}</span>)}</div><div className="memory-detail-actions"><button className="drawer-primary" type="button" onClick={() => onAsk(selected, [`Memory context: ${selected.title}`, `Type: ${selected.type}`, `Source: ${selected.sourcePath ?? selected.path}`, `Content: ${selected.content.slice(0, 3_000)}`, related.length ? `Connected records: ${related.map(({ relation, artifact }) => `${relation.type} → ${artifact.title}`).join('; ')}` : ''].filter(Boolean).join('\n'))}><ChatCircleDotsIcon /> Ask with this context</button>{['conversation', 'provider', 'task'].includes(selected.sourceKind ?? '') && <button className="drawer-danger" type="button" onClick={() => void deleteSelected()}>Delete memory</button>}</div></> : <div className="memory-detail-empty"><BrainIcon /><strong>Select a memory</strong><span>Its source, relations and concise indexed context will appear here.</span></div>}
        </aside>
      </div>
    </section>
  )
}

function FilesView({ project }: { project: StudioProject }) {
  const [files, setFiles] = useState<ProjectFile[]>([])
  const [preview, setPreview] = useState<ProjectFilePreview | null>(null)
  const [selectedPath, setSelectedPath] = useState<string | null>(null)
  const [collapsedDirs, setCollapsedDirs] = useState<Set<string>>(new Set())
  const [fileSearch, setFileSearch] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const previewRequestRef = useRef(0)

  useEffect(() => {
    previewRequestRef.current += 1
    let cancelled = false
    setLoading(true)
    setError('')
    setPreview(null)
    setSelectedPath(null)
    setCollapsedDirs(new Set())
    codexApi.projectFiles(project.cwd)
      .then((result) => { if (!cancelled) setFiles(result.files) })
      .catch((cause) => { if (!cancelled) setError(cause instanceof Error ? cause.message : 'Project files could not be loaded.') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [project.cwd])

  const openFile = async (file: ProjectFile) => {
    if (file.type !== 'file') return
    const requestId = ++previewRequestRef.current
    setSelectedPath(file.path)
    setError('')
    try {
      const next = await codexApi.projectFile(project.cwd, file.path)
      if (requestId === previewRequestRef.current) setPreview(next)
    } catch (cause) {
      if (requestId === previewRequestRef.current) {
        setPreview(null)
        setError(cause instanceof Error ? cause.message : 'File preview is unavailable.')
      }
    }
  }

  const visibleFiles = useMemo(() => {
    const query = fileSearch.trim().toLocaleLowerCase('tr-TR')
    return files.filter((file) => {
      if (query && !`${file.path} ${file.name}`.toLocaleLowerCase('tr-TR').includes(query)) return false
      const segments = file.path.split('/')
      return !segments.slice(0, -1).some((_, index) => collapsedDirs.has(segments.slice(0, index + 1).join('/')))
    }).slice(0, 500)
  }, [collapsedDirs, fileSearch, files])

  const toggleDirectory = (path: string) => {
    setCollapsedDirs((current) => {
      const next = new Set(current)
      if (next.has(path)) next.delete(path)
      else next.add(path)
      return next
    })
  }

  return (
    <section className="utility-view project-files-view">
      <header className="content-header">
        <div className="content-title-row"><span className="content-icon"><FolderOpenIcon /></span><div><span className="content-kicker">PROJECT EXPLORER</span><h1>{project.name}</h1></div></div>
        <span className="file-count">{loading ? 'Loading…' : `${files.length} entries`}</span>
      </header>
      <div className="project-files-layout">
        <aside className="project-file-tree">
          <div className="project-path"><FolderOpenIcon /><span>{project.cwd}</span></div>
          <label className="file-tree-search"><MagnifyingGlassIcon /><input value={fileSearch} onChange={(event) => setFileSearch(event.target.value)} placeholder="Filter files" aria-label="Filter files" /></label>
          {error && !files.length && <p className="file-error">{error}</p>}
          {!loading && !error && !files.length && <p className="file-empty">No project files were found.</p>}
          <div className="project-file-list" role="tree" aria-label="Project files">
            {visibleFiles.map((file, index) => <button type="button" key={file.path} aria-expanded={file.type === 'directory' ? !collapsedDirs.has(file.path) : undefined} data-tree-level={file.depth + 1} data-tree-position={`${index + 1}/${visibleFiles.length}`} className={selectedPath === file.path ? 'is-active' : ''} style={{ '--file-depth': file.depth } as React.CSSProperties} onClick={() => file.type === 'directory' ? toggleDirectory(file.path) : void openFile(file)}><span className={`file-kind is-${file.type}`}>{file.type === 'directory' ? <FolderOpenIcon /> : <FileCodeIcon />}</span><span>{file.name}</span>{file.type === 'file' && <small>{formatFileSize(file.size)}</small>}</button>)}
            {files.length > visibleFiles.length && <small className="file-tree-limit">Showing the first 500 matching entries.</small>}
          </div>
        </aside>
        <div className="project-file-preview">
          {preview ? <><header><div><strong>{preview.path}</strong><small>{formatFileSize(preview.size)} · {new Date(preview.modifiedAt).toLocaleString()}</small></div></header><pre><code>{preview.content}</code></pre></> : <div className="file-preview-empty"><FileCodeIcon /><strong>Select a file to preview</strong><span>Text files up to 240 KB can be inspected here. Excluded secrets and dependencies never appear in the browser.</span></div>}
          {error && files.length > 0 && <p className="file-preview-error">{error}</p>}
        </div>
      </div>
    </section>
  )
}

function formatFileSize(value: number | null) {
  if (value == null) return ''
  if (value < 1024) return `${value} B`
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`
  return `${(value / (1024 * 1024)).toFixed(1)} MB`
}

function ChangesView({ project, connection, activeThreadId }: { project: StudioProject; connection: 'checking' | 'ready' | 'offline'; activeThreadId: string | null }) {
  const [changes, setChanges] = useState<ProjectChange[]>([])
  const [repository, setRepository] = useState<boolean | null>(null)
  const [selectedPaths, setSelectedPaths] = useState<string[]>([])
  const [selectedPath, setSelectedPath] = useState<string | null>(null)
  const [diff, setDiff] = useState<ProjectDiff | null>(null)
  const [tab, setTab] = useState<'changes' | 'review'>('changes')
  const [reviewType, setReviewType] = useState<'uncommittedChanges' | 'baseBranch' | 'commit' | 'custom'>('uncommittedChanges')
  const [reviewValue, setReviewValue] = useState('')
  const [reviewLoading, setReviewLoading] = useState(false)
  const [lineWrap, setLineWrap] = useState(false)
  const [diffSearch, setDiffSearch] = useState('')
  const [commitMessage, setCommitMessage] = useState('')
  const [message, setMessage] = useState('')
  const [actionPending, setActionPending] = useState<'stage' | 'unstage' | 'revert' | 'commit' | null>(null)
  const [loading, setLoading] = useState(true)
  const loadChanges = useCallback(async () => {
    setLoading(true)
    setMessage('')
    try {
      const result = await codexApi.projectChanges(project.cwd)
      setChanges(result.changes)
      setRepository(result.repository)
      setSelectedPaths((current) => current.filter((path) => result.changes.some((change) => change.path === path)))
      setSelectedPath((current) => current && result.changes.some((change) => change.path === current) ? current : result.changes[0]?.path ?? null)
    } catch (error) { setChanges([]); setRepository(false); setMessage(error instanceof Error ? error.message : 'Git status could not be loaded.') }
    finally { setLoading(false) }
  }, [project.cwd])
  useEffect(() => { void loadChanges() }, [loadChanges])
  useEffect(() => {
    if (!selectedPath || !repository) { setDiff(null); return }
    let cancelled = false
    codexApi.projectDiff(project.cwd, selectedPath).then((result) => { if (!cancelled) setDiff(result) }).catch(() => { if (!cancelled) setDiff(null) })
    return () => { cancelled = true }
  }, [project.cwd, repository, selectedPath])

  const runAction = async (action: 'stage' | 'unstage' | 'revert' | 'commit') => {
    if (actionPending) return
    if (!selectedPaths.length && action !== 'commit') return
    if (action === 'revert' && !window.confirm('Revert the selected working-tree changes? This cannot be undone.')) return
    if (action === 'commit' && (!commitMessage.trim() || !window.confirm(`Create commit “${commitMessage.trim()}”?`))) return
    setActionPending(action)
    setMessage(`${action === 'revert' ? 'Reverting' : action === 'commit' ? 'Creating commit' : action === 'stage' ? 'Staging' : 'Unstaging'}…`)
    try {
      await codexApi.gitAction({ cwd: project.cwd, action, paths: selectedPaths, message: commitMessage.trim(), confirm: action === 'revert' || action === 'commit' })
      setCommitMessage('')
      setSelectedPaths([])
      setMessage(action === 'commit' ? 'Commit created.' : `Git ${action} completed.`)
      await loadChanges()
    } catch (error) { setMessage(error instanceof Error ? error.message : 'Git action failed.') }
    finally { setActionPending(null) }
  }

  const startReview = async () => {
    if (!activeThreadId) return
    setReviewLoading(true); setMessage('')
    try {
      const target = reviewType === 'uncommittedChanges' ? { type: reviewType } : reviewType === 'baseBranch' ? { type: reviewType, branch: reviewValue } : reviewType === 'commit' ? { type: reviewType, sha: reviewValue } : { type: reviewType, instruction: reviewValue }
      const result = await codexApi.startReview(activeThreadId, target)
      setMessage(result.reviewThreadId && result.reviewThreadId !== activeThreadId ? 'Detached review started in a new thread.' : 'Review started in this task.')
    } catch (error) { setMessage(error instanceof Error ? error.message : 'Review could not be started.') }
    finally { setReviewLoading(false) }
  }

  const visibleDiff = diffSearch.trim() ? diff?.diff.split('\n').filter((line) => line.toLocaleLowerCase().includes(diffSearch.toLocaleLowerCase())).join('\n') : diff?.diff

  return (
    <section className="utility-view">
      <header className="content-header"><div className="content-title-row"><span className="content-icon"><GitBranchIcon /></span><div><span className="content-kicker">SOURCE CONTROL</span><h1>Changes</h1></div></div><div className="content-header-actions"><button className={`subtle-button ${tab === 'changes' ? 'is-active' : ''}`} type="button" onClick={() => setTab('changes')}>Files & diff</button><button className={`subtle-button ${tab === 'review' ? 'is-active' : ''}`} type="button" onClick={() => setTab('review')}>Code review</button><button className="subtle-button" type="button" onClick={() => void loadChanges()} disabled={loading}><ClockCounterClockwiseIcon /> Refresh</button></div></header>
      <div className="changes-content">
        {loading && <div className="changes-state"><CircleNotchIcon className="spin" /><strong>Reading Git status…</strong></div>}
        {!loading && repository === false && <div className="changes-state"><GitBranchIcon /><strong>This workspace is not a Git repository.</strong><span>{project.cwd}</span></div>}
        {!loading && repository && tab === 'review' && <div className="review-panel"><span className="content-kicker">CODEX REVIEW</span><h2>Inspect the current work</h2><p>Review runs through the official App Server and streams findings into the active task timeline.</p><label>Target<select value={reviewType} onChange={(event) => setReviewType(event.target.value as typeof reviewType)}><option value="uncommittedChanges">Uncommitted changes</option><option value="baseBranch">Base branch</option><option value="commit">Commit</option><option value="custom">Custom instructions</option></select></label>{reviewType !== 'uncommittedChanges' && <input value={reviewValue} onChange={(event) => setReviewValue(event.target.value)} placeholder={reviewType === 'baseBranch' ? 'main' : reviewType === 'commit' ? 'commit SHA' : 'Focus the review on…'} />}{message && <p className="changes-message">{message}</p>}<button className="drawer-primary" type="button" disabled={!activeThreadId || reviewLoading || (reviewType !== 'uncommittedChanges' && !reviewValue.trim())} onClick={() => void startReview()}>{reviewLoading ? <CircleNotchIcon className="spin" /> : <GitBranchIcon />} {activeThreadId ? 'Start review' : 'Select a task first'}</button></div>}
        {!loading && repository && tab === 'changes' && !changes.length && <div className="changes-state is-clean"><CheckCircleIcon weight="fill" /><strong>Working tree clean</strong><span>No modified or untracked files.</span></div>}
        {!loading && repository && tab === 'changes' && changes.length > 0 && <div className="changes-workbench"><div className="changes-list"><div className="changes-summary"><span>{changes.length} changed {changes.length === 1 ? 'file' : 'files'}</span><small>{connection === 'ready' ? 'Live workspace' : 'Local Git status'}</small></div>{changes.map((change) => <button className={`change-row ${selectedPath === change.path ? 'is-selected' : ''}`} type="button" key={`${change.status}-${change.path}`} onClick={() => setSelectedPath(change.path)}><input type="checkbox" checked={selectedPaths.includes(change.path)} onChange={(event) => { event.stopPropagation(); setSelectedPaths((current) => event.target.checked ? [...current, change.path] : current.filter((path) => path !== change.path)) }} onClick={(event) => event.stopPropagation()} /><span className={`change-status is-${change.status.trim().toLowerCase() || 'unknown'}`}>{change.status.trim() || '?'}</span><strong>{change.path}</strong></button>)}</div><div className="diff-inspector"><div className="diff-toolbar"><strong>{selectedPath || 'Select a file'}</strong><label><input placeholder="Search diff" value={diffSearch} onChange={(event) => setDiffSearch(event.target.value)} /></label><button type="button" onClick={() => setLineWrap((value) => !value)}>{lineWrap ? 'No wrap' : 'Wrap lines'}</button></div>{diff?.binary ? <div className="diff-empty">Binary file — preview unavailable.</div> : <pre className={lineWrap ? 'is-wrapped' : ''}>{visibleDiff || 'No diff available for this file.'}</pre>}</div></div>}
        {!loading && repository && tab === 'changes' && changes.length > 0 && <div className="git-actions" aria-busy={Boolean(actionPending)}><button type="button" onClick={() => void runAction('stage')} disabled={!selectedPaths.length || Boolean(actionPending)}>{actionPending === 'stage' ? 'Staging…' : 'Stage selected'}</button><button type="button" onClick={() => void runAction('unstage')} disabled={!selectedPaths.length || Boolean(actionPending)}>{actionPending === 'unstage' ? 'Unstaging…' : 'Unstage'}</button><button type="button" className="is-danger" onClick={() => void runAction('revert')} disabled={!selectedPaths.length || Boolean(actionPending)}>{actionPending === 'revert' ? 'Reverting…' : 'Revert selected'}</button><input value={commitMessage} onChange={(event) => setCommitMessage(event.target.value)} placeholder="Commit message" disabled={Boolean(actionPending)} /><button type="button" onClick={() => void runAction('commit')} disabled={!commitMessage.trim() || !selectedPaths.length || Boolean(actionPending)}>{actionPending === 'commit' ? 'Committing…' : 'Commit selected'}</button></div>}
        {message && tab === 'changes' && <p className="changes-message">{message}</p>}
      </div>
    </section>
  )
}

function BrowserView({ activeTaskId, capabilities, sessions, url, error, onUrlChange, onRefresh, onOpen, onNavigate, onScreenshot, onStop }: { activeTaskId: string | null; capabilities: BrowserCapabilities | null; sessions: BrowserSession[]; url: string; error: string; onUrlChange: (value: string) => void; onRefresh: () => Promise<void>; onOpen: () => Promise<void>; onNavigate: (id: string, url: string) => Promise<void>; onScreenshot: (id: string) => Promise<void>; onStop: (id: string) => Promise<void> }) {
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [busyAction, setBusyAction] = useState<string | null>(null)
  const browser = capabilities?.browser
  const scopedSessions = activeTaskId ? sessions.filter((session) => !session.taskId || session.taskId === activeTaskId) : []
  const runAction = async (key: string, action: () => Promise<void>) => {
    if (busyAction) return
    setBusyAction(key)
    try { await action() } catch { /* The parent action owns the user-facing error state. */ } finally { setBusyAction(null) }
  }
  return <section className="workspace-view browser-view" aria-label="Browser workspace">
    <header className="workspace-view-header"><div><span className="view-kicker">VERIFIED CAPABILITIES</span><h1>Browser</h1><p>Browser actions stay visible in the task timeline and every screenshot is retained as evidence.</p></div><div className="browser-header-actions"><span className={`capability-pill is-${browser?.available ? 'ready' : 'unavailable'}`}>{browser?.available ? 'Ready' : 'Unavailable'}</span><button className="subtle-button" type="button" onClick={() => void runAction('refresh', onRefresh)} disabled={busyAction === 'refresh'}>{busyAction === 'refresh' ? <CircleNotchIcon className="spin" /> : <ClockCounterClockwiseIcon />} Refresh</button></div></header>
    {!activeTaskId && <div className="capability-empty"><GlobeHemisphereWestIcon /><strong>Select a task to scope browser actions.</strong><span>Tabs, navigation, screenshots, and evidence stay isolated to the active task.</span></div>}
    {activeTaskId && !browser?.available && <div className="capability-empty"><GlobeHemisphereWestIcon /><strong>No verified browser adapter is installed.</strong><span>{browser?.reason || 'This build will not claim browser or computer control until an installed adapter is detected.'}</span></div>}
    {activeTaskId && browser?.available && <div className="browser-open-bar"><input value={url} onChange={(event) => onUrlChange(event.target.value)} aria-label="Browser URL" placeholder="https://example.com" disabled={Boolean(busyAction)} /><button type="button" onClick={() => void runAction('open', onOpen)} disabled={Boolean(busyAction)}>{busyAction === 'open' ? <><CircleNotchIcon className="spin" /> Opening…</> : 'Open approved tab'}</button></div>}
    {error && <div className="browser-error" role="alert"><span>{error}</span><button type="button" onClick={() => void runAction('retry', onRefresh)} disabled={Boolean(busyAction)}>{busyAction === 'retry' ? 'Retrying…' : 'Retry'}</button></div>}
    {activeTaskId && browser?.available && <div className="browser-session-list">{scopedSessions.length ? scopedSessions.map((session) => <article className={`browser-session-card is-${session.state}`} key={session.id}><div className="browser-session-head"><div><strong>{session.title}</strong><small>{session.url}</small></div><span>{session.state}</span></div><div className="browser-session-actions"><input value={drafts[session.id] ?? session.url} onChange={(event) => setDrafts((current) => ({ ...current, [session.id]: event.target.value }))} aria-label={`Navigate ${session.title}`} disabled={Boolean(busyAction)} /><button type="button" onClick={() => void runAction(`navigate:${session.id}`, () => onNavigate(session.id, drafts[session.id] ?? session.url))} disabled={Boolean(busyAction) || ['stopped', 'failed', 'stale'].includes(session.state)}>{busyAction === `navigate:${session.id}` ? <><CircleNotchIcon className="spin" /> Navigating…</> : 'Navigate'}</button><button type="button" onClick={() => void runAction(`screenshot:${session.id}`, () => onScreenshot(session.id))} disabled={Boolean(busyAction) || ['stopped', 'failed', 'stale'].includes(session.state)}>{busyAction === `screenshot:${session.id}` ? <><CircleNotchIcon className="spin" /> Capturing…</> : 'Screenshot'}</button><button type="button" className="is-muted" onClick={() => void runAction(`stop:${session.id}`, () => onStop(session.id))} disabled={Boolean(busyAction) || ['stopped', 'failed', 'stale'].includes(session.state)}>{busyAction === `stop:${session.id}` ? <><CircleNotchIcon className="spin" /> Stopping…</> : 'Stop'}</button></div>{session.error && <small className="browser-session-error">{session.error}</small>}{session.artifacts?.length ? <small className="browser-artifacts">{session.artifacts.length} evidence artifact(s)</small> : null}</article>) : <div className="browser-session-empty">No browser tabs are open for this task.</div>}</div>}
  </section>
}

function EvidenceView({ activeTaskId, connection }: { activeTaskId: string | null; connection: 'checking' | 'ready' | 'offline' }) {
  const [tab, setTab] = useState<'sources' | 'artifacts'>('sources')
  const [events, setEvents] = useState<Array<Record<string, unknown>>>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const requestIdRef = useRef(0)
  const load = useCallback(async (initial = false) => {
    if (!activeTaskId || connection !== 'ready') return
    const requestId = ++requestIdRef.current
    if (initial) setLoading(true)
    try {
      const result = await codexApi.taskEvents(activeTaskId, -1, 500)
      if (requestId !== requestIdRef.current) return
      const unique = new Map<string, Record<string, unknown>>()
      const incoming = Array.isArray(result.events) ? result.events : []
      incoming.filter((event): event is Record<string, unknown> => Boolean(event && typeof event === 'object')).forEach((event, index) => {
        const id = typeof event.eventId === 'string' && event.eventId ? event.eventId : `${activeTaskId}:${event.sequence ?? index}`
        unique.set(id, event)
      })
      setEvents(Array.from(unique.values()))
      setError('')
    } catch (cause) {
      if (requestId === requestIdRef.current) setError(cause instanceof Error ? cause.message : 'Task evidence could not be loaded.')
    } finally {
      if (requestId === requestIdRef.current && initial) setLoading(false)
    }
  }, [activeTaskId, connection])
  useEffect(() => {
    requestIdRef.current += 1
    if (!activeTaskId || connection !== 'ready') { setEvents([]); setError(''); setLoading(false); return }
    void load(true)
    const timer = window.setInterval(() => void load(false), 1_500)
    return () => { requestIdRef.current += 1; window.clearInterval(timer) }
  }, [activeTaskId, connection, load])
  const records = useMemo(() => events.flatMap((event) => {
    const payload = event.payload && typeof event.payload === 'object' ? event.payload as Record<string, unknown> : {}
    const method = String(event.method ?? '')
    const isSource = event.kind === 'search' || event.kind === 'image' || method.includes('webSearch') || method.includes('imageView')
    const isArtifact = event.kind === 'diff' || event.kind === 'command' || event.kind === 'item' || method.includes('fileChange') || method.includes('test')
    if ((tab === 'sources' && !isSource) || (tab === 'artifacts' && !isArtifact)) return []
    const value = String(payload.url ?? payload.path ?? payload.query ?? payload.text ?? payload.summary ?? payload.review ?? '').trim()
    const source = event.source === 'workflow' ? 'workflow' : 'codex'
    return value ? [{ id: `${event.eventId ?? event.sequence ?? method}`, title: method || String(event.kind), value, kind: `${source} · ${String(event.kind)}` }] : []
  }), [events, tab])
  return <section className="utility-view evidence-view"><header className="content-header"><div className="content-title-row"><span className="content-icon"><FileCodeIcon /></span><div><span className="content-kicker">TASK EVIDENCE</span><h1>Sources & artifacts</h1></div></div><div className="content-header-actions"><button className={`subtle-button ${tab === 'sources' ? 'is-active' : ''}`} type="button" onClick={() => setTab('sources')}>Sources</button><button className={`subtle-button ${tab === 'artifacts' ? 'is-active' : ''}`} type="button" onClick={() => setTab('artifacts')}>Artifacts</button><button className="subtle-button" type="button" onClick={() => void load(true)} disabled={!activeTaskId || connection !== 'ready' || loading}>{loading ? <CircleNotchIcon className="spin" /> : <ClockCounterClockwiseIcon />} Refresh</button></div></header>{!activeTaskId && <div className="changes-state"><FileCodeIcon /><strong>Select a task first.</strong><span>Evidence is scoped to the active task.</span></div>}{activeTaskId && loading && <div className="changes-state"><CircleNotchIcon className="spin" /><strong>Loading task evidence…</strong></div>}{activeTaskId && error && <div className="evidence-error" role="alert"><span>{error}</span><button type="button" onClick={() => void load(true)} disabled={loading}>{loading ? 'Retrying…' : 'Retry'}</button></div>}{activeTaskId && !loading && !records.length && !error && <div className="changes-state"><FileCodeIcon /><strong>No {tab} recorded yet.</strong><span>Evidence appears as the task reads files, searches, reviews, and produces outputs.</span></div>}{activeTaskId && !loading && records.length > 0 && <div className="evidence-list">{records.map((record) => <article key={record.id}><span className="artifact-type">{record.kind}</span><strong>{record.title}</strong><code>{record.value}</code></article>)}</div>}</section>
}

function ExtensionGlyph({ label, image, kind }: { label: string; image?: string | null; kind: 'app' | 'skill' }) {
  const color = ['#6f68ff', '#e7a94a', '#4ac3b1', '#d9669a', '#6faee8', '#b284e8'][[...label].reduce((sum, character) => sum + character.charCodeAt(0), 0) % 6]
  return <span className={`extension-glyph is-${kind}`} style={{ '--extension-color': color } as React.CSSProperties}>{image && <img src={image} alt="" onError={(event) => { event.currentTarget.style.display = 'none' }} />}{kind === 'skill' ? <PuzzlePieceIcon /> : <span className="extension-glyph-label">{label.slice(0, 1).toUpperCase()}</span>}</span>
}

function ExtensionsView({ bootstrap, loading, error: catalogError, activeProjectId, query, onQueryChange, onToggleSkill, onRefresh }: { bootstrap: BootstrapState; loading: boolean; error: string; activeProjectId: string; query: string; onQueryChange: (value: string) => void; onToggleSkill: (path: string, enabled: boolean) => Promise<void>; onRefresh: () => void }) {
  const [tab, setTab] = useState<'skills' | 'apps'>('apps')
  const [busyPath, setBusyPath] = useState<string | null>(null)
  const [skillScope, setSkillScope] = useState<'user' | 'system'>('user')
  const [error, setError] = useState('')
  useEffect(() => { setSkillScope('user') }, [activeProjectId])
  const normalized = query.trim().toLocaleLowerCase('tr-TR')
  const skills = bootstrap.skills.filter((skill) => {
    const searchable = `${skill.name} ${skill.description ?? ''}`.toLocaleLowerCase('tr-TR')
    return searchable.includes(normalized) && (skill.scope || 'user') === skillScope
  })
  const apps = bootstrap.apps.filter((app) => `${app.name} ${app.description ?? ''}`.toLocaleLowerCase('tr-TR').includes(normalized)).slice(0, 48)
  const installedApps = apps.filter((app) => app.installed || app.isInstalled || app.isEnabled)
  const listedApps = apps.filter((app) => !(app.installed || app.isInstalled || app.isEnabled))
  const activeSkills = bootstrap.skills.filter((skill) => skill.enabled !== false).length
  const toggleSkill = async (path: string, enabled: boolean) => {
    setBusyPath(path)
    setError('')
    try { await onToggleSkill(path, enabled) } catch (cause) { setError(cause instanceof Error ? cause.message : 'Skill configuration failed.') } finally { setBusyPath(null) }
  }
  return (
    <section className="utility-view extensions-view">
      <header className="extensions-topbar"><div className="extensions-tabs"><button className={tab === 'apps' ? 'is-active' : ''} type="button" onClick={() => setTab('apps')}>Eklentiler</button><button className={tab === 'skills' ? 'is-active' : ''} type="button" onClick={() => setTab('skills')}>Beceriler</button></div><div className="extensions-topbar-actions"><button type="button" className="extensions-icon-button" aria-label="Kataloğu yenile" onClick={onRefresh} disabled={loading}>{loading ? <CircleNotchIcon className="spin" /> : <ArrowClockwiseIcon />}</button><span>{tab === 'apps' ? `${bootstrap.apps.length} eklenti` : `${activeSkills} aktif beceri`}</span></div></header>
      <div className="utility-content extensions-content">
        <div className="extensions-main">
          <header className="extensions-hero"><h1>{tab === 'apps' ? 'Eklentiler' : 'Beceriler'}</h1><p>{tab === 'apps' ? "Code'u favori araçlarınla kullan" : "Göreve özel becerilerle Code'un yapabileceklerini artır"}</p></header>
          <label className="extensions-search"><MagnifyingGlassIcon /><input value={query} onChange={(event) => onQueryChange(event.target.value)} placeholder={tab === 'apps' ? 'Eklenti ara' : 'Beceri ara'} /></label>
        {(catalogError || error) && <div className="provider-inline-error" role="alert">{catalogError || error}<button type="button" className="inline-retry" onClick={onRefresh} disabled={loading}>{loading ? 'Retrying…' : 'Retry'}</button></div>}
          {loading && <div className="extensions-loading"><CircleNotchIcon className="spin" /><span>Katalog yenileniyor…</span></div>}
          {tab === 'apps' && <>
            <section className="extensions-section"><div className="extensions-section-title"><h2>Kurulu</h2><span>{installedApps.length}</span></div>{installedApps.length ? <div className="extensions-icon-rail">{installedApps.slice(0, 18).map((app) => <div key={app.id} title={app.name}><ExtensionGlyph label={app.name} image={app.logoUrl || app.logoUrlDark} kind="app" /></div>)}</div> : <p className="extensions-quiet">Henüz bağlı bir eklenti yok.</p>}</section>
            <section className="extensions-section"><div className="extensions-section-title"><h2>Keşfet</h2><span>Bağlanmaya hazır</span></div><div className="extension-list">{listedApps.slice(0, 12).map((app) => <article className="extension-list-item" key={app.id}><ExtensionGlyph label={app.name} image={app.logoUrl || app.logoUrlDark} kind="app" /><div><h3>{app.name}</h3><p>{app.description || 'Code için bağlı araçlar ve ek bağlam sağlar.'}</p></div><span className={`extension-state ${(app.isAccessible || app.connectionState === 'ready') ? 'is-ready' : ''}`}>{app.isAccessible || app.connectionState === 'ready' ? 'Hazır' : 'Ekle'}</span></article>)}</div></section>
          </>}
          {tab === 'skills' && <>
            <section className="extensions-section"><div className="extensions-section-title"><h2>Kurulu</h2><span>{activeSkills} aktif</span></div><div className="extensions-scope-tabs"><button className={skillScope === 'user' ? 'is-active' : ''} type="button" onClick={() => setSkillScope('user')}>Kişisel</button><button className={skillScope === 'system' ? 'is-active' : ''} type="button" onClick={() => setSkillScope('system')}>Sistem</button></div><p className="extensions-quiet">Skill Gateway, uzman yönergelerini yalnız görev gerektirirse açar.</p></section>
            <section className="extensions-section"><div className="extension-list">{skills.slice(0, 18).map((skill) => {
            const enabled = skill.enabled !== false
            return <article className="extension-list-item" key={skill.path || skill.name}><ExtensionGlyph label={skill.interface?.displayName || skill.name} kind="skill" /><div><h3>{skill.interface?.displayName || skill.name}</h3><p>{skill.interface?.shortDescription || skill.description || 'Bu beceri Code görevlerine uzman iş akışı ekler.'}</p></div>{skill.path ? <button className={`toggle ${enabled ? 'is-on' : ''}`} type="button" disabled={busyPath === skill.path} onClick={() => void toggleSkill(skill.path!, !enabled)} aria-label={`${skill.name} ${enabled ? 'kapat' : 'aç'}`}><span /></button> : <span className="extension-state is-ready">Sistem</span>}</article>
            })}</div></section>
          </>}
          {!((tab === 'skills' ? skills : apps).length) && <div className="extensions-empty"><PuzzlePieceIcon /><strong>{bootstrap.health.ready ? 'Bu aramayla eşleşen öğe yok.' : 'Codex motoru bağlanınca gerçek beceri ve eklentiler burada listelenecek.'}</strong></div>}
        </div>
      </div>
    </section>
  )
}

function ProjectDialog({ projects, activeProjectId, draft, error, workspaceRoots, onDraftChange, onChoose, onRename, onTogglePinned, onToggleArchived, onClose, onSubmit }: { projects: StudioProject[]; activeProjectId: string; draft: { name: string; cwd: string }; error: string; workspaceRoots: string[]; onDraftChange: (draft: { name: string; cwd: string }) => void; onChoose: (id: string) => void; onRename: (project: StudioProject) => void; onTogglePinned: (project: StudioProject) => void; onToggleArchived: (project: StudioProject) => void; onClose: () => void; onSubmit: (event: FormEvent) => void }) {
  const dialogRef = useRef<HTMLDivElement>(null)
  useDialogFocus(dialogRef, true, onClose)
  const [showArchived, setShowArchived] = useState(false)
  const visibleProjects = projects
    .filter((project) => showArchived || !project.archived)
    .sort((left, right) => Number(Boolean(right.pinned)) - Number(Boolean(left.pinned)) || Number(right.lastOpenedAt ?? 0) - Number(left.lastOpenedAt ?? 0))
  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target) onClose() }}>
      <div ref={dialogRef} className="project-dialog" role="dialog" aria-modal="true" aria-labelledby="project-dialog-title">
        <div className="dialog-header"><div><span className="content-kicker">LOCI WORKSPACES</span><h2 id="project-dialog-title">Projeler</h2></div><button type="button" onClick={onClose} aria-label="Pencereyi kapat"><XIcon /></button></div>
        <div className="project-dialog-list">{visibleProjects.map((project) => <div className={`project-row ${project.id === activeProjectId ? 'is-active' : ''}`} key={project.id}><button className="project-row-main" type="button" onClick={() => { onChoose(project.id); onClose() }}><span className="project-avatar" style={{ '--project-accent': project.accent } as React.CSSProperties}>{project.name[0]?.toUpperCase()}</span><span><strong>{project.pinned ? '★ ' : ''}{project.name}</strong><small>{project.cwd}</small></span>{project.id === activeProjectId && <CheckCircleIcon weight="fill" />}</button><div className="project-row-actions"><button type="button" onClick={() => onTogglePinned(project)} aria-label={project.pinned ? 'Unpin project' : 'Pin project'}>{project.pinned ? '★' : '☆'}</button><button type="button" onClick={() => onRename(project)} aria-label="Rename project">···</button><button type="button" onClick={() => onToggleArchived(project)} disabled={project.id === activeProjectId} aria-label={project.archived ? 'Restore project' : 'Archive project'}>{project.archived ? '↩' : '×'}</button></div></div>)}</div>
        <button className="archived-projects-toggle" type="button" onClick={() => setShowArchived((value) => !value)}>{showArchived ? 'Show active projects' : 'Show archived projects'}</button>
        <form className="project-create-form" onSubmit={onSubmit}>
          <strong>Yeni çalışma alanı ekle</strong>
          {workspaceRoots.length > 0 && <small className="project-root-hint">İzinli kök: {workspaceRoots.join(', ')}</small>}
          {error && <p className="project-form-error" role="alert">{error}</p>}
          <label><span>Proje adı</span><input value={draft.name} onChange={(event) => onDraftChange({ ...draft, name: event.target.value })} placeholder="Örn. Yeni ürün" required /></label>
          <label><span>Folder path</span><input aria-label="Klasör yolu" value={draft.cwd} onChange={(event) => onDraftChange({ ...draft, cwd: event.target.value })} placeholder="/Users/name/project or C:\\project" required /></label>
          <button type="submit"><PlusIcon /> Projeyi ekle</button>
        </form>
      </div>
    </div>
  )
}
